'use client';
import { JustaName, sanitizeRecords, coinTypeMap, SUPPORTED_SOCIALS, generalKeys, sanitizeAddresses, sanitizeTexts } from '@justaname.id/sdk';
import { useQueryClient, useMutation, useQuery, useInfiniteQuery } from '@tanstack/react-query';
import { jsxs, jsx } from 'react/jsx-runtime';
import React, { createContext, useMemo, useContext, useEffect } from 'react';
import { useAccount, useSignMessage, useReadContract, useSimulateContract, useWriteContract, useWaitForTransactionReceipt, useWalletClient } from 'wagmi';
import { getNamesForAddress } from '@ensdomains/ensjs/subgraph';
import { createPublicClient, http } from 'viem';
import { addEnsContracts } from '@ensdomains/ensjs';
import { mainnet, sepolia } from 'viem/chains';
import { getRecords, batch, getResolver, getName } from '@ensdomains/ensjs/public';
import { normalize } from 'viem/ens';
import axios from 'axios';
import * as qs from 'qs';
import { ethers } from 'ethers';
import { setRecords, setContentHashRecord, setTextRecord, setAddressRecord } from '@ensdomains/ensjs/wallet';

var defaultRoutes = {
    addSubnameRoute: '/api/subnames/add',
    revokeSubnameRoute: '/api/subnames/revoke',
    signinRoute: '/api/signin',
    signinNonceRoute: '/api/signin/nonce',
    signoutRoute: '/api/signout',
    currentEnsRoute: '/api/current'
};

function _array_like_to_array$9(arr, len) {
    if (len == null || len > arr.length) len = arr.length;
    for(var i = 0, arr2 = new Array(len); i < len; i++)arr2[i] = arr[i];
    return arr2;
}
function _array_with_holes$3(arr) {
    if (Array.isArray(arr)) return arr;
}
function _iterable_to_array_limit$3(arr, i) {
    var _i = arr == null ? null : typeof Symbol !== "undefined" && arr[Symbol.iterator] || arr["@@iterator"];
    if (_i == null) return;
    var _arr = [];
    var _n = true;
    var _d = false;
    var _s, _e;
    try {
        for(_i = _i.call(arr); !(_n = (_s = _i.next()).done); _n = true){
            _arr.push(_s.value);
            if (i && _arr.length === i) break;
        }
    } catch (err) {
        _d = true;
        _e = err;
    } finally{
        try {
            if (!_n && _i["return"] != null) _i["return"]();
        } finally{
            if (_d) throw _e;
        }
    }
    return _arr;
}
function _non_iterable_rest$3() {
    throw new TypeError("Invalid attempt to destructure non-iterable instance.\\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}
function _sliced_to_array$3(arr, i) {
    return _array_with_holes$3(arr) || _iterable_to_array_limit$3(arr, i) || _unsupported_iterable_to_array$9(arr, i) || _non_iterable_rest$3();
}
function _unsupported_iterable_to_array$9(o, minLen) {
    if (!o) return;
    if (typeof o === "string") return _array_like_to_array$9(o, minLen);
    var n = Object.prototype.toString.call(o).slice(8, -1);
    if (n === "Object" && o.constructor) n = o.constructor.name;
    if (n === "Map" || n === "Set") return Array.from(n);
    if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _array_like_to_array$9(o, minLen);
}
/**
 * A custom React hook to determine if a component has been mounted.
 *
 * @returns {boolean} A boolean flag indicating whether the component is currently mounted.
 */ var useMounted = function() {
    var _React_useState = _sliced_to_array$3(React.useState(false), 2), mounted = _React_useState[0], setMounted = _React_useState[1];
    React.useEffect(function() {
        setMounted(true);
    }, []);
    return mounted;
};

function _define_property$s(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _object_spread$r(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property$s(target, key, source[key]);
        });
    }
    return target;
}
function ownKeys$q(object, enumerableOnly) {
    var keys = Object.keys(object);
    if (Object.getOwnPropertySymbols) {
        var symbols = Object.getOwnPropertySymbols(object);
        keys.push.apply(keys, symbols);
    }
    return keys;
}
function _object_spread_props$q(target, source) {
    source = source != null ? source : {};
    if (Object.getOwnPropertyDescriptors) {
        Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
        ownKeys$q(Object(source)).forEach(function(key) {
            Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
        });
    }
    return target;
}
/**
 * A custom hook that wraps the `useAccount` hook from wagmi, incorporating a component mount check.
 *
 * @returns {object} An enhanced account object that includes all properties and methods from `useAccount`,
 * along with an improved `isConnected` boolean that also takes the component's mount state into consideration.
 */ var useMountedAccount = function() {
    var isMounted = useMounted();
    var account = useAccount();
    return _object_spread_props$q(_object_spread$r({}, account), {
        isConnected: isMounted && account.isConnected
    });
};

function asyncGeneratorStep$t(gen, resolve, reject, _next, _throw, key, arg) {
    try {
        var info = gen[key](arg);
        var value = info.value;
    } catch (error) {
        reject(error);
        return;
    }
    if (info.done) {
        resolve(value);
    } else {
        Promise.resolve(value).then(_next, _throw);
    }
}
function _async_to_generator$t(fn) {
    return function() {
        var self = this, args = arguments;
        return new Promise(function(resolve, reject) {
            var gen = fn.apply(self, args);
            function _next(value) {
                asyncGeneratorStep$t(gen, resolve, reject, _next, _throw, "next", value);
            }
            function _throw(err) {
                asyncGeneratorStep$t(gen, resolve, reject, _next, _throw, "throw", err);
            }
            _next(undefined);
        });
    };
}
function _define_property$r(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _object_spread$q(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property$r(target, key, source[key]);
        });
    }
    return target;
}
function _ts_generator$t(thisArg, body) {
    var f, y, t, g, _ = {
        label: 0,
        sent: function() {
            if (t[0] & 1) throw t[1];
            return t[1];
        },
        trys: [],
        ops: []
    };
    return g = {
        next: verb(0),
        "throw": verb(1),
        "return": verb(2)
    }, typeof Symbol === "function" && (g[Symbol.iterator] = function() {
        return this;
    }), g;
    function verb(n) {
        return function(v) {
            return step([
                n,
                v
            ]);
        };
    }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while(_)try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [
                op[0] & 2,
                t.value
            ];
            switch(op[0]){
                case 0:
                case 1:
                    t = op;
                    break;
                case 4:
                    _.label++;
                    return {
                        value: op[1],
                        done: false
                    };
                case 5:
                    _.label++;
                    y = op[1];
                    op = [
                        0
                    ];
                    continue;
                case 7:
                    op = _.ops.pop();
                    _.trys.pop();
                    continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) {
                        _ = 0;
                        continue;
                    }
                    if (op[0] === 3 && (!t || op[1] > t[0] && op[1] < t[3])) {
                        _.label = op[1];
                        break;
                    }
                    if (op[0] === 6 && _.label < t[1]) {
                        _.label = t[1];
                        t = op;
                        break;
                    }
                    if (t && _.label < t[2]) {
                        _.label = t[2];
                        _.ops.push(op);
                        break;
                    }
                    if (t[2]) _.ops.pop();
                    _.trys.pop();
                    continue;
            }
            op = body.call(thisArg, _);
        } catch (e) {
            op = [
                6,
                e
            ];
            y = 0;
        } finally{
            f = t = 0;
        }
        if (op[0] & 5) throw op[1];
        return {
            value: op[0] ? op[1] : void 0,
            done: true
        };
    }
}
var JustaNameContext = /*#__PURE__*/ createContext(null);
/**
 * Provides JustaName context to child components, allowing them to access and interact
 * with the JustaName service.
 *
 * @component
 * @param {JustaNameProviderProps} props - The props for the JustaNameProvider component.
 * @returns {ReactNode} The provider component wrapping children.
 */ var JustaNameProvider = function(param) {
    var children = param.children, initialConfig = param.config;
    var chainId = useMountedAccount().chainId;
    var defaultChain = useMemo(function() {
        return !chainId === undefined ? 1 : chainId !== 1 && chainId !== 11155111 ? 1 : chainId;
    }, [
        chainId
    ]);
    // const [config, setConfig] = useState<JustaNameProviderConfig>(initialConfig);
    var config = useMemo(function() {
        return initialConfig;
    }, [
        initialConfig
    ]);
    var justanameConfig = useMemo(function() {
        return {
            config: config.config,
            ensDomains: config.ensDomains,
            networks: config.networks,
            defaultChainId: defaultChain,
            dev: config.dev
        };
    }, [
        config,
        defaultChain
    ]);
    var justaname = useMemo(function() {
        return JustaName.init(justanameConfig);
    }, [
        justanameConfig
    ]);
    var selectedEnsDomain = useMemo(function() {
        var _justanameConfig_ensDomains_find, _justanameConfig_ensDomains;
        return (_justanameConfig_ensDomains = justanameConfig.ensDomains) === null || _justanameConfig_ensDomains === void 0 ? void 0 : (_justanameConfig_ensDomains_find = _justanameConfig_ensDomains.find(function(ensDomain) {
            return ensDomain.chainId === defaultChain;
        })) === null || _justanameConfig_ensDomains_find === void 0 ? void 0 : _justanameConfig_ensDomains_find.ensDomain;
    }, [
        justanameConfig.ensDomains,
        defaultChain
    ]);
    var configuredNetworks = useMemo(function() {
        return JustaName.createNetworks(justanameConfig.networks);
    }, [
        justanameConfig.networks
    ]);
    var selectedNetwork = useMemo(function() {
        return configuredNetworks.find(function(network) {
            return network.chainId === defaultChain;
        });
    }, [
        configuredNetworks,
        defaultChain
    ]);
    return /*#__PURE__*/ jsxs(JustaNameContext.Provider, {
        value: {
            justanameConfig: justanameConfig,
            // handleJustaNameConfig,
            backendUrl: config.backendUrl || '',
            config: justanameConfig.config,
            dev: justanameConfig.dev,
            ensDomains: justanameConfig.ensDomains || [],
            selectedEnsDomain: selectedEnsDomain,
            networks: configuredNetworks,
            chainId: defaultChain,
            selectedNetwork: selectedNetwork,
            justaname: justaname,
            routes: _object_spread$q({}, defaultRoutes, config.routes)
        },
        children: [
            children,
            config.signOnMounted && /*#__PURE__*/ jsx(SignatureOnMounted, {})
        ]
    });
};
/**
 * Custom hook for accessing the JustaNameContext.
 *
 * @hook
 * @returns {JustaNameContextProps} The context value with JustaName service instance and configuration.
 * @throws {Error} If the hook is used outside a JustaNameProvider.
 */ var useJustaName = function() {
    var context = useContext(JustaNameContext);
    if (!context) {
        throw new Error('useJustaName must be used within a JustaNameProvider');
    }
    return context;
};
var buildSignature = function(address, chainId) {
    return [
        'SUBNAME_SIGNATURE',
        address,
        chainId
    ];
};
var useSubnameSignature = function() {
    var _useJustaName = useJustaName(), justaname = _useJustaName.justaname, chainId = _useJustaName.chainId;
    var address = useMountedAccount().address;
    var queryClient = useQueryClient();
    var signMessageAsync = useSignMessage().signMessageAsync;
    var isWeb = typeof window !== 'undefined';
    var mutation = useMutation({
        mutationFn: /*#__PURE__*/ _async_to_generator$t(function() {
            var message, signature, expirationTime, signedData;
            return _ts_generator$t(this, function(_state) {
                switch(_state.label){
                    case 0:
                        if (!address) {
                            throw new Error('No address found');
                        }
                        if (!chainId) {
                            throw new Error('No chainId found');
                        }
                        message = justaname.siwe.requestChallenge({
                            address: address,
                            chainId: chainId
                        });
                        return [
                            4,
                            signMessageAsync({
                                message: message.challenge,
                                account: address
                            })
                        ];
                    case 1:
                        signature = _state.sent();
                        if (!signature) {
                            throw new Error('Message not signed');
                        }
                        expirationTime = new Date(message.challenge.split('Expiration Time: ')[1]);
                        signedData = {
                            signature: signature,
                            message: message.challenge,
                            address: address,
                            expirationTime: expirationTime
                        };
                        return [
                            4,
                            queryClient.setQueryData(buildSignature(address, chainId), signedData)
                        ];
                    case 2:
                        _state.sent();
                        if (isWeb) {
                            localStorage.setItem(buildSignature(address, chainId).join('_'), JSON.stringify(signedData));
                        }
                        return [
                            2,
                            signedData
                        ];
                }
            });
        })
    });
    var query = useQuery({
        queryKey: buildSignature(address !== null && address !== void 0 ? address : '', chainId !== null && chainId !== void 0 ? chainId : 1),
        queryFn: function() {
            return mutation.mutateAsync();
        },
        enabled: false,
        refetchOnWindowFocus: false
    });
    var getSignature = function() {
        var _ref = _async_to_generator$t(function() {
            var now, localData, parsedData;
            return _ts_generator$t(this, function(_state) {
                switch(_state.label){
                    case 0:
                        now = new Date();
                        if (query.data) {
                            if (query.data.expirationTime > now) {
                                return [
                                    2,
                                    query.data
                                ];
                            }
                        }
                        if (!isWeb) return [
                            3,
                            2
                        ];
                        localData = localStorage.getItem(buildSignature(address !== null && address !== void 0 ? address : '', chainId).join('_'));
                        if (!localData) return [
                            3,
                            2
                        ];
                        parsedData = JSON.parse(localData);
                        if (!(new Date(parsedData.expirationTime) > now)) return [
                            3,
                            2
                        ];
                        return [
                            4,
                            queryClient.setQueryData(buildSignature(address !== null && address !== void 0 ? address : '', chainId), parsedData)
                        ];
                    case 1:
                        _state.sent();
                        return [
                            2,
                            parsedData
                        ];
                    case 2:
                        return [
                            4,
                            mutation.mutateAsync()
                        ];
                    case 3:
                        return [
                            2,
                            _state.sent()
                        ];
                }
            });
        });
        return function getSignature() {
            return _ref.apply(this, arguments);
        };
    }();
    return {
        getSignature: getSignature,
        isSubnameSignaturePending: mutation.isPending,
        isSubnameSignatureFetching: query.isFetching
    };
};
var SignatureOnMounted = function() {
    var _useMountedAccount = useMountedAccount(), address = _useMountedAccount.address, isConnected = _useMountedAccount.isConnected;
    var _useSubnameSignature = useSubnameSignature(), getSignature = _useSubnameSignature.getSignature, isSubnameSignaturePending = _useSubnameSignature.isSubnameSignaturePending;
    useEffect(function() {
        if (!address || !isConnected || isSubnameSignaturePending) return;
        var main = function() {
            var _ref = _async_to_generator$t(function() {
                return _ts_generator$t(this, function(_state) {
                    switch(_state.label){
                        case 0:
                            return [
                                4,
                                getSignature()
                            ];
                        case 1:
                            _state.sent();
                            return [
                                2
                            ];
                    }
                });
            });
            return function main() {
                return _ref.apply(this, arguments);
            };
        }();
        main();
    }, [
        address,
        isConnected,
        isSubnameSignaturePending
    ]);
    return null;
};

var defaultOptions = {
    staleTime: 1000 * 60 * 5,
    networkMode: 'offlineFirst',
    refetchOnWindowFocus: false,
    retry: 3,
    retryDelay: 1000,
    retryOnMount: false
};

function asyncGeneratorStep$s(gen, resolve, reject, _next, _throw, key, arg) {
    try {
        var info = gen[key](arg);
        var value = info.value;
    } catch (error) {
        reject(error);
        return;
    }
    if (info.done) {
        resolve(value);
    } else {
        Promise.resolve(value).then(_next, _throw);
    }
}
function _async_to_generator$s(fn) {
    return function() {
        var self = this, args = arguments;
        return new Promise(function(resolve, reject) {
            var gen = fn.apply(self, args);
            function _next(value) {
                asyncGeneratorStep$s(gen, resolve, reject, _next, _throw, "next", value);
            }
            function _throw(err) {
                asyncGeneratorStep$s(gen, resolve, reject, _next, _throw, "throw", err);
            }
            _next(undefined);
        });
    };
}
function _define_property$q(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _object_spread$p(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property$q(target, key, source[key]);
        });
    }
    return target;
}
function ownKeys$p(object, enumerableOnly) {
    var keys = Object.keys(object);
    if (Object.getOwnPropertySymbols) {
        var symbols = Object.getOwnPropertySymbols(object);
        keys.push.apply(keys, symbols);
    }
    return keys;
}
function _object_spread_props$p(target, source) {
    source = source != null ? source : {};
    if (Object.getOwnPropertyDescriptors) {
        Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
        ownKeys$p(Object(source)).forEach(function(key) {
            Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
        });
    }
    return target;
}
function _ts_generator$s(thisArg, body) {
    var f, y, t, g, _ = {
        label: 0,
        sent: function() {
            if (t[0] & 1) throw t[1];
            return t[1];
        },
        trys: [],
        ops: []
    };
    return g = {
        next: verb(0),
        "throw": verb(1),
        "return": verb(2)
    }, typeof Symbol === "function" && (g[Symbol.iterator] = function() {
        return this;
    }), g;
    function verb(n) {
        return function(v) {
            return step([
                n,
                v
            ]);
        };
    }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while(_)try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [
                op[0] & 2,
                t.value
            ];
            switch(op[0]){
                case 0:
                case 1:
                    t = op;
                    break;
                case 4:
                    _.label++;
                    return {
                        value: op[1],
                        done: false
                    };
                case 5:
                    _.label++;
                    y = op[1];
                    op = [
                        0
                    ];
                    continue;
                case 7:
                    op = _.ops.pop();
                    _.trys.pop();
                    continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) {
                        _ = 0;
                        continue;
                    }
                    if (op[0] === 3 && (!t || op[1] > t[0] && op[1] < t[3])) {
                        _.label = op[1];
                        break;
                    }
                    if (op[0] === 6 && _.label < t[1]) {
                        _.label = t[1];
                        t = op;
                        break;
                    }
                    if (t && _.label < t[2]) {
                        _.label = t[2];
                        _.ops.push(op);
                        break;
                    }
                    if (t[2]) _.ops.pop();
                    _.trys.pop();
                    continue;
            }
            op = body.call(thisArg, _);
        } catch (e) {
            op = [
                6,
                e
            ];
            y = 0;
        } finally{
            f = t = 0;
        }
        if (op[0] & 5) throw op[1];
        return {
            value: op[0] ? op[1] : void 0,
            done: true
        };
    }
}
var buildAccountInvitationsKey = function(address, chainId) {
    return [
        'INVITATIONS_BY_ADDRESS',
        address,
        chainId
    ];
};
var useAccountInvitations = function(params) {
    var address = useMountedAccount().address;
    var _useJustaName = useJustaName(), justaname = _useJustaName.justaname, chainId = _useJustaName.chainId;
    var _enabled = (params === null || params === void 0 ? void 0 : params.enabled) !== undefined ? params.enabled : true;
    var _chainId = useMemo(function() {
        return (params === null || params === void 0 ? void 0 : params.chainId) || chainId;
    }, [
        params,
        chainId
    ]);
    var query = useQuery(_object_spread_props$p(_object_spread$p({}, defaultOptions), {
        queryKey: buildAccountInvitationsKey(address, _chainId),
        queryFn: /*#__PURE__*/ _async_to_generator$s(function() {
            var invitations;
            return _ts_generator$s(this, function(_state) {
                switch(_state.label){
                    case 0:
                        return [
                            4,
                            justaname.subnames.getInvitationsByAddress({
                                address: address,
                                coinType: (params === null || params === void 0 ? void 0 : params.coinType) || 60,
                                chainId: _chainId
                            })
                        ];
                    case 1:
                        invitations = _state.sent();
                        return [
                            2,
                            (invitations === null || invitations === void 0 ? void 0 : invitations.subnames.map(function(subname) {
                                return _object_spread_props$p(_object_spread$p({}, subname), {
                                    sanitizedRecords: sanitizeRecords(subname)
                                });
                            })) || []
                        ];
                }
            });
        }),
        enabled: Boolean(address) && Boolean(justaname) && Boolean(_enabled)
    }));
    return {
        invitations: query.data || [],
        isInvitationsPending: query.isPending,
        refetchInvitations: query.refetch
    };
};

function asyncGeneratorStep$r(gen, resolve, reject, _next, _throw, key, arg) {
    try {
        var info = gen[key](arg);
        var value = info.value;
    } catch (error) {
        reject(error);
        return;
    }
    if (info.done) {
        resolve(value);
    } else {
        Promise.resolve(value).then(_next, _throw);
    }
}
function _async_to_generator$r(fn) {
    return function() {
        var self = this, args = arguments;
        return new Promise(function(resolve, reject) {
            var gen = fn.apply(self, args);
            function _next(value) {
                asyncGeneratorStep$r(gen, resolve, reject, _next, _throw, "next", value);
            }
            function _throw(err) {
                asyncGeneratorStep$r(gen, resolve, reject, _next, _throw, "throw", err);
            }
            _next(undefined);
        });
    };
}
function _define_property$p(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _object_spread$o(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property$p(target, key, source[key]);
        });
    }
    return target;
}
function ownKeys$o(object, enumerableOnly) {
    var keys = Object.keys(object);
    if (Object.getOwnPropertySymbols) {
        var symbols = Object.getOwnPropertySymbols(object);
        keys.push.apply(keys, symbols);
    }
    return keys;
}
function _object_spread_props$o(target, source) {
    source = source != null ? source : {};
    if (Object.getOwnPropertyDescriptors) {
        Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
        ownKeys$o(Object(source)).forEach(function(key) {
            Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
        });
    }
    return target;
}
function _ts_generator$r(thisArg, body) {
    var f, y, t, g, _ = {
        label: 0,
        sent: function() {
            if (t[0] & 1) throw t[1];
            return t[1];
        },
        trys: [],
        ops: []
    };
    return g = {
        next: verb(0),
        "throw": verb(1),
        "return": verb(2)
    }, typeof Symbol === "function" && (g[Symbol.iterator] = function() {
        return this;
    }), g;
    function verb(n) {
        return function(v) {
            return step([
                n,
                v
            ]);
        };
    }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while(_)try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [
                op[0] & 2,
                t.value
            ];
            switch(op[0]){
                case 0:
                case 1:
                    t = op;
                    break;
                case 4:
                    _.label++;
                    return {
                        value: op[1],
                        done: false
                    };
                case 5:
                    _.label++;
                    y = op[1];
                    op = [
                        0
                    ];
                    continue;
                case 7:
                    op = _.ops.pop();
                    _.trys.pop();
                    continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) {
                        _ = 0;
                        continue;
                    }
                    if (op[0] === 3 && (!t || op[1] > t[0] && op[1] < t[3])) {
                        _.label = op[1];
                        break;
                    }
                    if (op[0] === 6 && _.label < t[1]) {
                        _.label = t[1];
                        t = op;
                        break;
                    }
                    if (t && _.label < t[2]) {
                        _.label = t[2];
                        _.ops.push(op);
                        break;
                    }
                    if (t[2]) _.ops.pop();
                    _.trys.pop();
                    continue;
            }
            op = body.call(thisArg, _);
        } catch (e) {
            op = [
                6,
                e
            ];
            y = 0;
        } finally{
            f = t = 0;
        }
        if (op[0] & 5) throw op[1];
        return {
            value: op[0] ? op[1] : void 0,
            done: true
        };
    }
}
var buildSubnameBySubnameKey = function(subname, chainId) {
    return [
        'SUBNAME_BY_SUBNAME',
        subname,
        chainId
    ];
};
var useSubname = function(params) {
    var _useJustaName = useJustaName(), justaname = _useJustaName.justaname, chainId = _useJustaName.chainId;
    var _chainId = (params === null || params === void 0 ? void 0 : params.chainId) || chainId;
    var _enabled = (params === null || params === void 0 ? void 0 : params.enabled) !== undefined ? params.enabled : true;
    var query = useQuery(_object_spread_props$o(_object_spread$o({}, defaultOptions), {
        queryKey: buildSubnameBySubnameKey((params === null || params === void 0 ? void 0 : params.subname) || '', _chainId),
        queryFn: /*#__PURE__*/ _async_to_generator$r(function() {
            var subname;
            return _ts_generator$r(this, function(_state) {
                switch(_state.label){
                    case 0:
                        return [
                            4,
                            justaname.subnames.getSubname({
                                subname: params.subname || '',
                                chainId: _chainId
                            })
                        ];
                    case 1:
                        subname = _state.sent();
                        return [
                            2,
                            _object_spread_props$o(_object_spread$o({}, subname), {
                                sanitizedRecords: sanitizeRecords(subname)
                            })
                        ];
                }
            });
        }),
        enabled: Boolean(justaname) && Boolean(params.subname) && _enabled
    }));
    return {
        subname: query.data,
        isSubnamePending: query.isPending,
        isSubnameFetching: query.isFetching,
        isSubnameLoading: query.isPending || query.isFetching,
        refetchSubname: query.refetch
    };
};

function asyncGeneratorStep$q(gen, resolve, reject, _next, _throw, key, arg) {
    try {
        var info = gen[key](arg);
        var value = info.value;
    } catch (error) {
        reject(error);
        return;
    }
    if (info.done) {
        resolve(value);
    } else {
        Promise.resolve(value).then(_next, _throw);
    }
}
function _async_to_generator$q(fn) {
    return function() {
        var self = this, args = arguments;
        return new Promise(function(resolve, reject) {
            var gen = fn.apply(self, args);
            function _next(value) {
                asyncGeneratorStep$q(gen, resolve, reject, _next, _throw, "next", value);
            }
            function _throw(err) {
                asyncGeneratorStep$q(gen, resolve, reject, _next, _throw, "throw", err);
            }
            _next(undefined);
        });
    };
}
function _define_property$o(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _object_spread$n(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property$o(target, key, source[key]);
        });
    }
    return target;
}
function ownKeys$n(object, enumerableOnly) {
    var keys = Object.keys(object);
    if (Object.getOwnPropertySymbols) {
        var symbols = Object.getOwnPropertySymbols(object);
        keys.push.apply(keys, symbols);
    }
    return keys;
}
function _object_spread_props$n(target, source) {
    source = source != null ? source : {};
    if (Object.getOwnPropertyDescriptors) {
        Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
        ownKeys$n(Object(source)).forEach(function(key) {
            Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
        });
    }
    return target;
}
function _object_without_properties$1(source, excluded) {
    if (source == null) return {};
    var target = _object_without_properties_loose$1(source, excluded);
    var key, i;
    if (Object.getOwnPropertySymbols) {
        var sourceSymbolKeys = Object.getOwnPropertySymbols(source);
        for(i = 0; i < sourceSymbolKeys.length; i++){
            key = sourceSymbolKeys[i];
            if (excluded.indexOf(key) >= 0) continue;
            if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
            target[key] = source[key];
        }
    }
    return target;
}
function _object_without_properties_loose$1(source, excluded) {
    if (source == null) return {};
    var target = {};
    var sourceKeys = Object.keys(source);
    var key, i;
    for(i = 0; i < sourceKeys.length; i++){
        key = sourceKeys[i];
        if (excluded.indexOf(key) >= 0) continue;
        target[key] = source[key];
    }
    return target;
}
function _ts_generator$q(thisArg, body) {
    var f, y, t, g, _ = {
        label: 0,
        sent: function() {
            if (t[0] & 1) throw t[1];
            return t[1];
        },
        trys: [],
        ops: []
    };
    return g = {
        next: verb(0),
        "throw": verb(1),
        "return": verb(2)
    }, typeof Symbol === "function" && (g[Symbol.iterator] = function() {
        return this;
    }), g;
    function verb(n) {
        return function(v) {
            return step([
                n,
                v
            ]);
        };
    }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while(_)try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [
                op[0] & 2,
                t.value
            ];
            switch(op[0]){
                case 0:
                case 1:
                    t = op;
                    break;
                case 4:
                    _.label++;
                    return {
                        value: op[1],
                        done: false
                    };
                case 5:
                    _.label++;
                    y = op[1];
                    op = [
                        0
                    ];
                    continue;
                case 7:
                    op = _.ops.pop();
                    _.trys.pop();
                    continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) {
                        _ = 0;
                        continue;
                    }
                    if (op[0] === 3 && (!t || op[1] > t[0] && op[1] < t[3])) {
                        _.label = op[1];
                        break;
                    }
                    if (op[0] === 6 && _.label < t[1]) {
                        _.label = t[1];
                        t = op;
                        break;
                    }
                    if (t && _.label < t[2]) {
                        _.label = t[2];
                        _.ops.push(op);
                        break;
                    }
                    if (t[2]) _.ops.pop();
                    _.trys.pop();
                    continue;
            }
            op = body.call(thisArg, _);
        } catch (e) {
            op = [
                6,
                e
            ];
            y = 0;
        } finally{
            f = t = 0;
        }
        if (op[0] & 5) throw op[1];
        return {
            value: op[0] ? op[1] : void 0,
            done: true
        };
    }
}
var buildAddressSubnamesKey = function(address, chainId) {
    return [
        'WALLET_SUBNAMES_BY_ADDRESS',
        address,
        chainId
    ];
};
var useAddressSubnames = function(params) {
    var queryClient = useQueryClient();
    var _useJustaName = useJustaName(), justaname = _useJustaName.justaname, defaultChainId = _useJustaName.chainId;
    var chainId = params.chainId, rest = _object_without_properties$1(params, [
        "chainId"
    ]);
    var _chainId = chainId || defaultChainId;
    var _enabled = (params === null || params === void 0 ? void 0 : params.enabled) !== undefined ? params.enabled : true;
    var query = useQuery(_object_spread_props$n(_object_spread$n({}, defaultOptions), {
        queryKey: buildAddressSubnamesKey(params.address, _chainId),
        queryFn: /*#__PURE__*/ _async_to_generator$q(function() {
            var _params_isClaimed, response;
            return _ts_generator$q(this, function(_state) {
                switch(_state.label){
                    case 0:
                        if (!params.address) {
                            throw new Error('Address is required');
                        }
                        return [
                            4,
                            justaname === null || justaname === void 0 ? void 0 : justaname.subnames.getSubnamesByAddress(_object_spread_props$n(_object_spread$n({}, rest), {
                                address: params.address,
                                isClaimed: (_params_isClaimed = params.isClaimed) !== null && _params_isClaimed !== void 0 ? _params_isClaimed : true,
                                coinType: params.coinType,
                                chainId: _chainId
                            }))
                        ];
                    case 1:
                        response = _state.sent();
                        response === null || response === void 0 ? void 0 : response.subnames.forEach(function(subname) {
                            queryClient.setQueryData(buildSubnameBySubnameKey(subname.ens, _chainId), _object_spread_props$n(_object_spread$n({}, subname), {
                                sanitizedRecords: sanitizeRecords(subname)
                            }));
                        });
                        return [
                            2,
                            (response === null || response === void 0 ? void 0 : response.subnames.map(function(subname) {
                                return _object_spread_props$n(_object_spread$n({}, subname), {
                                    sanitizedRecords: sanitizeRecords(subname)
                                });
                            })) || []
                        ];
                }
            });
        }),
        enabled: Boolean(justaname) && Boolean(params.address) && Boolean(_enabled)
    }));
    var _query_data;
    return {
        addressSubnames: (_query_data = query.data) !== null && _query_data !== void 0 ? _query_data : [],
        isAddressSubnamesPending: query.isPending,
        isAddressSubnamesFetching: query.isFetching,
        isAddressSubnamesLoading: query.isPending || query.isFetching,
        refetchAddressSubnames: query.refetch
    };
};

var useAccountSubnames = function(params) {
    var chainId = useJustaName().chainId;
    var address = useMountedAccount().address;
    var _chainId = (params === null || params === void 0 ? void 0 : params.chainId) || chainId;
    var _useAddressSubnames = useAddressSubnames({
        chainId: _chainId,
        address: address,
        enabled: params === null || params === void 0 ? void 0 : params.enabled
    }), addressSubnames = _useAddressSubnames.addressSubnames, isAddressSubnamesFetching = _useAddressSubnames.isAddressSubnamesFetching, isAddressSubnamesPending = _useAddressSubnames.isAddressSubnamesPending, isAddressSubnamesLoading = _useAddressSubnames.isAddressSubnamesLoading, refetchAddressSubnames = _useAddressSubnames.refetchAddressSubnames;
    return {
        accountSubnames: addressSubnames,
        isAccountSubnamesPending: isAddressSubnamesPending,
        isAccountSubnamesFetching: isAddressSubnamesFetching,
        isAccountSubnamesLoading: isAddressSubnamesLoading,
        refetchAccountSubnames: refetchAddressSubnames
    };
};

function _define_property$n(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _object_spread$m(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property$n(target, key, source[key]);
        });
    }
    return target;
}
function ownKeys$m(object, enumerableOnly) {
    var keys = Object.keys(object);
    if (Object.getOwnPropertySymbols) {
        var symbols = Object.getOwnPropertySymbols(object);
        keys.push.apply(keys, symbols);
    }
    return keys;
}
function _object_spread_props$m(target, source) {
    source = source != null ? source : {};
    if (Object.getOwnPropertyDescriptors) {
        Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
        ownKeys$m(Object(source)).forEach(function(key) {
            Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
        });
    }
    return target;
}
var getEnsPublicClient = function(providerUrl, chainId) {
    if (!chainId) {
        throw new Error('Chain ID is required');
    }
    return createPublicClient({
        chain: addEnsContracts(chainId === 1 ? mainnet : sepolia),
        transport: http(providerUrl)
    });
};
var buildEnsPublicClientKey = function(chainId) {
    return [
        'ENS_PUBLIC_CLIENT',
        chainId
    ];
};
var useEnsPublicClient = function(params) {
    var _useJustaName = useJustaName(), networks = _useJustaName.networks, chainId = _useJustaName.chainId;
    var _enabled = (params === null || params === void 0 ? void 0 : params.enabled) !== undefined ? params.enabled : true;
    var _chainId = useMemo(function() {
        return (params === null || params === void 0 ? void 0 : params.chainId) || chainId;
    }, [
        params === null || params === void 0 ? void 0 : params.chainId,
        chainId
    ]);
    var _network = useMemo(function() {
        return networks.find(function(network) {
            return network.chainId === _chainId;
        });
    }, [
        networks,
        _chainId
    ]);
    var _providerUrl = useMemo(function() {
        return (_network === null || _network === void 0 ? void 0 : _network.providerUrl) || '';
    }, [
        _network
    ]);
    var query = useQuery(_object_spread_props$m(_object_spread$m({}, defaultOptions), {
        queryKey: buildEnsPublicClientKey(_chainId),
        queryFn: function() {
            return getEnsPublicClient(_providerUrl, _chainId);
        },
        enabled: Boolean(_enabled)
    }));
    return {
        ensClient: query.data,
        isEnsPublicClientPending: query.isPending
    };
};

function _class_call_check(instance, Constructor) {
    if (!(instance instanceof Constructor)) {
        throw new TypeError("Cannot call a class as a function");
    }
}
function _defineProperties(target, props) {
    for(var i = 0; i < props.length; i++){
        var descriptor = props[i];
        descriptor.enumerable = descriptor.enumerable || false;
        descriptor.configurable = true;
        if ("value" in descriptor) descriptor.writable = true;
        Object.defineProperty(target, descriptor.key, descriptor);
    }
}
function _create_class(Constructor, protoProps, staticProps) {
    if (protoProps) _defineProperties(Constructor.prototype, protoProps);
    return Constructor;
}
function _define_property$m(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
var TaskQueue = /*#__PURE__*/ function() {
    function TaskQueue(concurrencyLimit) {
        _class_call_check(this, TaskQueue);
        _define_property$m(this, "concurrencyLimit", void 0);
        _define_property$m(this, "runningCount", void 0);
        _define_property$m(this, "queue", void 0);
        this.concurrencyLimit = concurrencyLimit;
        this.runningCount = 0;
        this.queue = [];
    }
    _create_class(TaskQueue, [
        {
            key: "enqueue",
            value: function enqueue(taskFn) {
                var _this = this;
                var priority = arguments.length > 1 && arguments[1] !== void 0 ? arguments[1] : false;
                return new Promise(function(resolve, reject) {
                    var task = {
                        taskFn: taskFn,
                        resolve: resolve,
                        reject: reject,
                        priority: priority
                    };
                    if (_this.runningCount < _this.concurrencyLimit) {
                        _this.runTask(task);
                    } else {
                        if (priority) {
                            _this.queue.unshift(task);
                        } else {
                            _this.queue.push(task);
                        }
                    }
                });
            }
        },
        {
            key: "runTask",
            value: function runTask(task) {
                var _this = this;
                this.runningCount++;
                task.taskFn().then(function(result) {
                    task.resolve(result);
                }).catch(function(error) {
                    task.reject(error);
                }).finally(function() {
                    _this.runningCount--;
                    if (_this.queue.length > 0) {
                        var nextTask = _this.queue.shift();
                        _this.runTask(nextTask);
                    }
                });
            }
        }
    ]);
    return TaskQueue;
}();

var RecordsTaskQueue = new TaskQueue(4);

function _define_property$l(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _object_spread$l(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property$l(target, key, source[key]);
        });
    }
    return target;
}
function ownKeys$l(object, enumerableOnly) {
    var keys = Object.keys(object);
    if (Object.getOwnPropertySymbols) {
        var symbols = Object.getOwnPropertySymbols(object);
        keys.push.apply(keys, symbols);
    }
    return keys;
}
function _object_spread_props$l(target, source) {
    source = source != null ? source : {};
    if (Object.getOwnPropertyDescriptors) {
        Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
        ownKeys$l(Object(source)).forEach(function(key) {
            Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
        });
    }
    return target;
}
var OFFCHAIN_RESOLVERS_KEY = [
    'OFFCHAIN_RESOLVERS'
];
var useOffchainResolvers = function() {
    var justaname = useJustaName().justaname;
    var query = useQuery(_object_spread_props$l(_object_spread$l({}, defaultOptions), {
        queryKey: OFFCHAIN_RESOLVERS_KEY,
        queryFn: function() {
            return justaname === null || justaname === void 0 ? void 0 : justaname.offchainResolvers.getAllOffchainResolvers();
        },
        enabled: Boolean(justaname)
    }));
    return {
        offchainResolvers: query.data || {
            offchainResolvers: []
        },
        isOffchainResolversPending: query.isPending
    };
};

var checkEnsValid = function(ens) {
    if (ens.records.resolverAddress === '0x0000000000000000000000000000000000000000') {
        throw new Error('Resolver address not found');
    }
    var ethAddress = null;
    if (ens) {
        var _ens_records_coins_find, _ens_records_coins;
        ethAddress = ens === null || ens === void 0 ? void 0 : (_ens_records_coins = ens.records.coins) === null || _ens_records_coins === void 0 ? void 0 : (_ens_records_coins_find = _ens_records_coins.find(function(coin) {
            return coin.id === 60;
        })) === null || _ens_records_coins_find === void 0 ? void 0 : _ens_records_coins_find.value;
        if (!ethAddress) {
            throw new Error('ETH address not found');
        }
    }
};

var normalizeEns = function(name) {
    if (typeof name !== 'string' || name.trim() === '') {
        return;
    }
    try {
        var normalized = normalize(name);
        return normalized;
    } catch (error) {
        return;
    }
};
var validateEns = function(name) {
    var ensRegex = /(?:^|[^a-zA-Z0-9-_.])(([^\s.]{1,63}\.)+[^\s.]{2,63})/;
    if (typeof name !== 'string' || name.trim() === '') {
        return false;
    }
    return ensRegex.test(name);
};

function _array_like_to_array$8(arr, len) {
    if (len == null || len > arr.length) len = arr.length;
    for(var i = 0, arr2 = new Array(len); i < len; i++)arr2[i] = arr[i];
    return arr2;
}
function _array_without_holes$5(arr) {
    if (Array.isArray(arr)) return _array_like_to_array$8(arr);
}
function asyncGeneratorStep$p(gen, resolve, reject, _next, _throw, key, arg) {
    try {
        var info = gen[key](arg);
        var value = info.value;
    } catch (error) {
        reject(error);
        return;
    }
    if (info.done) {
        resolve(value);
    } else {
        Promise.resolve(value).then(_next, _throw);
    }
}
function _async_to_generator$p(fn) {
    return function() {
        var self = this, args = arguments;
        return new Promise(function(resolve, reject) {
            var gen = fn.apply(self, args);
            function _next(value) {
                asyncGeneratorStep$p(gen, resolve, reject, _next, _throw, "next", value);
            }
            function _throw(err) {
                asyncGeneratorStep$p(gen, resolve, reject, _next, _throw, "throw", err);
            }
            _next(undefined);
        });
    };
}
function _define_property$k(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _instanceof(left, right) {
    if (right != null && typeof Symbol !== "undefined" && right[Symbol.hasInstance]) {
        return !!right[Symbol.hasInstance](left);
    } else {
        return left instanceof right;
    }
}
function _iterable_to_array$5(iter) {
    if (typeof Symbol !== "undefined" && iter[Symbol.iterator] != null || iter["@@iterator"] != null) return Array.from(iter);
}
function _non_iterable_spread$5() {
    throw new TypeError("Invalid attempt to spread non-iterable instance.\\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}
function _object_spread$k(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property$k(target, key, source[key]);
        });
    }
    return target;
}
function ownKeys$k(object, enumerableOnly) {
    var keys = Object.keys(object);
    if (Object.getOwnPropertySymbols) {
        var symbols = Object.getOwnPropertySymbols(object);
        keys.push.apply(keys, symbols);
    }
    return keys;
}
function _object_spread_props$k(target, source) {
    source = source != null ? source : {};
    if (Object.getOwnPropertyDescriptors) {
        Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
        ownKeys$k(Object(source)).forEach(function(key) {
            Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
        });
    }
    return target;
}
function _to_consumable_array$5(arr) {
    return _array_without_holes$5(arr) || _iterable_to_array$5(arr) || _unsupported_iterable_to_array$8(arr) || _non_iterable_spread$5();
}
function _unsupported_iterable_to_array$8(o, minLen) {
    if (!o) return;
    if (typeof o === "string") return _array_like_to_array$8(o, minLen);
    var n = Object.prototype.toString.call(o).slice(8, -1);
    if (n === "Object" && o.constructor) n = o.constructor.name;
    if (n === "Map" || n === "Set") return Array.from(n);
    if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _array_like_to_array$8(o, minLen);
}
function _ts_generator$p(thisArg, body) {
    var f, y, t, g, _ = {
        label: 0,
        sent: function() {
            if (t[0] & 1) throw t[1];
            return t[1];
        },
        trys: [],
        ops: []
    };
    return g = {
        next: verb(0),
        "throw": verb(1),
        "return": verb(2)
    }, typeof Symbol === "function" && (g[Symbol.iterator] = function() {
        return this;
    }), g;
    function verb(n) {
        return function(v) {
            return step([
                n,
                v
            ]);
        };
    }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while(_)try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [
                op[0] & 2,
                t.value
            ];
            switch(op[0]){
                case 0:
                case 1:
                    t = op;
                    break;
                case 4:
                    _.label++;
                    return {
                        value: op[1],
                        done: false
                    };
                case 5:
                    _.label++;
                    y = op[1];
                    op = [
                        0
                    ];
                    continue;
                case 7:
                    op = _.ops.pop();
                    _.trys.pop();
                    continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) {
                        _ = 0;
                        continue;
                    }
                    if (op[0] === 3 && (!t || op[1] > t[0] && op[1] < t[3])) {
                        _.label = op[1];
                        break;
                    }
                    if (op[0] === 6 && _.label < t[1]) {
                        _.label = t[1];
                        t = op;
                        break;
                    }
                    if (t && _.label < t[2]) {
                        _.label = t[2];
                        _.ops.push(op);
                        break;
                    }
                    if (t[2]) _.ops.pop();
                    _.trys.pop();
                    continue;
            }
            op = body.call(thisArg, _);
        } catch (e) {
            op = [
                6,
                e
            ];
            y = 0;
        } finally{
            f = t = 0;
        }
        if (op[0] & 5) throw op[1];
        return {
            value: op[0] ? op[1] : void 0,
            done: true
        };
    }
}
var buildRecordsBySubnameKey = function(subname, chainId) {
    var standard = arguments.length > 2 && arguments[2] !== void 0 ? arguments[2] : false;
    return [
        'RECORDS_BY_SUBNAME',
        subname,
        chainId,
        standard
    ];
};
var useRecords = function(params) {
    var _useJustaName = useJustaName(), justaname = _useJustaName.justaname, chainId = _useJustaName.chainId, networks = _useJustaName.networks;
    var queryClient = useQueryClient();
    var _enabled = (params === null || params === void 0 ? void 0 : params.enabled) !== undefined ? params.enabled : true;
    var _chainId = useMemo(function() {
        return (params === null || params === void 0 ? void 0 : params.chainId) || chainId;
    }, [
        params === null || params === void 0 ? void 0 : params.chainId,
        chainId
    ]);
    var _ens = useMemo(function() {
        return normalizeEns(params === null || params === void 0 ? void 0 : params.ens);
    }, [
        params === null || params === void 0 ? void 0 : params.ens
    ]);
    var offchainResolvers = useOffchainResolvers().offchainResolvers;
    var ensClient = useEnsPublicClient({
        chainId: _chainId
    }).ensClient;
    var _networks = useMemo(function() {
        return networks.find(function(network) {
            return network.chainId === _chainId;
        });
    }, [
        _chainId,
        networks
    ]);
    var _providerUrl = useMemo(function() {
        return _networks === null || _networks === void 0 ? void 0 : _networks.providerUrl;
    }, [
        _networks
    ]);
    var getRecords$1 = function() {
        var _ref = _async_to_generator$p(function(_params) {
            var __ens, result, sanitized;
            return _ts_generator$p(this, function(_state) {
                switch(_state.label){
                    case 0:
                        __ens = normalizeEns(_params.ens);
                        if (!__ens) {
                            throw new Error('Invalid ENS name');
                        }
                        if (!validateEns(__ens)) {
                            throw new Error('Invalid ENS name');
                        }
                        return [
                            4,
                            justaname.subnames.getRecords({
                                ens: __ens,
                                providerUrl: _params.providerUrl,
                                chainId: _params.chainId
                            })
                        ];
                    case 1:
                        result = _state.sent();
                        checkEnsValid(result);
                        sanitized = sanitizeRecords(result);
                        return [
                            2,
                            _object_spread_props$k(_object_spread$k({}, result), {
                                sanitizedRecords: sanitized
                            })
                        ];
                }
            });
        });
        return function getRecords(_params) {
            return _ref.apply(this, arguments);
        };
    }();
    var getStandardRecords = function() {
        var _ref = _async_to_generator$p(function(_params) {
            var _offchainResolvers_offchainResolvers, _result_contentHash, _result_contentHash1, __ens, result, __chainId, offchainResolver, record, sanitized;
            return _ts_generator$p(this, function(_state) {
                switch(_state.label){
                    case 0:
                        if (!ensClient) {
                            throw new Error('Public client not found');
                        }
                        __ens = normalizeEns(_params.ens) || _ens;
                        if (!__ens) {
                            throw new Error('Invalid ENS name');
                        }
                        if (!validateEns(__ens)) {
                            throw new Error('Invalid ENS name');
                        }
                        return [
                            4,
                            getRecords(ensClient, {
                                name: __ens,
                                coins: Object.keys(coinTypeMap),
                                texts: _to_consumable_array$5(generalKeys).concat(_to_consumable_array$5(SUPPORTED_SOCIALS.map(function(social) {
                                    return social.identifier;
                                }))),
                                contentHash: true
                            })
                        ];
                    case 1:
                        result = _state.sent();
                        __chainId = (_params === null || _params === void 0 ? void 0 : _params.chainId) || _chainId;
                        offchainResolver = offchainResolvers === null || offchainResolvers === void 0 ? void 0 : (_offchainResolvers_offchainResolvers = offchainResolvers.offchainResolvers) === null || _offchainResolvers_offchainResolvers === void 0 ? void 0 : _offchainResolvers_offchainResolvers.find(function(resolver) {
                            return resolver.chainId === __chainId;
                        });
                        record = {
                            ens: __ens,
                            isJAN: result.resolverAddress === (offchainResolver === null || offchainResolver === void 0 ? void 0 : offchainResolver.resolverAddress),
                            records: _object_spread_props$k(_object_spread$k({}, result), {
                                contentHash: {
                                    protocolType: ((_result_contentHash = result.contentHash) === null || _result_contentHash === void 0 ? void 0 : _result_contentHash.protocolType) || '',
                                    decoded: ((_result_contentHash1 = result.contentHash) === null || _result_contentHash1 === void 0 ? void 0 : _result_contentHash1.decoded) || ''
                                }
                            })
                        };
                        checkEnsValid(record);
                        sanitized = sanitizeRecords(record);
                        return [
                            2,
                            _object_spread_props$k(_object_spread$k({}, record), {
                                sanitizedRecords: sanitized
                            })
                        ];
                }
            });
        });
        return function getStandardRecords(_params) {
            return _ref.apply(this, arguments);
        };
    }();
    var getRecordsInternal = function() {
        var _ref = _async_to_generator$p(function(_params) {
            var forceUpdate, __chainId, __ens, __standard, cachedRecords, __networks, __providerUrl, __skipQueue, taskFn, records;
            var _arguments = arguments;
            return _ts_generator$p(this, function(_state) {
                switch(_state.label){
                    case 0:
                        forceUpdate = _arguments.length > 1 && _arguments[1] !== void 0 ? _arguments[1] : false;
                        __chainId = (_params === null || _params === void 0 ? void 0 : _params.chainId) || _chainId;
                        __ens = normalizeEns(_params === null || _params === void 0 ? void 0 : _params.ens) || _ens;
                        if (!__ens) {
                            throw new Error('Invalid ENS name');
                        }
                        if (!validateEns(__ens)) {
                            throw new Error('Invalid ENS name');
                        }
                        // const __standard = _params?.standard || params?.standard;
                        __standard = false;
                        if (!forceUpdate) {
                            cachedRecords = queryClient.getQueryData(buildRecordsBySubnameKey(__ens, __chainId, __standard));
                            if (cachedRecords) {
                                return [
                                    2,
                                    cachedRecords
                                ];
                            }
                        }
                        __networks = networks.find(function(network) {
                            return network.chainId === __chainId;
                        });
                        __providerUrl = __networks === null || __networks === void 0 ? void 0 : __networks.providerUrl;
                        __skipQueue = (_params === null || _params === void 0 ? void 0 : _params.skipQueue) || (params === null || params === void 0 ? void 0 : params.skipQueue);
                        if (!__providerUrl) {
                            throw new Error('ChainId not found');
                        }
                        taskFn = function() {
                            var _ref = _async_to_generator$p(function() {
                                var records, error;
                                return _ts_generator$p(this, function(_state) {
                                    switch(_state.label){
                                        case 0:
                                            _state.trys.push([
                                                0,
                                                2,
                                                ,
                                                4
                                            ]);
                                            return [
                                                4,
                                                getRecords$1({
                                                    ens: __ens,
                                                    chainId: __chainId,
                                                    providerUrl: __providerUrl
                                                })
                                            ];
                                        case 1:
                                            records = _state.sent();
                                            return [
                                                3,
                                                4
                                            ];
                                        case 2:
                                            error = _state.sent();
                                            if (_instanceof(error, Error) && (error.message.includes('NotFound') || error.message.includes('ETH address not found'))) {
                                                throw error;
                                            }
                                            return [
                                                4,
                                                getStandardRecords({
                                                    ens: __ens,
                                                    chainId: __chainId,
                                                    providerUrl: __providerUrl
                                                })
                                            ];
                                        case 3:
                                            records = _state.sent();
                                            return [
                                                3,
                                                4
                                            ];
                                        case 4:
                                            return [
                                                2,
                                                records
                                            ];
                                    }
                                });
                            });
                            return function taskFn() {
                                return _ref.apply(this, arguments);
                            };
                        }();
                        if (!__skipQueue) return [
                            3,
                            2
                        ];
                        return [
                            4,
                            taskFn()
                        ];
                    case 1:
                        records = _state.sent();
                        return [
                            3,
                            4
                        ];
                    case 2:
                        return [
                            4,
                            RecordsTaskQueue.enqueue(taskFn)
                        ];
                    case 3:
                        records = _state.sent();
                        _state.label = 4;
                    case 4:
                        // }
                        queryClient.setQueryData(buildRecordsBySubnameKey(__ens, __chainId, __standard), records);
                        return [
                            2,
                            records
                        ];
                }
            });
        });
        return function getRecordsInternal(_params) {
            return _ref.apply(this, arguments);
        };
    }();
    var query = useQuery(_object_spread_props$k(_object_spread$k({}, defaultOptions), {
        retry: function(failureCount, error) {
            if (_instanceof(error, Error)) {
                if (error.message.includes('NotFound') || error.message.includes('ETH address not found')) {
                    return false;
                }
            }
            return failureCount < 3;
        },
        queryKey: buildRecordsBySubnameKey(_ens || '', _chainId),
        queryFn: function() {
            return getRecordsInternal({
                ens: _ens || '',
                chainId: _chainId
            }, true);
        },
        enabled: Boolean(_ens) && Boolean(_chainId) && Boolean(_providerUrl) && Boolean(validateEns(_ens)) && Boolean(_enabled)
    }));
    return {
        isRecordsPending: query.isPending,
        isRecordsFetching: query.isFetching,
        isRecordsLoading: query.isPending || query.isFetching,
        records: query.data,
        getRecords: getRecordsInternal,
        refetchRecords: query.refetch,
        recordsStatus: query.status
    };
};

function asyncGeneratorStep$o(gen, resolve, reject, _next, _throw, key, arg) {
    try {
        var info = gen[key](arg);
        var value = info.value;
    } catch (error) {
        reject(error);
        return;
    }
    if (info.done) {
        resolve(value);
    } else {
        Promise.resolve(value).then(_next, _throw);
    }
}
function _async_to_generator$o(fn) {
    return function() {
        var self = this, args = arguments;
        return new Promise(function(resolve, reject) {
            var gen = fn.apply(self, args);
            function _next(value) {
                asyncGeneratorStep$o(gen, resolve, reject, _next, _throw, "next", value);
            }
            function _throw(err) {
                asyncGeneratorStep$o(gen, resolve, reject, _next, _throw, "throw", err);
            }
            _next(undefined);
        });
    };
}
function _define_property$j(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _object_spread$j(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property$j(target, key, source[key]);
        });
    }
    return target;
}
function ownKeys$j(object, enumerableOnly) {
    var keys = Object.keys(object);
    if (Object.getOwnPropertySymbols) {
        var symbols = Object.getOwnPropertySymbols(object);
        keys.push.apply(keys, symbols);
    }
    return keys;
}
function _object_spread_props$j(target, source) {
    source = source != null ? source : {};
    if (Object.getOwnPropertyDescriptors) {
        Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
        ownKeys$j(Object(source)).forEach(function(key) {
            Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
        });
    }
    return target;
}
function _ts_generator$o(thisArg, body) {
    var f, y, t, g, _ = {
        label: 0,
        sent: function() {
            if (t[0] & 1) throw t[1];
            return t[1];
        },
        trys: [],
        ops: []
    };
    return g = {
        next: verb(0),
        "throw": verb(1),
        "return": verb(2)
    }, typeof Symbol === "function" && (g[Symbol.iterator] = function() {
        return this;
    }), g;
    function verb(n) {
        return function(v) {
            return step([
                n,
                v
            ]);
        };
    }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while(_)try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [
                op[0] & 2,
                t.value
            ];
            switch(op[0]){
                case 0:
                case 1:
                    t = op;
                    break;
                case 4:
                    _.label++;
                    return {
                        value: op[1],
                        done: false
                    };
                case 5:
                    _.label++;
                    y = op[1];
                    op = [
                        0
                    ];
                    continue;
                case 7:
                    op = _.ops.pop();
                    _.trys.pop();
                    continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) {
                        _ = 0;
                        continue;
                    }
                    if (op[0] === 3 && (!t || op[1] > t[0] && op[1] < t[3])) {
                        _.label = op[1];
                        break;
                    }
                    if (op[0] === 6 && _.label < t[1]) {
                        _.label = t[1];
                        t = op;
                        break;
                    }
                    if (t && _.label < t[2]) {
                        _.label = t[2];
                        _.ops.push(op);
                        break;
                    }
                    if (t[2]) _.ops.pop();
                    _.trys.pop();
                    continue;
            }
            op = body.call(thisArg, _);
        } catch (e) {
            op = [
                6,
                e
            ];
            y = 0;
        } finally{
            f = t = 0;
        }
        if (op[0] & 5) throw op[1];
        return {
            value: op[0] ? op[1] : void 0,
            done: true
        };
    }
}
var buildEnsAvatarKey = function(ens, chainId) {
    return [
        'ENS_AVATAR',
        ens,
        chainId
    ];
};
var useEnsAvatar = function(params) {
    var chainId = useJustaName().chainId;
    var _chainId = useMemo(function() {
        return (params === null || params === void 0 ? void 0 : params.chainId) || chainId;
    }, [
        params === null || params === void 0 ? void 0 : params.chainId,
        chainId
    ]);
    var _enabled = (params === null || params === void 0 ? void 0 : params.enabled) !== undefined ? params.enabled : true;
    var _useRecords = useRecords({
        chainId: _chainId,
        ens: params === null || params === void 0 ? void 0 : params.ens,
        enabled: _enabled
    }), records = _useRecords.records, getRecords = _useRecords.getRecords;
    var sanitizeEnsImage = function(_params) {
        var _params_image, _params_image1;
        var _chainId = _params.chainId || chainId || 1;
        var avatar = _params.image;
        if ((_params_image = _params.image) === null || _params_image === void 0 ? void 0 : _params_image.startsWith('eip')) {
            avatar = "https://metadata.ens.domains/".concat(_chainId === 1 ? 'mainnet' : 'sepolia', "/avatar/").concat(_params.name);
        }
        if ((_params_image1 = _params.image) === null || _params_image1 === void 0 ? void 0 : _params_image1.startsWith('ipfs')) {
            avatar = "https://ipfs.io/ipfs/".concat(_params.image.replace('ipfs://', ''));
        }
        return avatar;
    };
    var getEnsAvatar = function() {
        var _ref = _async_to_generator$o(function(_params) {
            var _records, name, __chainId, avatar;
            return _ts_generator$o(this, function(_state) {
                switch(_state.label){
                    case 0:
                        _records = records;
                        name = _params.name;
                        __chainId = _params.chainId || _chainId || 1;
                        if (!name) {
                            return [
                                2,
                                ''
                            ];
                        }
                        if (!!_records) return [
                            3,
                            2
                        ];
                        return [
                            4,
                            getRecords({
                                ens: name,
                                chainId: __chainId
                            })
                        ];
                    case 1:
                        _records = _state.sent();
                        _state.label = 2;
                    case 2:
                        avatar = '';
                        if (_records === null || _records === void 0 ? void 0 : _records.sanitizedRecords.avatar) {
                            avatar = sanitizeEnsImage({
                                name: _params.name,
                                image: _records === null || _records === void 0 ? void 0 : _records.sanitizedRecords.avatar,
                                chainId: __chainId
                            });
                        }
                        return [
                            2,
                            avatar ? avatar : ''
                        ];
                }
            });
        });
        return function getEnsAvatar(_params) {
            return _ref.apply(this, arguments);
        };
    }();
    var query = useQuery(_object_spread_props$j(_object_spread$j({}, defaultOptions), {
        queryKey: buildEnsAvatarKey(params === null || params === void 0 ? void 0 : params.ens, _chainId),
        queryFn: /*#__PURE__*/ _async_to_generator$o(function() {
            return _ts_generator$o(this, function(_state) {
                if (!(params === null || params === void 0 ? void 0 : params.ens)) {
                    return [
                        2,
                        undefined
                    ];
                }
                return [
                    2,
                    getEnsAvatar({
                        name: params === null || params === void 0 ? void 0 : params.ens,
                        chainId: _chainId
                    })
                ];
            });
        }),
        enabled: Boolean(params === null || params === void 0 ? void 0 : params.ens) && Boolean(records) && Boolean(_enabled)
    }));
    useEffect(function() {
        if (records) {
            query.refetch();
        }
    }, [
        records
    ]);
    return {
        avatar: query.data === null ? undefined : query.data,
        getEnsAvatar: getEnsAvatar,
        sanitizeEnsImage: sanitizeEnsImage,
        isLoading: query.isLoading
    };
};

function _array_like_to_array$7(arr, len) {
    if (len == null || len > arr.length) len = arr.length;
    for(var i = 0, arr2 = new Array(len); i < len; i++)arr2[i] = arr[i];
    return arr2;
}
function _array_without_holes$4(arr) {
    if (Array.isArray(arr)) return _array_like_to_array$7(arr);
}
function asyncGeneratorStep$n(gen, resolve, reject, _next, _throw, key, arg) {
    try {
        var info = gen[key](arg);
        var value = info.value;
    } catch (error) {
        reject(error);
        return;
    }
    if (info.done) {
        resolve(value);
    } else {
        Promise.resolve(value).then(_next, _throw);
    }
}
function _async_to_generator$n(fn) {
    return function() {
        var self = this, args = arguments;
        return new Promise(function(resolve, reject) {
            var gen = fn.apply(self, args);
            function _next(value) {
                asyncGeneratorStep$n(gen, resolve, reject, _next, _throw, "next", value);
            }
            function _throw(err) {
                asyncGeneratorStep$n(gen, resolve, reject, _next, _throw, "throw", err);
            }
            _next(undefined);
        });
    };
}
function _define_property$i(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _iterable_to_array$4(iter) {
    if (typeof Symbol !== "undefined" && iter[Symbol.iterator] != null || iter["@@iterator"] != null) return Array.from(iter);
}
function _non_iterable_spread$4() {
    throw new TypeError("Invalid attempt to spread non-iterable instance.\\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}
function _object_spread$i(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property$i(target, key, source[key]);
        });
    }
    return target;
}
function ownKeys$i(object, enumerableOnly) {
    var keys = Object.keys(object);
    if (Object.getOwnPropertySymbols) {
        var symbols = Object.getOwnPropertySymbols(object);
        keys.push.apply(keys, symbols);
    }
    return keys;
}
function _object_spread_props$i(target, source) {
    source = source != null ? source : {};
    if (Object.getOwnPropertyDescriptors) {
        Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
        ownKeys$i(Object(source)).forEach(function(key) {
            Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
        });
    }
    return target;
}
function _to_consumable_array$4(arr) {
    return _array_without_holes$4(arr) || _iterable_to_array$4(arr) || _unsupported_iterable_to_array$7(arr) || _non_iterable_spread$4();
}
function _unsupported_iterable_to_array$7(o, minLen) {
    if (!o) return;
    if (typeof o === "string") return _array_like_to_array$7(o, minLen);
    var n = Object.prototype.toString.call(o).slice(8, -1);
    if (n === "Object" && o.constructor) n = o.constructor.name;
    if (n === "Map" || n === "Set") return Array.from(n);
    if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _array_like_to_array$7(o, minLen);
}
function _ts_generator$n(thisArg, body) {
    var f, y, t, g, _ = {
        label: 0,
        sent: function() {
            if (t[0] & 1) throw t[1];
            return t[1];
        },
        trys: [],
        ops: []
    };
    return g = {
        next: verb(0),
        "throw": verb(1),
        "return": verb(2)
    }, typeof Symbol === "function" && (g[Symbol.iterator] = function() {
        return this;
    }), g;
    function verb(n) {
        return function(v) {
            return step([
                n,
                v
            ]);
        };
    }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while(_)try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [
                op[0] & 2,
                t.value
            ];
            switch(op[0]){
                case 0:
                case 1:
                    t = op;
                    break;
                case 4:
                    _.label++;
                    return {
                        value: op[1],
                        done: false
                    };
                case 5:
                    _.label++;
                    y = op[1];
                    op = [
                        0
                    ];
                    continue;
                case 7:
                    op = _.ops.pop();
                    _.trys.pop();
                    continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) {
                        _ = 0;
                        continue;
                    }
                    if (op[0] === 3 && (!t || op[1] > t[0] && op[1] < t[3])) {
                        _.label = op[1];
                        break;
                    }
                    if (op[0] === 6 && _.label < t[1]) {
                        _.label = t[1];
                        t = op;
                        break;
                    }
                    if (t && _.label < t[2]) {
                        _.label = t[2];
                        _.ops.push(op);
                        break;
                    }
                    if (t[2]) _.ops.pop();
                    _.trys.pop();
                    continue;
            }
            op = body.call(thisArg, _);
        } catch (e) {
            op = [
                6,
                e
            ];
            y = 0;
        } finally{
            f = t = 0;
        }
        if (op[0] & 5) throw op[1];
        return {
            value: op[0] ? op[1] : void 0,
            done: true
        };
    }
}
var buildStandardRecordsBatchByEnsesKey = function(enses, chainId) {
    return [
        'ALL_RECORDS_BY_ENSES',
        enses,
        chainId
    ];
};
var buildStandardRecordsBatchKey = function(chainId) {
    return [
        'ALL_RECORDS',
        chainId
    ];
};
var useStandardRecordsBatch = function(params) {
    var _params_enses;
    var _useJustaName = useJustaName(), chainId = _useJustaName.chainId, networks = _useJustaName.networks;
    var queryClient = useQueryClient();
    var offchainResolvers = useOffchainResolvers().offchainResolvers;
    var getRecords$1 = useRecords().getRecords;
    var _enabled = (params === null || params === void 0 ? void 0 : params.enabled) !== undefined ? params.enabled : true;
    var _chainId = useMemo(function() {
        return (params === null || params === void 0 ? void 0 : params.chainId) || chainId;
    }, [
        params === null || params === void 0 ? void 0 : params.chainId,
        chainId
    ]);
    var ensClient = useEnsPublicClient({
        chainId: _chainId
    }).ensClient;
    var _networks = useMemo(function() {
        return networks.find(function(network) {
            return network.chainId === _chainId;
        });
    }, [
        _chainId,
        networks
    ]);
    var _providerUrl = useMemo(function() {
        return _networks === null || _networks === void 0 ? void 0 : _networks.providerUrl;
    }, [
        _networks
    ]);
    var getStandardRecordsBatch = function() {
        var _ref = _async_to_generator$n(function(_params) {
            var _params_enses, _offchainResolvers_offchainResolvers, enses, __chainId, offchainResolver, recordsBatch, checkMissing, resolverAddressesCalls, resolverAddresses, ensIsJAN, janEns, nonJanEns, batchCalls, batchJanCalls, ensRecords, janRecords;
            return _ts_generator$n(this, function(_state) {
                switch(_state.label){
                    case 0:
                        if (!ensClient) {
                            throw new Error('Public client not found');
                        }
                        enses = (params === null || params === void 0 ? void 0 : (_params_enses = params.enses) === null || _params_enses === void 0 ? void 0 : _params_enses.filter(function(ens) {
                            return ens !== undefined && ens !== '';
                        })) || [];
                        if (!(params === null || params === void 0 ? void 0 : params.enses) || enses.length === 0) {
                            throw new Error('Enses is required');
                        }
                        __chainId = (_params === null || _params === void 0 ? void 0 : _params.chainId) || _chainId;
                        offchainResolver = offchainResolvers === null || offchainResolvers === void 0 ? void 0 : (_offchainResolvers_offchainResolvers = offchainResolvers.offchainResolvers) === null || _offchainResolvers_offchainResolvers === void 0 ? void 0 : _offchainResolvers_offchainResolvers.find(function(resolver) {
                            return resolver.chainId === __chainId;
                        });
                        recordsBatch = enses === null || enses === void 0 ? void 0 : enses.reduce(function(acc, ens) {
                            if (ens) {
                                acc[ens] = undefined;
                            }
                            return acc;
                        }, {});
                        enses.forEach(function(ensName) {
                            var ens = queryClient.getQueryData(buildRecordsBySubnameKey(ensName, _chainId, true));
                            if (ens) {
                                recordsBatch[ensName] = ens;
                            } else {
                                recordsBatch[ensName] = undefined;
                            }
                        });
                        checkMissing = Object.keys(recordsBatch).filter(function(ensName) {
                            return recordsBatch[ensName] === undefined;
                        });
                        resolverAddressesCalls = checkMissing === null || checkMissing === void 0 ? void 0 : checkMissing.map(function(ensName) {
                            return getResolver.batch({
                                name: ensName
                            });
                        });
                        return [
                            4,
                            batch.apply(void 0, [
                                ensClient
                            ].concat(_to_consumable_array$4(resolverAddressesCalls)))
                        ];
                    case 1:
                        resolverAddresses = _state.sent();
                        ensIsJAN = resolverAddresses === null || resolverAddresses === void 0 ? void 0 : resolverAddresses.map(function(result) {
                            return result === (offchainResolver === null || offchainResolver === void 0 ? void 0 : offchainResolver.resolverAddress);
                        });
                        janEns = checkMissing === null || checkMissing === void 0 ? void 0 : checkMissing.filter(function(_, index) {
                            return ensIsJAN[index];
                        });
                        nonJanEns = checkMissing === null || checkMissing === void 0 ? void 0 : checkMissing.filter(function(_, index) {
                            return !ensIsJAN[index];
                        });
                        batchCalls = nonJanEns === null || nonJanEns === void 0 ? void 0 : nonJanEns.map(function(ensName) {
                            return getRecords.batch({
                                name: ensName,
                                coins: Object.keys(coinTypeMap),
                                texts: _to_consumable_array$4(generalKeys).concat(_to_consumable_array$4(SUPPORTED_SOCIALS.map(function(social) {
                                    return social.identifier;
                                }))),
                                contentHash: true
                            });
                        });
                        batchJanCalls = janEns === null || janEns === void 0 ? void 0 : janEns.map(function(ensName) {
                            return getRecords$1({
                                ens: ensName,
                                chainId: _chainId
                            });
                        });
                        return [
                            4,
                            batch.apply(void 0, [
                                ensClient
                            ].concat(_to_consumable_array$4(batchCalls)))
                        ];
                    case 2:
                        ensRecords = _state.sent();
                        return [
                            4,
                            Promise.all(batchJanCalls)
                        ];
                    case 3:
                        janRecords = _state.sent();
                        ensRecords.forEach(function(result, index) {
                            if (result) {
                                var _result_contentHash, _result_contentHash1;
                                var record = {
                                    ens: nonJanEns[index],
                                    isJAN: result.resolverAddress === (offchainResolver === null || offchainResolver === void 0 ? void 0 : offchainResolver.resolverAddress),
                                    records: _object_spread_props$i(_object_spread$i({}, result), {
                                        contentHash: {
                                            protocolType: ((_result_contentHash = result.contentHash) === null || _result_contentHash === void 0 ? void 0 : _result_contentHash.protocolType) || '',
                                            decoded: ((_result_contentHash1 = result.contentHash) === null || _result_contentHash1 === void 0 ? void 0 : _result_contentHash1.decoded) || ''
                                        }
                                    })
                                };
                                checkEnsValid(record);
                                var sanitized = sanitizeRecords(record);
                                recordsBatch[nonJanEns[index]] = _object_spread_props$i(_object_spread$i({}, record), {
                                    sanitizedRecords: sanitized
                                });
                            } else {
                                recordsBatch[nonJanEns[index]] = undefined;
                            }
                        });
                        janRecords.forEach(function(result, index) {
                            if (result) {
                                recordsBatch[janEns[index]] = result;
                            } else {
                                recordsBatch[janEns[index]] = undefined;
                            }
                        });
                        Object.keys(recordsBatch).forEach(function(ensName) {
                            queryClient.setQueryData(buildRecordsBySubnameKey(ensName, _chainId, true), recordsBatch[ensName]);
                        });
                        queryClient.setQueryData(buildStandardRecordsBatchKey(_chainId), function(old) {
                            return _object_spread$i({}, old, recordsBatch);
                        });
                        return [
                            2,
                            recordsBatch
                        ];
                }
            });
        });
        return function getStandardRecordsBatch(_params) {
            return _ref.apply(this, arguments);
        };
    }();
    var query = useQuery(_object_spread_props$i(_object_spread$i({}, defaultOptions), {
        queryKey: buildStandardRecordsBatchByEnsesKey((params === null || params === void 0 ? void 0 : params.enses) || [], _chainId),
        queryFn: function() {
            return getStandardRecordsBatch({
                enses: (params === null || params === void 0 ? void 0 : params.enses) || [],
                chainId: _chainId
            });
        },
        enabled: Boolean((params === null || params === void 0 ? void 0 : params.enses) && (params === null || params === void 0 ? void 0 : (_params_enses = params.enses) === null || _params_enses === void 0 ? void 0 : _params_enses.length) > 0) && Boolean(_chainId) && Boolean(_providerUrl) && Boolean(_enabled)
    }));
    var allQuery = useQuery(_object_spread_props$i(_object_spread$i({}, defaultOptions), {
        queryKey: buildStandardRecordsBatchKey(_chainId),
        queryFn: function() {
            return {};
        },
        enabled: false
    }));
    return {
        isStandardRecordsBatchPending: query.isPending,
        isStandardRecordsBatchFetching: query.isFetching,
        isStandardRecordsBatchLoading: query.isLoading,
        recordsBatch: query.data,
        allRecordsBatch: allQuery.data,
        refetchStandardRecordsBatch: query.refetch
    };
}; //
 // export const useStandardRecordsBatch = () => {
 //   throw new Error('Not implemented');
 // };

function _array_like_to_array$6(arr, len) {
    if (len == null || len > arr.length) len = arr.length;
    for(var i = 0, arr2 = new Array(len); i < len; i++)arr2[i] = arr[i];
    return arr2;
}
function _array_without_holes$3(arr) {
    if (Array.isArray(arr)) return _array_like_to_array$6(arr);
}
function asyncGeneratorStep$m(gen, resolve, reject, _next, _throw, key, arg) {
    try {
        var info = gen[key](arg);
        var value = info.value;
    } catch (error) {
        reject(error);
        return;
    }
    if (info.done) {
        resolve(value);
    } else {
        Promise.resolve(value).then(_next, _throw);
    }
}
function _async_to_generator$m(fn) {
    return function() {
        var self = this, args = arguments;
        return new Promise(function(resolve, reject) {
            var gen = fn.apply(self, args);
            function _next(value) {
                asyncGeneratorStep$m(gen, resolve, reject, _next, _throw, "next", value);
            }
            function _throw(err) {
                asyncGeneratorStep$m(gen, resolve, reject, _next, _throw, "throw", err);
            }
            _next(undefined);
        });
    };
}
function _define_property$h(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _iterable_to_array$3(iter) {
    if (typeof Symbol !== "undefined" && iter[Symbol.iterator] != null || iter["@@iterator"] != null) return Array.from(iter);
}
function _non_iterable_spread$3() {
    throw new TypeError("Invalid attempt to spread non-iterable instance.\\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}
function _object_spread$h(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property$h(target, key, source[key]);
        });
    }
    return target;
}
function ownKeys$h(object, enumerableOnly) {
    var keys = Object.keys(object);
    if (Object.getOwnPropertySymbols) {
        var symbols = Object.getOwnPropertySymbols(object);
        keys.push.apply(keys, symbols);
    }
    return keys;
}
function _object_spread_props$h(target, source) {
    source = source != null ? source : {};
    if (Object.getOwnPropertyDescriptors) {
        Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
        ownKeys$h(Object(source)).forEach(function(key) {
            Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
        });
    }
    return target;
}
function _to_consumable_array$3(arr) {
    return _array_without_holes$3(arr) || _iterable_to_array$3(arr) || _unsupported_iterable_to_array$6(arr) || _non_iterable_spread$3();
}
function _unsupported_iterable_to_array$6(o, minLen) {
    if (!o) return;
    if (typeof o === "string") return _array_like_to_array$6(o, minLen);
    var n = Object.prototype.toString.call(o).slice(8, -1);
    if (n === "Object" && o.constructor) n = o.constructor.name;
    if (n === "Map" || n === "Set") return Array.from(n);
    if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _array_like_to_array$6(o, minLen);
}
function _ts_generator$m(thisArg, body) {
    var f, y, t, g, _ = {
        label: 0,
        sent: function() {
            if (t[0] & 1) throw t[1];
            return t[1];
        },
        trys: [],
        ops: []
    };
    return g = {
        next: verb(0),
        "throw": verb(1),
        "return": verb(2)
    }, typeof Symbol === "function" && (g[Symbol.iterator] = function() {
        return this;
    }), g;
    function verb(n) {
        return function(v) {
            return step([
                n,
                v
            ]);
        };
    }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while(_)try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [
                op[0] & 2,
                t.value
            ];
            switch(op[0]){
                case 0:
                case 1:
                    t = op;
                    break;
                case 4:
                    _.label++;
                    return {
                        value: op[1],
                        done: false
                    };
                case 5:
                    _.label++;
                    y = op[1];
                    op = [
                        0
                    ];
                    continue;
                case 7:
                    op = _.ops.pop();
                    _.trys.pop();
                    continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) {
                        _ = 0;
                        continue;
                    }
                    if (op[0] === 3 && (!t || op[1] > t[0] && op[1] < t[3])) {
                        _.label = op[1];
                        break;
                    }
                    if (op[0] === 6 && _.label < t[1]) {
                        _.label = t[1];
                        t = op;
                        break;
                    }
                    if (t && _.label < t[2]) {
                        _.label = t[2];
                        _.ops.push(op);
                        break;
                    }
                    if (t[2]) _.ops.pop();
                    _.trys.pop();
                    continue;
            }
            op = body.call(thisArg, _);
        } catch (e) {
            op = [
                6,
                e
            ];
            y = 0;
        } finally{
            f = t = 0;
        }
        if (op[0] & 5) throw op[1];
        return {
            value: op[0] ? op[1] : void 0,
            done: true
        };
    }
}
var buildAddressEnsNames = function(address, chainId) {
    return [
        'ACCOUNT_ENS_NAMES',
        address,
        chainId
    ];
};
var useAddressEnsNames = function(params) {
    var ensClient = useEnsPublicClient().ensClient;
    var queryClient = useQueryClient();
    var chainId = useJustaName().chainId;
    var _chainId = (params === null || params === void 0 ? void 0 : params.chainId) || chainId;
    var _enabled = (params === null || params === void 0 ? void 0 : params.enabled) !== undefined ? params.enabled : true;
    var getRecords = useRecords().getRecords;
    var getEnsNamesForAddress = function() {
        var _ref = _async_to_generator$m(function(_params) {
            var names, records;
            return _ts_generator$m(this, function(_state) {
                switch(_state.label){
                    case 0:
                        if (!ensClient) {
                            throw new Error('Public client not found');
                        }
                        if (!(params === null || params === void 0 ? void 0 : params.address)) {
                            throw new Error('Address is required');
                        }
                        return [
                            4,
                            getNamesForAddress(ensClient, {
                                address: params === null || params === void 0 ? void 0 : params.address
                            })
                        ];
                    case 1:
                        names = _state.sent();
                        return [
                            4,
                            Promise.allSettled(_to_consumable_array$3(names.filter(function(name) {
                                return !!(name === null || name === void 0 ? void 0 : name.name);
                            }).map(function() {
                                var _ref = _async_to_generator$m(function(name) {
                                    return _ts_generator$m(this, function(_state) {
                                        return [
                                            2,
                                            getRecords({
                                                ens: (name === null || name === void 0 ? void 0 : name.name) || '',
                                                chainId: _chainId
                                            })
                                        ];
                                    });
                                });
                                return function(name) {
                                    return _ref.apply(this, arguments);
                                };
                            }())))
                        ];
                    case 2:
                        records = _state.sent();
                        return [
                            2,
                            records.filter(function(record) {
                                return record.status === 'fulfilled';
                            }).map(function(record) {
                                return record === null || record === void 0 ? void 0 : record.value;
                            }).filter(function(record) {
                                return !!record && !!record.sanitizedRecords;
                            })
                        ];
                }
            });
        });
        return function getEnsNamesForAddress(_params) {
            return _ref.apply(this, arguments);
        };
    }();
    var query = useQuery(_object_spread_props$h(_object_spread$h({}, defaultOptions), {
        queryKey: buildAddressEnsNames((params === null || params === void 0 ? void 0 : params.address) || '', _chainId),
        queryFn: function() {
            return getEnsNamesForAddress({
                address: params === null || params === void 0 ? void 0 : params.address
            });
        },
        enabled: Boolean(params === null || params === void 0 ? void 0 : params.address) && Boolean(_chainId) && Boolean(ensClient) && Boolean(_enabled)
    }));
    var getEnsNamesForAddressInternal = function() {
        var _ref = _async_to_generator$m(function(address) {
            var force, cachedNames, names;
            var _arguments = arguments;
            return _ts_generator$m(this, function(_state) {
                switch(_state.label){
                    case 0:
                        force = _arguments.length > 1 && _arguments[1] !== void 0 ? _arguments[1] : false;
                        if (!force) {
                            cachedNames = queryClient.getQueryData(buildAddressEnsNames(address, _chainId));
                            if (cachedNames) {
                                return [
                                    2,
                                    cachedNames
                                ];
                            }
                        }
                        return [
                            4,
                            getEnsNamesForAddress({
                                address: address
                            })
                        ];
                    case 1:
                        names = _state.sent();
                        queryClient.setQueryData(buildAddressEnsNames(address, _chainId), names);
                        return [
                            2,
                            names
                        ];
                }
            });
        });
        return function getEnsNamesForAddressInternal(address) {
            return _ref.apply(this, arguments);
        };
    }();
    var _query_data;
    return {
        addressEnsNames: (_query_data = query.data) !== null && _query_data !== void 0 ? _query_data : [],
        getEnsNamesForAddress: getEnsNamesForAddressInternal,
        isAddressEnsNamesPending: query.isPending,
        isAddressEnsNamesFetching: query.isFetching,
        isAddressEnsNamesLoading: query.isLoading,
        refetchAddressEnsNames: query.refetch
    };
};

function asyncGeneratorStep$l(gen, resolve, reject, _next, _throw, key, arg) {
    try {
        var info = gen[key](arg);
        var value = info.value;
    } catch (error) {
        reject(error);
        return;
    }
    if (info.done) {
        resolve(value);
    } else {
        Promise.resolve(value).then(_next, _throw);
    }
}
function _async_to_generator$l(fn) {
    return function() {
        var self = this, args = arguments;
        return new Promise(function(resolve, reject) {
            var gen = fn.apply(self, args);
            function _next(value) {
                asyncGeneratorStep$l(gen, resolve, reject, _next, _throw, "next", value);
            }
            function _throw(err) {
                asyncGeneratorStep$l(gen, resolve, reject, _next, _throw, "throw", err);
            }
            _next(undefined);
        });
    };
}
function _define_property$g(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _object_spread$g(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property$g(target, key, source[key]);
        });
    }
    return target;
}
function ownKeys$g(object, enumerableOnly) {
    var keys = Object.keys(object);
    if (Object.getOwnPropertySymbols) {
        var symbols = Object.getOwnPropertySymbols(object);
        keys.push.apply(keys, symbols);
    }
    return keys;
}
function _object_spread_props$g(target, source) {
    source = source != null ? source : {};
    if (Object.getOwnPropertyDescriptors) {
        Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
        ownKeys$g(Object(source)).forEach(function(key) {
            Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
        });
    }
    return target;
}
function _ts_generator$l(thisArg, body) {
    var f, y, t, g, _ = {
        label: 0,
        sent: function() {
            if (t[0] & 1) throw t[1];
            return t[1];
        },
        trys: [],
        ops: []
    };
    return g = {
        next: verb(0),
        "throw": verb(1),
        "return": verb(2)
    }, typeof Symbol === "function" && (g[Symbol.iterator] = function() {
        return this;
    }), g;
    function verb(n) {
        return function(v) {
            return step([
                n,
                v
            ]);
        };
    }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while(_)try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [
                op[0] & 2,
                t.value
            ];
            switch(op[0]){
                case 0:
                case 1:
                    t = op;
                    break;
                case 4:
                    _.label++;
                    return {
                        value: op[1],
                        done: false
                    };
                case 5:
                    _.label++;
                    y = op[1];
                    op = [
                        0
                    ];
                    continue;
                case 7:
                    op = _.ops.pop();
                    _.trys.pop();
                    continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) {
                        _ = 0;
                        continue;
                    }
                    if (op[0] === 3 && (!t || op[1] > t[0] && op[1] < t[3])) {
                        _.label = op[1];
                        break;
                    }
                    if (op[0] === 6 && _.label < t[1]) {
                        _.label = t[1];
                        t = op;
                        break;
                    }
                    if (t && _.label < t[2]) {
                        _.label = t[2];
                        _.ops.push(op);
                        break;
                    }
                    if (t[2]) _.ops.pop();
                    _.trys.pop();
                    continue;
            }
            op = body.call(thisArg, _);
        } catch (e) {
            op = [
                6,
                e
            ];
            y = 0;
        } finally{
            f = t = 0;
        }
        if (op[0] & 5) throw op[1];
        return {
            value: op[0] ? op[1] : void 0,
            done: true
        };
    }
}
var buildAllEnsKey = function(chainId, orderBy, orderDirection) {
    return [
        'ALL_ENS',
        chainId,
        orderBy,
        orderDirection
    ];
};
var useAllEns = function(params) {
    var orderDirection = params.orderDirection || 'desc';
    var _enabled = (params === null || params === void 0 ? void 0 : params.enabled) !== undefined ? params.enabled : true;
    var orderBy = params.orderBy || 'subnameCount';
    var _useJustaName = useJustaName(), justaname = _useJustaName.justaname, justChainId = _useJustaName.chainId;
    var chainId = params.chainId || justChainId;
    return useInfiniteQuery(_object_spread_props$g(_object_spread$g({}, defaultOptions), {
        queryKey: buildAllEnsKey(chainId, orderBy, orderDirection),
        queryFn: function() {
            var _ref = _async_to_generator$l(function(param) {
                var _param_pageParam, page, limit, response;
                return _ts_generator$l(this, function(_state) {
                    switch(_state.label){
                        case 0:
                            _param_pageParam = param.pageParam, page = _param_pageParam.page, limit = _param_pageParam.limit;
                            return [
                                4,
                                justaname === null || justaname === void 0 ? void 0 : justaname.subnames.getSubnamesByEnsDomainWithCount({
                                    chainId: chainId,
                                    page: page,
                                    limit: limit,
                                    orderBy: orderBy,
                                    orderDirection: orderDirection
                                })
                            ];
                        case 1:
                            response = _state.sent();
                            return [
                                2,
                                {
                                    data: response.data.map(function(subname) {
                                        return _object_spread_props$g(_object_spread$g({}, subname), {
                                            ensSubname: _object_spread_props$g(_object_spread$g({}, subname.ensSubname), {
                                                sanitizedRecords: sanitizeRecords(subname.ensSubname)
                                            })
                                        });
                                    }),
                                    pagination: response.pagination
                                }
                            ];
                    }
                });
            });
            return function(_) {
                return _ref.apply(this, arguments);
            };
        }(),
        initialPageParam: {
            page: 1,
            limit: 20
        },
        getNextPageParam: function(p) {
            if (p.pagination.hasNextPage) {
                var _p_pagination_nextPage;
                return {
                    page: (_p_pagination_nextPage = p.pagination.nextPage) !== null && _p_pagination_nextPage !== void 0 ? _p_pagination_nextPage : 1,
                    limit: 20
                };
            }
            return undefined;
        },
        getPreviousPageParam: function(p) {
            if (p.pagination.hasPrevPage) {
                var _p_pagination_prevPage;
                return {
                    page: (_p_pagination_prevPage = p.pagination.prevPage) !== null && _p_pagination_prevPage !== void 0 ? _p_pagination_prevPage : 1,
                    limit: 20
                };
            }
            return undefined;
        },
        enabled: Boolean(justaname) && Boolean(_enabled)
    }));
};

function asyncGeneratorStep$k(gen, resolve, reject, _next, _throw, key, arg) {
    try {
        var info = gen[key](arg);
        var value = info.value;
    } catch (error) {
        reject(error);
        return;
    }
    if (info.done) {
        resolve(value);
    } else {
        Promise.resolve(value).then(_next, _throw);
    }
}
function _async_to_generator$k(fn) {
    return function() {
        var self = this, args = arguments;
        return new Promise(function(resolve, reject) {
            var gen = fn.apply(self, args);
            function _next(value) {
                asyncGeneratorStep$k(gen, resolve, reject, _next, _throw, "next", value);
            }
            function _throw(err) {
                asyncGeneratorStep$k(gen, resolve, reject, _next, _throw, "throw", err);
            }
            _next(undefined);
        });
    };
}
function _define_property$f(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _object_spread$f(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property$f(target, key, source[key]);
        });
    }
    return target;
}
function ownKeys$f(object, enumerableOnly) {
    var keys = Object.keys(object);
    if (Object.getOwnPropertySymbols) {
        var symbols = Object.getOwnPropertySymbols(object);
        keys.push.apply(keys, symbols);
    }
    return keys;
}
function _object_spread_props$f(target, source) {
    source = source != null ? source : {};
    if (Object.getOwnPropertyDescriptors) {
        Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
        ownKeys$f(Object(source)).forEach(function(key) {
            Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
        });
    }
    return target;
}
function _ts_generator$k(thisArg, body) {
    var f, y, t, g, _ = {
        label: 0,
        sent: function() {
            if (t[0] & 1) throw t[1];
            return t[1];
        },
        trys: [],
        ops: []
    };
    return g = {
        next: verb(0),
        "throw": verb(1),
        "return": verb(2)
    }, typeof Symbol === "function" && (g[Symbol.iterator] = function() {
        return this;
    }), g;
    function verb(n) {
        return function(v) {
            return step([
                n,
                v
            ]);
        };
    }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while(_)try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [
                op[0] & 2,
                t.value
            ];
            switch(op[0]){
                case 0:
                case 1:
                    t = op;
                    break;
                case 4:
                    _.label++;
                    return {
                        value: op[1],
                        done: false
                    };
                case 5:
                    _.label++;
                    y = op[1];
                    op = [
                        0
                    ];
                    continue;
                case 7:
                    op = _.ops.pop();
                    _.trys.pop();
                    continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) {
                        _ = 0;
                        continue;
                    }
                    if (op[0] === 3 && (!t || op[1] > t[0] && op[1] < t[3])) {
                        _.label = op[1];
                        break;
                    }
                    if (op[0] === 6 && _.label < t[1]) {
                        _.label = t[1];
                        t = op;
                        break;
                    }
                    if (t && _.label < t[2]) {
                        _.label = t[2];
                        _.ops.push(op);
                        break;
                    }
                    if (t[2]) _.ops.pop();
                    _.trys.pop();
                    continue;
            }
            op = body.call(thisArg, _);
        } catch (e) {
            op = [
                6,
                e
            ];
            y = 0;
        } finally{
            f = t = 0;
        }
        if (op[0] & 5) throw op[1];
        return {
            value: op[0] ? op[1] : void 0,
            done: true
        };
    }
}
var buildEnsSubnamesKey = function(domainName, isClaimed, chainId, page, limit) {
    return [
        'ENS_SUBNAMES',
        domainName,
        isClaimed,
        chainId,
        page,
        limit
    ];
};
var useEnsSubnames = function(params) {
    var _useJustaName = useJustaName(), justaname = _useJustaName.justaname, chainId = _useJustaName.chainId;
    var _enabled = params.enabled !== undefined ? params.enabled : true;
    var _chainId = params.chainId || chainId;
    var records = useRecords({
        ens: params.ensDomain,
        chainId: _chainId
    }).records;
    var offchainResolvers = useOffchainResolvers().offchainResolvers;
    var isJustaNameResolver = useMemo(function() {
        if (!offchainResolvers) return false;
        if (!records) return false;
        var offchainResolver = offchainResolvers.offchainResolvers.find(function(resolver) {
            return resolver.chainId === _chainId;
        });
        if (!offchainResolver) return false;
        return records.records.resolverAddress === offchainResolver.resolverAddress;
    }, [
        offchainResolvers,
        records,
        _chainId
    ]);
    var _params_page, _params_limit, _params_page1;
    return useInfiniteQuery(_object_spread_props$f(_object_spread$f({}, defaultOptions), {
        queryKey: buildEnsSubnamesKey(params.ensDomain, params.isClaimed, params.chainId || chainId, (_params_page = params.page) !== null && _params_page !== void 0 ? _params_page : 1, (_params_limit = params.limit) !== null && _params_limit !== void 0 ? _params_limit : 20),
        queryFn: function() {
            var _ref = _async_to_generator$k(function(param) {
                var _param_pageParam, page, limit, subnames;
                return _ts_generator$k(this, function(_state) {
                    switch(_state.label){
                        case 0:
                            _param_pageParam = param.pageParam, page = _param_pageParam.page, limit = _param_pageParam.limit;
                            return [
                                4,
                                justaname === null || justaname === void 0 ? void 0 : justaname.subnames.getSubnamesByEnsDomain({
                                    ensDomain: params.ensDomain,
                                    isClaimed: params.isClaimed,
                                    chainId: (params === null || params === void 0 ? void 0 : params.chainId) ? params === null || params === void 0 ? void 0 : params.chainId : chainId,
                                    page: page,
                                    limit: limit
                                })
                            ];
                        case 1:
                            subnames = _state.sent();
                            return [
                                2,
                                {
                                    data: subnames.data.map(function(subname) {
                                        return _object_spread_props$f(_object_spread$f({}, subname), {
                                            sanitizedRecords: sanitizeRecords(subname)
                                        });
                                    }),
                                    pagination: subnames.pagination
                                }
                            ];
                    }
                });
            });
            return function(_) {
                return _ref.apply(this, arguments);
            };
        }(),
        initialPageParam: {
            page: (_params_page1 = params.page) !== null && _params_page1 !== void 0 ? _params_page1 : 1,
            limit: params.initialLimit || params.limit || 20
        },
        getNextPageParam: function(p) {
            if (p.pagination.hasNextPage) {
                var _p_pagination_nextPage, _ref, _params_limit;
                return {
                    page: (_ref = (_p_pagination_nextPage = p.pagination.nextPage) !== null && _p_pagination_nextPage !== void 0 ? _p_pagination_nextPage : params.page) !== null && _ref !== void 0 ? _ref : 1,
                    limit: (_params_limit = params.limit) !== null && _params_limit !== void 0 ? _params_limit : 20
                };
            }
            return undefined;
        },
        getPreviousPageParam: function(p) {
            if (p.pagination.hasPrevPage) {
                var _p_pagination_prevPage, _ref, _params_limit;
                return {
                    page: (_ref = (_p_pagination_prevPage = p.pagination.prevPage) !== null && _p_pagination_prevPage !== void 0 ? _p_pagination_prevPage : params.page) !== null && _ref !== void 0 ? _ref : 1,
                    limit: (_params_limit = params.limit) !== null && _params_limit !== void 0 ? _params_limit : 20
                };
            }
            return undefined;
        },
        enabled: Boolean(params.ensDomain) && Boolean(justaname) && Boolean(_enabled) && Boolean(records) && Boolean(offchainResolvers) && Boolean(isJustaNameResolver)
    }));
};

var useAccountEnsNames = function(props) {
    var chainId = useJustaName().chainId;
    var address = useMountedAccount().address;
    var _useAddressEnsNames = useAddressEnsNames({
        address: address,
        chainId: (props === null || props === void 0 ? void 0 : props.chainId) || chainId,
        enabled: props === null || props === void 0 ? void 0 : props.enabled
    }), addressEnsNames = _useAddressEnsNames.addressEnsNames, isAddressEnsNamesPending = _useAddressEnsNames.isAddressEnsNamesPending, isAddressEnsNamesFetching = _useAddressEnsNames.isAddressEnsNamesFetching, isAddressEnsNamesLoading = _useAddressEnsNames.isAddressEnsNamesLoading, refetchAddressEnsNames = _useAddressEnsNames.refetchAddressEnsNames;
    return {
        accountEnsNames: addressEnsNames,
        isAccountEnsNamesPending: isAddressEnsNamesPending,
        isAccountEnsNamesLoading: isAddressEnsNamesLoading,
        isAccountEnsNamesFetching: isAddressEnsNamesFetching,
        refetchAccountEnsNames: refetchAddressEnsNames
    };
};

function asyncGeneratorStep$j(gen, resolve, reject, _next, _throw, key, arg) {
    try {
        var info = gen[key](arg);
        var value = info.value;
    } catch (error) {
        reject(error);
        return;
    }
    if (info.done) {
        resolve(value);
    } else {
        Promise.resolve(value).then(_next, _throw);
    }
}
function _async_to_generator$j(fn) {
    return function() {
        var self = this, args = arguments;
        return new Promise(function(resolve, reject) {
            var gen = fn.apply(self, args);
            function _next(value) {
                asyncGeneratorStep$j(gen, resolve, reject, _next, _throw, "next", value);
            }
            function _throw(err) {
                asyncGeneratorStep$j(gen, resolve, reject, _next, _throw, "throw", err);
            }
            _next(undefined);
        });
    };
}
function _ts_generator$j(thisArg, body) {
    var f, y, t, g, _ = {
        label: 0,
        sent: function() {
            if (t[0] & 1) throw t[1];
            return t[1];
        },
        trys: [],
        ops: []
    };
    return g = {
        next: verb(0),
        "throw": verb(1),
        "return": verb(2)
    }, typeof Symbol === "function" && (g[Symbol.iterator] = function() {
        return this;
    }), g;
    function verb(n) {
        return function(v) {
            return step([
                n,
                v
            ]);
        };
    }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while(_)try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [
                op[0] & 2,
                t.value
            ];
            switch(op[0]){
                case 0:
                case 1:
                    t = op;
                    break;
                case 4:
                    _.label++;
                    return {
                        value: op[1],
                        done: false
                    };
                case 5:
                    _.label++;
                    y = op[1];
                    op = [
                        0
                    ];
                    continue;
                case 7:
                    op = _.ops.pop();
                    _.trys.pop();
                    continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) {
                        _ = 0;
                        continue;
                    }
                    if (op[0] === 3 && (!t || op[1] > t[0] && op[1] < t[3])) {
                        _.label = op[1];
                        break;
                    }
                    if (op[0] === 6 && _.label < t[1]) {
                        _.label = t[1];
                        t = op;
                        break;
                    }
                    if (t && _.label < t[2]) {
                        _.label = t[2];
                        _.ops.push(op);
                        break;
                    }
                    if (t[2]) _.ops.pop();
                    _.trys.pop();
                    continue;
            }
            op = body.call(thisArg, _);
        } catch (e) {
            op = [
                6,
                e
            ];
            y = 0;
        } finally{
            f = t = 0;
        }
        if (op[0] & 5) throw op[1];
        return {
            value: op[0] ? op[1] : void 0,
            done: true
        };
    }
}
var useAddMAppPermission = function(params) {
    var _useJustaName = useJustaName(), justaname = _useJustaName.justaname, chainId = _useJustaName.chainId;
    var signMessageAsync = useSignMessage().signMessageAsync;
    var address = useMountedAccount().address;
    var getRecords = useRecords().getRecords;
    var _chainId = useMemo(function() {
        return params.chainId || chainId;
    }, [
        params.chainId,
        chainId
    ]);
    var refetchAccountSubnames = useAccountSubnames().refetchAccountSubnames;
    var mutate = useMutation({
        mutationFn: function() {
            var _ref = _async_to_generator$j(function(_params) {
                var challengeResponse, signature, response;
                return _ts_generator$j(this, function(_state) {
                    switch(_state.label){
                        case 0:
                            if (!address) {
                                throw new Error('Wallet not connected');
                            }
                            return [
                                4,
                                justaname.mApps.requestAddMAppPermissionChallenge({
                                    subname: _params.subname,
                                    address: address,
                                    mApp: (_params === null || _params === void 0 ? void 0 : _params.mApp) || params.mApp,
                                    chainId: (_params === null || _params === void 0 ? void 0 : _params.chainId) || _chainId
                                })
                            ];
                        case 1:
                            challengeResponse = _state.sent();
                            return [
                                4,
                                signMessageAsync({
                                    message: challengeResponse.challenge,
                                    account: address
                                })
                            ];
                        case 2:
                            signature = _state.sent();
                            return [
                                4,
                                justaname.mApps.addMAppPermission({
                                    message: challengeResponse.challenge,
                                    address: address,
                                    signature: signature
                                })
                            ];
                        case 3:
                            response = _state.sent();
                            refetchAccountSubnames();
                            getRecords({
                                ens: _params.subname,
                                chainId: _chainId
                            }, true);
                            return [
                                2,
                                response
                            ];
                    }
                });
            });
            return function(_params) {
                return _ref.apply(this, arguments);
            };
        }()
    });
    return {
        addMAppPermission: mutate.mutateAsync,
        isAddMAppPermissionPending: mutate.isPending
    };
};

function asyncGeneratorStep$i(gen, resolve, reject, _next, _throw, key, arg) {
    try {
        var info = gen[key](arg);
        var value = info.value;
    } catch (error) {
        reject(error);
        return;
    }
    if (info.done) {
        resolve(value);
    } else {
        Promise.resolve(value).then(_next, _throw);
    }
}
function _async_to_generator$i(fn) {
    return function() {
        var self = this, args = arguments;
        return new Promise(function(resolve, reject) {
            var gen = fn.apply(self, args);
            function _next(value) {
                asyncGeneratorStep$i(gen, resolve, reject, _next, _throw, "next", value);
            }
            function _throw(err) {
                asyncGeneratorStep$i(gen, resolve, reject, _next, _throw, "throw", err);
            }
            _next(undefined);
        });
    };
}
function _ts_generator$i(thisArg, body) {
    var f, y, t, g, _ = {
        label: 0,
        sent: function() {
            if (t[0] & 1) throw t[1];
            return t[1];
        },
        trys: [],
        ops: []
    };
    return g = {
        next: verb(0),
        "throw": verb(1),
        "return": verb(2)
    }, typeof Symbol === "function" && (g[Symbol.iterator] = function() {
        return this;
    }), g;
    function verb(n) {
        return function(v) {
            return step([
                n,
                v
            ]);
        };
    }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while(_)try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [
                op[0] & 2,
                t.value
            ];
            switch(op[0]){
                case 0:
                case 1:
                    t = op;
                    break;
                case 4:
                    _.label++;
                    return {
                        value: op[1],
                        done: false
                    };
                case 5:
                    _.label++;
                    y = op[1];
                    op = [
                        0
                    ];
                    continue;
                case 7:
                    op = _.ops.pop();
                    _.trys.pop();
                    continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) {
                        _ = 0;
                        continue;
                    }
                    if (op[0] === 3 && (!t || op[1] > t[0] && op[1] < t[3])) {
                        _.label = op[1];
                        break;
                    }
                    if (op[0] === 6 && _.label < t[1]) {
                        _.label = t[1];
                        t = op;
                        break;
                    }
                    if (t && _.label < t[2]) {
                        _.label = t[2];
                        _.ops.push(op);
                        break;
                    }
                    if (t[2]) _.ops.pop();
                    _.trys.pop();
                    continue;
            }
            op = body.call(thisArg, _);
        } catch (e) {
            op = [
                6,
                e
            ];
            y = 0;
        } finally{
            f = t = 0;
        }
        if (op[0] & 5) throw op[1];
        return {
            value: op[0] ? op[1] : void 0,
            done: true
        };
    }
}
var useRevokeMAppPermission = function(params) {
    var _useJustaName = useJustaName(), justaname = _useJustaName.justaname, chainId = _useJustaName.chainId;
    var _chainId = useMemo(function() {
        return params.chainId || chainId;
    }, [
        params.chainId,
        chainId
    ]);
    var signMessageAsync = useSignMessage().signMessageAsync;
    var address = useMountedAccount().address;
    var refetchAccountSubnames = useAccountSubnames().refetchAccountSubnames;
    var getRecords = useRecords().getRecords;
    var mutate = useMutation({
        mutationFn: function() {
            var _ref = _async_to_generator$i(function(_params) {
                var challengeResponse, signature, response;
                return _ts_generator$i(this, function(_state) {
                    switch(_state.label){
                        case 0:
                            if (!address) {
                                throw new Error('Wallet not connected');
                            }
                            return [
                                4,
                                justaname.mApps.requestRevokeMAppPermissionChallenge({
                                    subname: _params.ens,
                                    address: address,
                                    mApp: params.mApp,
                                    chainId: _chainId
                                })
                            ];
                        case 1:
                            challengeResponse = _state.sent();
                            return [
                                4,
                                signMessageAsync({
                                    message: challengeResponse.challenge,
                                    account: address
                                })
                            ];
                        case 2:
                            signature = _state.sent();
                            return [
                                4,
                                justaname.mApps.revokeMAppPermission({
                                    message: challengeResponse.challenge,
                                    address: address,
                                    signature: signature
                                })
                            ];
                        case 3:
                            response = _state.sent();
                            refetchAccountSubnames();
                            getRecords({
                                ens: _params.ens,
                                chainId: _chainId
                            }, true);
                            return [
                                2,
                                response
                            ];
                    }
                });
            });
            return function(_params) {
                return _ref.apply(this, arguments);
            };
        }()
    });
    return {
        revokeMAppPermission: mutate.mutateAsync,
        isRevokeMAppPermissionPending: mutate.isPending
    };
};

function asyncGeneratorStep$h(gen, resolve, reject, _next, _throw, key, arg) {
    try {
        var info = gen[key](arg);
        var value = info.value;
    } catch (error) {
        reject(error);
        return;
    }
    if (info.done) {
        resolve(value);
    } else {
        Promise.resolve(value).then(_next, _throw);
    }
}
function _async_to_generator$h(fn) {
    return function() {
        var self = this, args = arguments;
        return new Promise(function(resolve, reject) {
            var gen = fn.apply(self, args);
            function _next(value) {
                asyncGeneratorStep$h(gen, resolve, reject, _next, _throw, "next", value);
            }
            function _throw(err) {
                asyncGeneratorStep$h(gen, resolve, reject, _next, _throw, "throw", err);
            }
            _next(undefined);
        });
    };
}
function _define_property$e(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _object_spread$e(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property$e(target, key, source[key]);
        });
    }
    return target;
}
function ownKeys$e(object, enumerableOnly) {
    var keys = Object.keys(object);
    if (Object.getOwnPropertySymbols) {
        var symbols = Object.getOwnPropertySymbols(object);
        keys.push.apply(keys, symbols);
    }
    return keys;
}
function _object_spread_props$e(target, source) {
    source = source != null ? source : {};
    if (Object.getOwnPropertyDescriptors) {
        Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
        ownKeys$e(Object(source)).forEach(function(key) {
            Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
        });
    }
    return target;
}
function _ts_generator$h(thisArg, body) {
    var f, y, t, g, _ = {
        label: 0,
        sent: function() {
            if (t[0] & 1) throw t[1];
            return t[1];
        },
        trys: [],
        ops: []
    };
    return g = {
        next: verb(0),
        "throw": verb(1),
        "return": verb(2)
    }, typeof Symbol === "function" && (g[Symbol.iterator] = function() {
        return this;
    }), g;
    function verb(n) {
        return function(v) {
            return step([
                n,
                v
            ]);
        };
    }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while(_)try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [
                op[0] & 2,
                t.value
            ];
            switch(op[0]){
                case 0:
                case 1:
                    t = op;
                    break;
                case 4:
                    _.label++;
                    return {
                        value: op[1],
                        done: false
                    };
                case 5:
                    _.label++;
                    y = op[1];
                    op = [
                        0
                    ];
                    continue;
                case 7:
                    op = _.ops.pop();
                    _.trys.pop();
                    continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) {
                        _ = 0;
                        continue;
                    }
                    if (op[0] === 3 && (!t || op[1] > t[0] && op[1] < t[3])) {
                        _.label = op[1];
                        break;
                    }
                    if (op[0] === 6 && _.label < t[1]) {
                        _.label = t[1];
                        t = op;
                        break;
                    }
                    if (t && _.label < t[2]) {
                        _.label = t[2];
                        _.ops.push(op);
                        break;
                    }
                    if (t[2]) _.ops.pop();
                    _.trys.pop();
                    continue;
            }
            op = body.call(thisArg, _);
        } catch (e) {
            op = [
                6,
                e
            ];
            y = 0;
        } finally{
            f = t = 0;
        }
        if (op[0] & 5) throw op[1];
        return {
            value: op[0] ? op[1] : void 0,
            done: true
        };
    }
}
var buildIsMAppEnabledKey = function(ens, mApp, chainId) {
    return [
        'IS_MAPP_ENABLED',
        ens,
        mApp,
        chainId
    ];
};
var useIsMAppEnabled = function(params) {
    var _params_mApp;
    var _useJustaName = useJustaName(), justaname = _useJustaName.justaname, chainId = _useJustaName.chainId;
    var _chainId = useMemo(function() {
        return params.chainId || chainId;
    }, [
        params.chainId,
        chainId
    ]);
    var records = useRecords({
        ens: params.ens,
        chainId: _chainId
    }).records;
    var query = useQuery(_object_spread_props$e(_object_spread$e({}, defaultOptions), {
        queryKey: buildIsMAppEnabledKey(params.ens, params.mApp, _chainId),
        queryFn: /*#__PURE__*/ _async_to_generator$h(function() {
            var mAppField, mAppFieldValue;
            return _ts_generator$h(this, function(_state) {
                if (!records) {
                    return [
                        2,
                        false
                    ];
                }
                if (!records.isJAN) {
                    return [
                        2,
                        false
                    ];
                }
                mAppField = records.records.texts.find(function(text) {
                    return text.key === 'mApps';
                });
                if (!mAppField) {
                    return [
                        2,
                        false
                    ];
                }
                mAppFieldValue = JSON.parse(mAppField.value);
                if (!mAppFieldValue) {
                    return [
                        2,
                        false
                    ];
                }
                return [
                    2,
                    mAppFieldValue.mApps.includes(params === null || params === void 0 ? void 0 : params.mApp)
                ];
            });
        }),
        enabled: Boolean(params.ens) && Boolean(justaname) && params.ens.length > 0 && (params === null || params === void 0 ? void 0 : (_params_mApp = params.mApp) === null || _params_mApp === void 0 ? void 0 : _params_mApp.length) > 0 && Boolean(_chainId) && Boolean(records)
    }));
    useEffect(function() {
        query.refetch();
    }, [
        records
    ]);
    return {
        isMAppEnabled: query.data,
        refetchIsMAppEnabled: query.refetch,
        isMAppEnabledPending: query.isPending
    };
};

function _define_property$d(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _object_spread$d(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property$d(target, key, source[key]);
        });
    }
    return target;
}
function ownKeys$d(object, enumerableOnly) {
    var keys = Object.keys(object);
    if (Object.getOwnPropertySymbols) {
        var symbols = Object.getOwnPropertySymbols(object);
        keys.push.apply(keys, symbols);
    }
    return keys;
}
function _object_spread_props$d(target, source) {
    source = source != null ? source : {};
    if (Object.getOwnPropertyDescriptors) {
        Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
        ownKeys$d(Object(source)).forEach(function(key) {
            Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
        });
    }
    return target;
}
var buildCanEnableMAppsKey = function(ens, chainId) {
    return [
        'CAN_ENABLE_MAPPS',
        ens,
        chainId
    ];
};
var useCanEnableMApps = function(params) {
    var chainId = useJustaName().chainId;
    var _chainId = useMemo(function() {
        return params.chainId || chainId;
    }, [
        params.chainId,
        chainId
    ]);
    var records = useRecords({
        ens: params.ens,
        chainId: _chainId
    }).records;
    var query = useQuery(_object_spread_props$d(_object_spread$d({}, defaultOptions), {
        queryKey: buildCanEnableMAppsKey(params.ens, _chainId),
        queryFn: function() {
            return records === null || records === void 0 ? void 0 : records.isJAN;
        },
        enabled: Boolean(params.ens) && Boolean(_chainId) && Boolean(records)
    }));
    return {
        canEnableMApps: query.data,
        refetchCanEnableMApps: query.refetch,
        isCanEnableMAppsPending: query.isPending
    };
};

function asyncGeneratorStep$g(gen, resolve, reject, _next, _throw, key, arg) {
    try {
        var info = gen[key](arg);
        var value = info.value;
    } catch (error) {
        reject(error);
        return;
    }
    if (info.done) {
        resolve(value);
    } else {
        Promise.resolve(value).then(_next, _throw);
    }
}
function _async_to_generator$g(fn) {
    return function() {
        var self = this, args = arguments;
        return new Promise(function(resolve, reject) {
            var gen = fn.apply(self, args);
            function _next(value) {
                asyncGeneratorStep$g(gen, resolve, reject, _next, _throw, "next", value);
            }
            function _throw(err) {
                asyncGeneratorStep$g(gen, resolve, reject, _next, _throw, "throw", err);
            }
            _next(undefined);
        });
    };
}
function _define_property$c(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _object_spread$c(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property$c(target, key, source[key]);
        });
    }
    return target;
}
function ownKeys$c(object, enumerableOnly) {
    var keys = Object.keys(object);
    if (Object.getOwnPropertySymbols) {
        var symbols = Object.getOwnPropertySymbols(object);
        keys.push.apply(keys, symbols);
    }
    return keys;
}
function _object_spread_props$c(target, source) {
    source = source != null ? source : {};
    if (Object.getOwnPropertyDescriptors) {
        Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
        ownKeys$c(Object(source)).forEach(function(key) {
            Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
        });
    }
    return target;
}
function _ts_generator$g(thisArg, body) {
    var f, y, t, g, _ = {
        label: 0,
        sent: function() {
            if (t[0] & 1) throw t[1];
            return t[1];
        },
        trys: [],
        ops: []
    };
    return g = {
        next: verb(0),
        "throw": verb(1),
        "return": verb(2)
    }, typeof Symbol === "function" && (g[Symbol.iterator] = function() {
        return this;
    }), g;
    function verb(n) {
        return function(v) {
            return step([
                n,
                v
            ]);
        };
    }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while(_)try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [
                op[0] & 2,
                t.value
            ];
            switch(op[0]){
                case 0:
                case 1:
                    t = op;
                    break;
                case 4:
                    _.label++;
                    return {
                        value: op[1],
                        done: false
                    };
                case 5:
                    _.label++;
                    y = op[1];
                    op = [
                        0
                    ];
                    continue;
                case 7:
                    op = _.ops.pop();
                    _.trys.pop();
                    continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) {
                        _ = 0;
                        continue;
                    }
                    if (op[0] === 3 && (!t || op[1] > t[0] && op[1] < t[3])) {
                        _.label = op[1];
                        break;
                    }
                    if (op[0] === 6 && _.label < t[1]) {
                        _.label = t[1];
                        t = op;
                        break;
                    }
                    if (t && _.label < t[2]) {
                        _.label = t[2];
                        _.ops.push(op);
                        break;
                    }
                    if (t[2]) _.ops.pop();
                    _.trys.pop();
                    continue;
            }
            op = body.call(thisArg, _);
        } catch (e) {
            op = [
                6,
                e
            ];
            y = 0;
        } finally{
            f = t = 0;
        }
        if (op[0] & 5) throw op[1];
        return {
            value: op[0] ? op[1] : void 0,
            done: true
        };
    }
}
var buildEnabledMAppsKey = function(ens, chainId) {
    return [
        'ENABLED_MAPPS',
        ens,
        chainId
    ];
};
var useEnabledMApps = function(params) {
    var _useJustaName = useJustaName(), justaname = _useJustaName.justaname, chainId = _useJustaName.chainId;
    var _chainId = useMemo(function() {
        return params.chainId || chainId;
    }, [
        params.chainId,
        chainId
    ]);
    var records = useRecords({
        ens: params.ens,
        chainId: _chainId
    }).records;
    var query = useQuery(_object_spread_props$c(_object_spread$c({}, defaultOptions), {
        queryKey: buildEnabledMAppsKey(params.ens, _chainId),
        queryFn: /*#__PURE__*/ _async_to_generator$g(function() {
            var mAppField;
            return _ts_generator$g(this, function(_state) {
                if (!records) {
                    return [
                        2
                    ];
                }
                if (!records.isJAN) {
                    return [
                        2,
                        false
                    ];
                }
                mAppField = records.records.texts.find(function(text) {
                    return text.key === 'mApps';
                });
                return [
                    2,
                    mAppField ? JSON.parse(mAppField.value).mApps : []
                ];
            });
        }),
        enabled: Boolean(params.ens) && Boolean(justaname) && Boolean(params.ens.length > 0) && Boolean(_chainId) && Boolean(records)
    }));
    useEffect(function() {
        if (records) query.refetch();
    }, [
        records
    ]);
    return {
        enabledMApps: query.data,
        refetchEnabledMApps: query.refetch,
        isMAppEnabledPending: query.isPending
    };
};

function asyncGeneratorStep$f(gen, resolve, reject, _next, _throw, key, arg) {
    try {
        var info = gen[key](arg);
        var value = info.value;
    } catch (error) {
        reject(error);
        return;
    }
    if (info.done) {
        resolve(value);
    } else {
        Promise.resolve(value).then(_next, _throw);
    }
}
function _async_to_generator$f(fn) {
    return function() {
        var self = this, args = arguments;
        return new Promise(function(resolve, reject) {
            var gen = fn.apply(self, args);
            function _next(value) {
                asyncGeneratorStep$f(gen, resolve, reject, _next, _throw, "next", value);
            }
            function _throw(err) {
                asyncGeneratorStep$f(gen, resolve, reject, _next, _throw, "throw", err);
            }
            _next(undefined);
        });
    };
}
function _ts_generator$f(thisArg, body) {
    var f, y, t, g, _ = {
        label: 0,
        sent: function() {
            if (t[0] & 1) throw t[1];
            return t[1];
        },
        trys: [],
        ops: []
    };
    return g = {
        next: verb(0),
        "throw": verb(1),
        "return": verb(2)
    }, typeof Symbol === "function" && (g[Symbol.iterator] = function() {
        return this;
    }), g;
    function verb(n) {
        return function(v) {
            return step([
                n,
                v
            ]);
        };
    }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while(_)try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [
                op[0] & 2,
                t.value
            ];
            switch(op[0]){
                case 0:
                case 1:
                    t = op;
                    break;
                case 4:
                    _.label++;
                    return {
                        value: op[1],
                        done: false
                    };
                case 5:
                    _.label++;
                    y = op[1];
                    op = [
                        0
                    ];
                    continue;
                case 7:
                    op = _.ops.pop();
                    _.trys.pop();
                    continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) {
                        _ = 0;
                        continue;
                    }
                    if (op[0] === 3 && (!t || op[1] > t[0] && op[1] < t[3])) {
                        _.label = op[1];
                        break;
                    }
                    if (op[0] === 6 && _.label < t[1]) {
                        _.label = t[1];
                        t = op;
                        break;
                    }
                    if (t && _.label < t[2]) {
                        _.label = t[2];
                        _.ops.push(op);
                        break;
                    }
                    if (t[2]) _.ops.pop();
                    _.trys.pop();
                    continue;
            }
            op = body.call(thisArg, _);
        } catch (e) {
            op = [
                6,
                e
            ];
            y = 0;
        } finally{
            f = t = 0;
        }
        if (op[0] & 5) throw op[1];
        return {
            value: op[0] ? op[1] : void 0,
            done: true
        };
    }
}
var query = function(ens, type, chainId) {
    return qs.stringify({
        ens: ens,
        type: type.toLowerCase(),
        chainId: chainId
    });
};
var useUploadMedia = function(params) {
    var _useJustaName = useJustaName(), dev = _useJustaName.dev, chainId = _useJustaName.chainId;
    var getSignature = useSubnameSignature().getSignature;
    var mutation = useMutation({
        mutationFn: function() {
            var _ref = _async_to_generator$f(function(_params) {
                var _ens, _type, _chainId, _ref, signature, message, address, baseUrl, result, url;
                return _ts_generator$f(this, function(_state) {
                    switch(_state.label){
                        case 0:
                            _ens = _params.ens || (params === null || params === void 0 ? void 0 : params.ens);
                            _type = _params.type || (params === null || params === void 0 ? void 0 : params.type);
                            _chainId = _params.chainId || (params === null || params === void 0 ? void 0 : params.chainId) || chainId;
                            if (!_ens) {
                                throw new Error('Subname is required');
                            }
                            if (!_params.form) {
                                throw new Error('Form is required');
                            }
                            if (!_type) {
                                throw new Error('Type is required');
                            }
                            if (!_chainId) {
                                throw new Error('ChainId is required');
                            }
                            return [
                                4,
                                getSignature()
                            ];
                        case 1:
                            _ref = _state.sent(), signature = _ref.signature, message = _ref.message, address = _ref.address;
                            _params.form.append('signature', signature);
                            // const baseUrl = 'http://localhost:3000';
                            baseUrl = dev ? 'https://api-staging.justaname.id' : 'https://api.justaname.id';
                            return [
                                4,
                                axios.post("".concat(baseUrl, "/ens/v1/subname/upload-to-cdn") + '?' + query(_ens, _type, _chainId), _params.form, {
                                    headers: {
                                        'x-message': message.replace(/\n/g, '\\n'),
                                        'x-address': address
                                    }
                                })
                            ];
                        case 2:
                            result = _state.sent();
                            url = '';
                            if (result.data.statusCode === 200) {
                                url = result.data.result.data.url;
                            }
                            return [
                                2,
                                {
                                    url: url
                                }
                            ];
                    }
                });
            });
            return function(_params) {
                return _ref.apply(this, arguments);
            };
        }()
    });
    return {
        uploadMedia: mutation.mutateAsync,
        isUploadPending: mutation.isPending
    };
};

// inspired by spruceid siwe: https://github.com/spruceid/siwe/blob/main/packages/siwe/lib/ethersCompat.ts
var ethersCompat = ethers;
var namehash = 'namehash' in ethersCompat ? ethersCompat.namehash : ethersCompat.utils.namehash;
var getAddress = 'getAddress' in ethersCompat ? ethersCompat.getAddress : ethersCompat.utils.getAddress;

function asyncGeneratorStep$e(gen, resolve, reject, _next, _throw, key, arg) {
    try {
        var info = gen[key](arg);
        var value = info.value;
    } catch (error) {
        reject(error);
        return;
    }
    if (info.done) {
        resolve(value);
    } else {
        Promise.resolve(value).then(_next, _throw);
    }
}
function _async_to_generator$e(fn) {
    return function() {
        var self = this, args = arguments;
        return new Promise(function(resolve, reject) {
            var gen = fn.apply(self, args);
            function _next(value) {
                asyncGeneratorStep$e(gen, resolve, reject, _next, _throw, "next", value);
            }
            function _throw(err) {
                asyncGeneratorStep$e(gen, resolve, reject, _next, _throw, "throw", err);
            }
            _next(undefined);
        });
    };
}
function _ts_generator$e(thisArg, body) {
    var f, y, t, g, _ = {
        label: 0,
        sent: function() {
            if (t[0] & 1) throw t[1];
            return t[1];
        },
        trys: [],
        ops: []
    };
    return g = {
        next: verb(0),
        "throw": verb(1),
        "return": verb(2)
    }, typeof Symbol === "function" && (g[Symbol.iterator] = function() {
        return this;
    }), g;
    function verb(n) {
        return function(v) {
            return step([
                n,
                v
            ]);
        };
    }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while(_)try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [
                op[0] & 2,
                t.value
            ];
            switch(op[0]){
                case 0:
                case 1:
                    t = op;
                    break;
                case 4:
                    _.label++;
                    return {
                        value: op[1],
                        done: false
                    };
                case 5:
                    _.label++;
                    y = op[1];
                    op = [
                        0
                    ];
                    continue;
                case 7:
                    op = _.ops.pop();
                    _.trys.pop();
                    continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) {
                        _ = 0;
                        continue;
                    }
                    if (op[0] === 3 && (!t || op[1] > t[0] && op[1] < t[3])) {
                        _.label = op[1];
                        break;
                    }
                    if (op[0] === 6 && _.label < t[1]) {
                        _.label = t[1];
                        t = op;
                        break;
                    }
                    if (t && _.label < t[2]) {
                        _.label = t[2];
                        _.ops.push(op);
                        break;
                    }
                    if (t[2]) _.ops.pop();
                    _.trys.pop();
                    continue;
            }
            op = body.call(thisArg, _);
        } catch (e) {
            op = [
                6,
                e
            ];
            y = 0;
        } finally{
            f = t = 0;
        }
        if (op[0] & 5) throw op[1];
        return {
            value: op[0] ? op[1] : void 0,
            done: true
        };
    }
}
var ZeroAddress = '0x0000000000000000000000000000000000000000';
var REGISTRY_ADDRESS = '0x00000000000C2E074eC69A0dFb2997BA6C7d2e1e';
var SEPOLIA_REGISTRAR_ADDRESS = '0xA0a1AbcDAe1a2a4A2EF8e9113Ff0e02DD81DC0C6';
var MAINNET_REGISTRAR_ADDRESS = '0xa58E81fe9b61B5c3fE2AFD33CF304c454AbFc7Cb';
var setResolverABI = [
    {
        constant: false,
        inputs: [
            {
                name: 'node',
                type: 'bytes32'
            },
            {
                name: 'resolver',
                type: 'address'
            }
        ],
        name: 'setResolver',
        outputs: [],
        type: 'function'
    }
];
var setClaimWithResolverABI = [
    {
        constant: false,
        inputs: [
            {
                name: 'owner',
                type: 'address'
            },
            {
                name: 'resolver',
                type: 'address'
            }
        ],
        name: 'claimWithResolver',
        outputs: [],
        type: 'function'
    }
];
var getResolverABI = [
    {
        constant: true,
        inputs: [
            {
                name: 'node',
                type: 'bytes32'
            }
        ],
        name: 'resolver',
        outputs: [
            {
                name: '',
                type: 'address'
            }
        ],
        type: 'function'
    }
];
var recordExistsABI = [
    {
        constant: true,
        inputs: [
            {
                name: 'node',
                type: 'bytes32'
            }
        ],
        name: 'recordExists',
        outputs: [
            {
                name: '',
                type: 'bool'
            }
        ],
        type: 'function'
    }
];
/**
 * Custom hook for performing a mutation to set the JustaName resolver.
 *
 * @template T - The type of additional parameters that can be passed to the set JustaName resolver mutation, extending the base request.
 * @returns {UseSetNameHashJustaNameResolver} An object containing the `setNameHashJustaNameResolver` async function to initiate the JustaName resolve,a boolean `NameHashJustaNameResolverSet` indicating if the resolver is set,a boolean `setNameHashJustaNameResolverPending` indicating the state of the process, and a boolean `setNameHashJustaNameResolverError` indicating if an error has occured.
 */ var useSetNameHashJustaNameResolver = function() {
    var _useMountedAccount = useMountedAccount(), chainId = _useMountedAccount.chainId, address = _useMountedAccount.address;
    var _useOffchainResolvers = useOffchainResolvers(), offchainResolvers = _useOffchainResolvers.offchainResolvers, isOffchainResolversPending = _useOffchainResolvers.isOffchainResolversPending;
    var currentResolver = useMemo(function() {
        var _offchainResolvers_offchainResolvers_find;
        if (!chainId || !offchainResolvers || isOffchainResolversPending) return;
        return offchainResolvers === null || offchainResolvers === void 0 ? void 0 : (_offchainResolvers_offchainResolvers_find = offchainResolvers.offchainResolvers.find(function(resolver) {
            return resolver.chainId === chainId;
        })) === null || _offchainResolvers_offchainResolvers_find === void 0 ? void 0 : _offchainResolvers_offchainResolvers_find.resolverAddress;
    }, [
        offchainResolvers,
        chainId,
        isOffchainResolversPending
    ]);
    var _useReadContract = useReadContract({
        address: REGISTRY_ADDRESS,
        abi: recordExistsABI,
        functionName: 'recordExists',
        args: [
            namehash(getAddress(address !== null && address !== void 0 ? address : ZeroAddress).substring(2) + '.addr.reverse')
        ],
        chainId: chainId,
        query: {
            enabled: address && address !== ZeroAddress
        }
    }), recordExistsConfig = _useReadContract.data, recordExistsStatus = _useReadContract.status, recordExistsRefetch = _useReadContract.refetch;
    var _useSimulateContract = useSimulateContract({
        address: chainId === 1 ? MAINNET_REGISTRAR_ADDRESS : SEPOLIA_REGISTRAR_ADDRESS,
        abi: setClaimWithResolverABI,
        functionName: 'claimWithResolver',
        args: [
            getAddress(address !== null && address !== void 0 ? address : ZeroAddress),
            currentResolver
        ],
        chainId: chainId,
        query: {
            enabled: recordExistsStatus === 'success' && recordExistsConfig === false && address && address !== ZeroAddress
        }
    }), setClaimWithResolverConfig = _useSimulateContract.data;
    var _useReadContract1 = useReadContract({
        address: REGISTRY_ADDRESS,
        abi: getResolverABI,
        functionName: 'resolver',
        args: [
            namehash(getAddress(address !== null && address !== void 0 ? address : ZeroAddress).substring(2) + '.addr.reverse')
        ],
        chainId: chainId,
        query: {
            enabled: recordExistsStatus === 'success' && recordExistsConfig === true && address && address !== ZeroAddress
        }
    }), getResolverABIConfig = _useReadContract1.data, getResolverABIStatus = _useReadContract1.status, getResolverStatusRefetch = _useReadContract1.refetch;
    var _useSimulateContract1 = useSimulateContract({
        address: REGISTRY_ADDRESS,
        abi: setResolverABI,
        functionName: 'setResolver',
        args: [
            namehash(getAddress(address !== null && address !== void 0 ? address : ZeroAddress).substring(2) + '.addr.reverse'),
            currentResolver
        ],
        chainId: chainId,
        query: {
            enabled: getResolverABIStatus === 'success' && getResolverABIConfig.toLowerCase() !== (currentResolver === null || currentResolver === void 0 ? void 0 : currentResolver.toLowerCase()) && address && address !== ZeroAddress
        }
    }), setResolverConfig = _useSimulateContract1.data;
    var _useWriteContract = useWriteContract(), setClaimWithResolverWrite = _useWriteContract.writeContractAsync, setClaimWithResolverData = _useWriteContract.data, setClaimWithResolverReset = _useWriteContract.reset;
    var _useWriteContract1 = useWriteContract(), setResolverConfigWrite = _useWriteContract1.writeContractAsync, setResolverConfigData = _useWriteContract1.data, setResolverConfigReset = _useWriteContract1.reset;
    var _useWaitForTransactionReceipt = useWaitForTransactionReceipt({
        hash: setResolverConfigData
    }), setResolverConfigDataIsLoading = _useWaitForTransactionReceipt.isLoading, setResolverConfigDataIsSuccess = _useWaitForTransactionReceipt.isSuccess, setResolverConfigDataIsError = _useWaitForTransactionReceipt.isError;
    var _useWaitForTransactionReceipt1 = useWaitForTransactionReceipt({
        hash: setClaimWithResolverData
    }), setClaimWithResolverIsLoading = _useWaitForTransactionReceipt1.isLoading, setClaimWithResolverIsSuccess = _useWaitForTransactionReceipt1.isSuccess, setClaimWithResolverIsError = _useWaitForTransactionReceipt1.isError;
    var isResolverConfigured = useMemo(function() {
        if (!getResolverABIConfig) return false;
        return getResolverABIConfig.toLowerCase() === (currentResolver === null || currentResolver === void 0 ? void 0 : currentResolver.toLowerCase());
    }, [
        getResolverABIConfig,
        currentResolver
    ]);
    useEffect(function() {
        if (!setResolverConfigData && !setClaimWithResolverData) return;
        if (setResolverConfigData) {
            if (setResolverConfigDataIsSuccess) {
                recordExistsRefetch();
                getResolverStatusRefetch();
            }
            if (setResolverConfigDataIsSuccess || setResolverConfigDataIsError) {
                setResolverConfigReset();
            }
        }
        if (setClaimWithResolverData) {
            if (setClaimWithResolverIsSuccess) {
                recordExistsRefetch();
                getResolverStatusRefetch();
            }
            if (setClaimWithResolverIsSuccess || setClaimWithResolverIsError) {
                setClaimWithResolverReset();
            }
        }
    }, [
        setResolverConfigDataIsSuccess,
        setResolverConfigDataIsError,
        setResolverConfigData,
        setResolverConfigReset,
        setClaimWithResolverIsSuccess,
        setClaimWithResolverIsError,
        setClaimWithResolverData,
        setClaimWithResolverReset,
        getResolverStatusRefetch,
        recordExistsRefetch
    ]);
    var noResultData = !setResolverConfigData && !setClaimWithResolverData;
    var setResolverConfigPending = !!setResolverConfigData && setResolverConfigDataIsLoading;
    var setClaimWithResolverPending = !!setClaimWithResolverData && setClaimWithResolverIsLoading;
    var mutate = useMutation({
        mutationFn: /*#__PURE__*/ _async_to_generator$e(function() {
            return _ts_generator$e(this, function(_state) {
                switch(_state.label){
                    case 0:
                        if (!(recordExistsStatus === 'success' && recordExistsConfig === false)) return [
                            3,
                            2
                        ];
                        return [
                            4,
                            setClaimWithResolverWrite(setClaimWithResolverConfig.request)
                        ];
                    case 1:
                        _state.sent();
                        return [
                            3,
                            4
                        ];
                    case 2:
                        if (!(getResolverABIStatus === 'success')) return [
                            3,
                            4
                        ];
                        if (!!isResolverConfigured) return [
                            3,
                            4
                        ];
                        return [
                            4,
                            setResolverConfigWrite(setResolverConfig.request)
                        ];
                    case 3:
                        _state.sent();
                        _state.label = 4;
                    case 4:
                        return [
                            2
                        ];
                }
            });
        })
    });
    return {
        setNameHashJustaNameResolver: mutate.mutateAsync,
        NameHashJustaNameResolverSet: isResolverConfigured && getResolverABIStatus !== 'pending',
        isSetNameHashJustaNameResolverPending: !noResultData && (setResolverConfigPending || setClaimWithResolverPending),
        setNameHashJustaNameResolverError: !noResultData && (setResolverConfigDataIsError || setClaimWithResolverIsError)
    };
};

var isParseable = function(value) {
    try {
        JSON.parse(value);
        return true;
    } catch (e) {
        return false;
    }
};

function asyncGeneratorStep$d(gen, resolve, reject, _next, _throw, key, arg) {
    try {
        var info = gen[key](arg);
        var value = info.value;
    } catch (error) {
        reject(error);
        return;
    }
    if (info.done) {
        resolve(value);
    } else {
        Promise.resolve(value).then(_next, _throw);
    }
}
function _async_to_generator$d(fn) {
    return function() {
        var self = this, args = arguments;
        return new Promise(function(resolve, reject) {
            var gen = fn.apply(self, args);
            function _next(value) {
                asyncGeneratorStep$d(gen, resolve, reject, _next, _throw, "next", value);
            }
            function _throw(err) {
                asyncGeneratorStep$d(gen, resolve, reject, _next, _throw, "throw", err);
            }
            _next(undefined);
        });
    };
}
function _define_property$b(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _object_spread$b(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property$b(target, key, source[key]);
        });
    }
    return target;
}
function ownKeys$b(object, enumerableOnly) {
    var keys = Object.keys(object);
    if (Object.getOwnPropertySymbols) {
        var symbols = Object.getOwnPropertySymbols(object);
        keys.push.apply(keys, symbols);
    }
    return keys;
}
function _object_spread_props$b(target, source) {
    source = source != null ? source : {};
    if (Object.getOwnPropertyDescriptors) {
        Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
        ownKeys$b(Object(source)).forEach(function(key) {
            Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
        });
    }
    return target;
}
function _ts_generator$d(thisArg, body) {
    var f, y, t, g, _ = {
        label: 0,
        sent: function() {
            if (t[0] & 1) throw t[1];
            return t[1];
        },
        trys: [],
        ops: []
    };
    return g = {
        next: verb(0),
        "throw": verb(1),
        "return": verb(2)
    }, typeof Symbol === "function" && (g[Symbol.iterator] = function() {
        return this;
    }), g;
    function verb(n) {
        return function(v) {
            return step([
                n,
                v
            ]);
        };
    }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while(_)try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [
                op[0] & 2,
                t.value
            ];
            switch(op[0]){
                case 0:
                case 1:
                    t = op;
                    break;
                case 4:
                    _.label++;
                    return {
                        value: op[1],
                        done: false
                    };
                case 5:
                    _.label++;
                    y = op[1];
                    op = [
                        0
                    ];
                    continue;
                case 7:
                    op = _.ops.pop();
                    _.trys.pop();
                    continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) {
                        _ = 0;
                        continue;
                    }
                    if (op[0] === 3 && (!t || op[1] > t[0] && op[1] < t[3])) {
                        _.label = op[1];
                        break;
                    }
                    if (op[0] === 6 && _.label < t[1]) {
                        _.label = t[1];
                        t = op;
                        break;
                    }
                    if (t && _.label < t[2]) {
                        _.label = t[2];
                        _.ops.push(op);
                        break;
                    }
                    if (t[2]) _.ops.pop();
                    _.trys.pop();
                    continue;
            }
            op = body.call(thisArg, _);
        } catch (e) {
            op = [
                6,
                e
            ];
            y = 0;
        } finally{
            f = t = 0;
        }
        if (op[0] & 5) throw op[1];
        return {
            value: op[0] ? op[1] : void 0,
            done: true
        };
    }
}
var buildEnsAuthKey = function(backendUrl) {
    return [
        'ENS_AUTH',
        backendUrl
    ];
};
var useEnsAuth = function(params) {
    var _useJustaName = useJustaName(), backendUrl = _useJustaName.backendUrl, routes = _useJustaName.routes;
    var _enabled = (params === null || params === void 0 ? void 0 : params.enabled) !== undefined ? params.enabled : true;
    var _backendUrl = useMemo(function() {
        return (params === null || params === void 0 ? void 0 : params.backendUrl) || backendUrl || '';
    }, [
        backendUrl,
        params === null || params === void 0 ? void 0 : params.backendUrl
    ]);
    var _currentEnsRoute = useMemo(function() {
        return (params === null || params === void 0 ? void 0 : params.currentEnsRoute) || routes.currentEnsRoute;
    }, [
        routes.currentEnsRoute,
        params === null || params === void 0 ? void 0 : params.currentEnsRoute
    ]);
    var currentEnsEndpoint = useMemo(function() {
        return _backendUrl + _currentEnsRoute;
    }, [
        _backendUrl,
        _currentEnsRoute
    ]);
    var query = useQuery(_object_spread_props$b(_object_spread$b({}, defaultOptions), {
        retry: 0,
        queryKey: buildEnsAuthKey(_backendUrl),
        queryFn: /*#__PURE__*/ _async_to_generator$d(function() {
            var response, response1;
            return _ts_generator$d(this, function(_state) {
                switch(_state.label){
                    case 0:
                        _state.trys.push([
                            0,
                            2,
                            ,
                            3
                        ]);
                        if (params === null || params === void 0 ? void 0 : params.local) {
                            response = localStorage.getItem('ENS_AUTH') || '';
                            if (isParseable(response)) {
                                return [
                                    2,
                                    JSON.parse(response)
                                ];
                            } else {
                                localStorage.removeItem('ENS_AUTH');
                            }
                            return [
                                2,
                                null
                            ];
                        }
                        return [
                            4,
                            axios.get(currentEnsEndpoint, {
                                headers: {
                                    'Content-Type': 'application/json'
                                },
                                withCredentials: true
                            })
                        ];
                    case 1:
                        response1 = _state.sent();
                        return [
                            2,
                            response1.data
                        ];
                    case 2:
                        _state.sent();
                        return [
                            2,
                            null
                        ];
                    case 3:
                        return [
                            2
                        ];
                }
            });
        }),
        enabled: Boolean(_enabled)
    }));
    return {
        isLoggedIn: !!query.data,
        connectedEns: query.data,
        isEnsAuthPending: query.isPending,
        isEnsAuthFetching: query.isFetching,
        isEnsAuthLoading: query.isPending || query.isFetching,
        refreshEnsAuth: query.refetch
    };
};

function asyncGeneratorStep$c(gen, resolve, reject, _next, _throw, key, arg) {
    try {
        var info = gen[key](arg);
        var value = info.value;
    } catch (error) {
        reject(error);
        return;
    }
    if (info.done) {
        resolve(value);
    } else {
        Promise.resolve(value).then(_next, _throw);
    }
}
function _async_to_generator$c(fn) {
    return function() {
        var self = this, args = arguments;
        return new Promise(function(resolve, reject) {
            var gen = fn.apply(self, args);
            function _next(value) {
                asyncGeneratorStep$c(gen, resolve, reject, _next, _throw, "next", value);
            }
            function _throw(err) {
                asyncGeneratorStep$c(gen, resolve, reject, _next, _throw, "throw", err);
            }
            _next(undefined);
        });
    };
}
function _define_property$a(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _object_spread$a(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property$a(target, key, source[key]);
        });
    }
    return target;
}
function ownKeys$a(object, enumerableOnly) {
    var keys = Object.keys(object);
    if (Object.getOwnPropertySymbols) {
        var symbols = Object.getOwnPropertySymbols(object);
        keys.push.apply(keys, symbols);
    }
    return keys;
}
function _object_spread_props$a(target, source) {
    source = source != null ? source : {};
    if (Object.getOwnPropertyDescriptors) {
        Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
        ownKeys$a(Object(source)).forEach(function(key) {
            Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
        });
    }
    return target;
}
function _ts_generator$c(thisArg, body) {
    var f, y, t, g, _ = {
        label: 0,
        sent: function() {
            if (t[0] & 1) throw t[1];
            return t[1];
        },
        trys: [],
        ops: []
    };
    return g = {
        next: verb(0),
        "throw": verb(1),
        "return": verb(2)
    }, typeof Symbol === "function" && (g[Symbol.iterator] = function() {
        return this;
    }), g;
    function verb(n) {
        return function(v) {
            return step([
                n,
                v
            ]);
        };
    }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while(_)try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [
                op[0] & 2,
                t.value
            ];
            switch(op[0]){
                case 0:
                case 1:
                    t = op;
                    break;
                case 4:
                    _.label++;
                    return {
                        value: op[1],
                        done: false
                    };
                case 5:
                    _.label++;
                    y = op[1];
                    op = [
                        0
                    ];
                    continue;
                case 7:
                    op = _.ops.pop();
                    _.trys.pop();
                    continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) {
                        _ = 0;
                        continue;
                    }
                    if (op[0] === 3 && (!t || op[1] > t[0] && op[1] < t[3])) {
                        _.label = op[1];
                        break;
                    }
                    if (op[0] === 6 && _.label < t[1]) {
                        _.label = t[1];
                        t = op;
                        break;
                    }
                    if (t && _.label < t[2]) {
                        _.label = t[2];
                        _.ops.push(op);
                        break;
                    }
                    if (t[2]) _.ops.pop();
                    _.trys.pop();
                    continue;
            }
            op = body.call(thisArg, _);
        } catch (e) {
            op = [
                6,
                e
            ];
            y = 0;
        } finally{
            f = t = 0;
        }
        if (op[0] & 5) throw op[1];
        return {
            value: op[0] ? op[1] : void 0,
            done: true
        };
    }
}
var buildNonceKey = function(backendUrl, route, address) {
    return [
        'ENS_NONCE',
        backendUrl,
        route,
        address
    ];
};
var useEnsNonce = function(params) {
    var _useJustaName = useJustaName(), backendUrl = _useJustaName.backendUrl, routes = _useJustaName.routes;
    var address = useMountedAccount().address;
    var _enabled = (params === null || params === void 0 ? void 0 : params.enabled) !== undefined ? params.enabled : true;
    var _backendUrl = useMemo(function() {
        return (params === null || params === void 0 ? void 0 : params.backendUrl) || backendUrl || '';
    }, [
        backendUrl,
        params === null || params === void 0 ? void 0 : params.backendUrl
    ]);
    var _signinNonceRoute = useMemo(function() {
        return (params === null || params === void 0 ? void 0 : params.signinNonceRoute) || routes.signinNonceRoute;
    }, [
        routes.signinNonceRoute,
        params === null || params === void 0 ? void 0 : params.signinNonceRoute
    ]);
    var nonceEndpoint = useMemo(function() {
        return _backendUrl + _signinNonceRoute;
    }, [
        _backendUrl,
        _signinNonceRoute
    ]);
    var query = useQuery(_object_spread_props$a(_object_spread$a({}, defaultOptions), {
        retry: 0,
        staleTime: Infinity,
        queryKey: buildNonceKey(_backendUrl, _signinNonceRoute, address || ''),
        queryFn: /*#__PURE__*/ _async_to_generator$c(function() {
            var nonceResponse;
            return _ts_generator$c(this, function(_state) {
                switch(_state.label){
                    case 0:
                        return [
                            4,
                            fetch(nonceEndpoint, {
                                credentials: 'include'
                            })
                        ];
                    case 1:
                        nonceResponse = _state.sent();
                        if (!nonceResponse.ok) {
                            throw new Error('Failed to fetch nonce');
                        }
                        return [
                            2,
                            nonceResponse.text()
                        ];
                }
            });
        }),
        enabled: Boolean(address) && Boolean(_enabled)
    }));
    return {
        nonce: query.data,
        refetchNonce: query.refetch,
        isNoncePending: query.isFetching,
        isNonceLoading: query.isLoading,
        isNonceFetching: query.isFetching
    };
};

function asyncGeneratorStep$b(gen, resolve, reject, _next, _throw, key, arg) {
    try {
        var info = gen[key](arg);
        var value = info.value;
    } catch (error) {
        reject(error);
        return;
    }
    if (info.done) {
        resolve(value);
    } else {
        Promise.resolve(value).then(_next, _throw);
    }
}
function _async_to_generator$b(fn) {
    return function() {
        var self = this, args = arguments;
        return new Promise(function(resolve, reject) {
            var gen = fn.apply(self, args);
            function _next(value) {
                asyncGeneratorStep$b(gen, resolve, reject, _next, _throw, "next", value);
            }
            function _throw(err) {
                asyncGeneratorStep$b(gen, resolve, reject, _next, _throw, "throw", err);
            }
            _next(undefined);
        });
    };
}
function _ts_generator$b(thisArg, body) {
    var f, y, t, g, _ = {
        label: 0,
        sent: function() {
            if (t[0] & 1) throw t[1];
            return t[1];
        },
        trys: [],
        ops: []
    };
    return g = {
        next: verb(0),
        "throw": verb(1),
        "return": verb(2)
    }, typeof Symbol === "function" && (g[Symbol.iterator] = function() {
        return this;
    }), g;
    function verb(n) {
        return function(v) {
            return step([
                n,
                v
            ]);
        };
    }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while(_)try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [
                op[0] & 2,
                t.value
            ];
            switch(op[0]){
                case 0:
                case 1:
                    t = op;
                    break;
                case 4:
                    _.label++;
                    return {
                        value: op[1],
                        done: false
                    };
                case 5:
                    _.label++;
                    y = op[1];
                    op = [
                        0
                    ];
                    continue;
                case 7:
                    op = _.ops.pop();
                    _.trys.pop();
                    continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) {
                        _ = 0;
                        continue;
                    }
                    if (op[0] === 3 && (!t || op[1] > t[0] && op[1] < t[3])) {
                        _.label = op[1];
                        break;
                    }
                    if (op[0] === 6 && _.label < t[1]) {
                        _.label = t[1];
                        t = op;
                        break;
                    }
                    if (t && _.label < t[2]) {
                        _.label = t[2];
                        _.ops.push(op);
                        break;
                    }
                    if (t[2]) _.ops.pop();
                    _.trys.pop();
                    continue;
            }
            op = body.call(thisArg, _);
        } catch (e) {
            op = [
                6,
                e
            ];
            y = 0;
        } finally{
            f = t = 0;
        }
        if (op[0] & 5) throw op[1];
        return {
            value: op[0] ? op[1] : void 0,
            done: true
        };
    }
}
var useEnsSignIn = function(params) {
    var _useJustaName = useJustaName(), justaname = _useJustaName.justaname, backendUrl = _useJustaName.backendUrl, config = _useJustaName.config, routes = _useJustaName.routes, chainId = _useJustaName.chainId;
    var address = useMountedAccount().address;
    var signMessageAsync = useSignMessage().signMessageAsync;
    var _backendUrl = useMemo(function() {
        return (params === null || params === void 0 ? void 0 : params.backendUrl) || backendUrl || '';
    }, [
        backendUrl,
        params === null || params === void 0 ? void 0 : params.backendUrl
    ]);
    var _signinRoute = useMemo(function() {
        return (params === null || params === void 0 ? void 0 : params.signinRoute) || routes.signinRoute;
    }, [
        routes.signinRoute,
        params === null || params === void 0 ? void 0 : params.signinRoute
    ]);
    var signinEndpoint = useMemo(function() {
        return _backendUrl + _signinRoute;
    }, [
        _backendUrl,
        _signinRoute
    ]);
    var _currentEnsRoute = useMemo(function() {
        return (params === null || params === void 0 ? void 0 : params.currentEnsRoute) || routes.currentEnsRoute;
    }, [
        routes.currentEnsRoute,
        params === null || params === void 0 ? void 0 : params.currentEnsRoute
    ]);
    var _signinNonceRoute = useMemo(function() {
        return (params === null || params === void 0 ? void 0 : params.signinNonceRoute) || routes.signinNonceRoute;
    }, [
        routes.signinNonceRoute,
        params === null || params === void 0 ? void 0 : params.signinNonceRoute
    ]);
    var refreshEnsAuth = useEnsAuth({
        backendUrl: _backendUrl,
        currentEnsRoute: _currentEnsRoute,
        local: params === null || params === void 0 ? void 0 : params.local
    }).refreshEnsAuth;
    var _useEnsNonce = useEnsNonce({
        backendUrl: _backendUrl,
        signinNonceRoute: _signinNonceRoute,
        enabled: !(params === null || params === void 0 ? void 0 : params.local)
    }), nonce = _useEnsNonce.nonce, refetchNonce = _useEnsNonce.refetchNonce;
    var mutation = useMutation({
        mutationFn: function() {
            var _ref = _async_to_generator$b(function(_params) {
                var message, signature, response, e;
                return _ts_generator$b(this, function(_state) {
                    switch(_state.label){
                        case 0:
                            _state.trys.push([
                                0,
                                3,
                                ,
                                5
                            ]);
                            if (!address) {
                                throw new Error('No address found');
                            }
                            if (params === null || params === void 0 ? void 0 : params.local) {
                                localStorage.setItem('ENS_AUTH', JSON.stringify({
                                    ens: _params.ens,
                                    chainId: chainId,
                                    address: address
                                }));
                                refreshEnsAuth();
                                return [
                                    2,
                                    'success'
                                ];
                            }
                            if (!nonce) {
                                throw new Error('No nonce found');
                            }
                            message = justaname.signIn.requestSignIn({
                                ens: _params.ens,
                                ttl: config === null || config === void 0 ? void 0 : config.signInTtl,
                                uri: config === null || config === void 0 ? void 0 : config.origin,
                                domain: config === null || config === void 0 ? void 0 : config.domain,
                                chainId: chainId,
                                address: address,
                                nonce: nonce
                            });
                            return [
                                4,
                                signMessageAsync({
                                    message: message,
                                    account: address
                                })
                            ];
                        case 1:
                            signature = _state.sent();
                            return [
                                4,
                                fetch(signinEndpoint, {
                                    method: 'POST',
                                    headers: {
                                        'Content-Type': 'application/json'
                                    },
                                    body: JSON.stringify({
                                        signature: signature,
                                        message: message
                                    }),
                                    credentials: 'include'
                                })
                            ];
                        case 2:
                            response = _state.sent();
                            if (response.status !== 200) {
                                throw new Error('Failed to sign in');
                            }
                            refreshEnsAuth();
                            return [
                                2,
                                response.text()
                            ];
                        case 3:
                            e = _state.sent();
                            return [
                                4,
                                refetchNonce()
                            ];
                        case 4:
                            _state.sent();
                            throw e;
                        case 5:
                            return [
                                2
                            ];
                    }
                });
            });
            return function(_params) {
                return _ref.apply(this, arguments);
            };
        }()
    });
    return {
        signIn: mutation.mutateAsync,
        isSignInPending: mutation.isPending
    };
};

function asyncGeneratorStep$a(gen, resolve, reject, _next, _throw, key, arg) {
    try {
        var info = gen[key](arg);
        var value = info.value;
    } catch (error) {
        reject(error);
        return;
    }
    if (info.done) {
        resolve(value);
    } else {
        Promise.resolve(value).then(_next, _throw);
    }
}
function _async_to_generator$a(fn) {
    return function() {
        var self = this, args = arguments;
        return new Promise(function(resolve, reject) {
            var gen = fn.apply(self, args);
            function _next(value) {
                asyncGeneratorStep$a(gen, resolve, reject, _next, _throw, "next", value);
            }
            function _throw(err) {
                asyncGeneratorStep$a(gen, resolve, reject, _next, _throw, "throw", err);
            }
            _next(undefined);
        });
    };
}
function _ts_generator$a(thisArg, body) {
    var f, y, t, g, _ = {
        label: 0,
        sent: function() {
            if (t[0] & 1) throw t[1];
            return t[1];
        },
        trys: [],
        ops: []
    };
    return g = {
        next: verb(0),
        "throw": verb(1),
        "return": verb(2)
    }, typeof Symbol === "function" && (g[Symbol.iterator] = function() {
        return this;
    }), g;
    function verb(n) {
        return function(v) {
            return step([
                n,
                v
            ]);
        };
    }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while(_)try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [
                op[0] & 2,
                t.value
            ];
            switch(op[0]){
                case 0:
                case 1:
                    t = op;
                    break;
                case 4:
                    _.label++;
                    return {
                        value: op[1],
                        done: false
                    };
                case 5:
                    _.label++;
                    y = op[1];
                    op = [
                        0
                    ];
                    continue;
                case 7:
                    op = _.ops.pop();
                    _.trys.pop();
                    continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) {
                        _ = 0;
                        continue;
                    }
                    if (op[0] === 3 && (!t || op[1] > t[0] && op[1] < t[3])) {
                        _.label = op[1];
                        break;
                    }
                    if (op[0] === 6 && _.label < t[1]) {
                        _.label = t[1];
                        t = op;
                        break;
                    }
                    if (t && _.label < t[2]) {
                        _.label = t[2];
                        _.ops.push(op);
                        break;
                    }
                    if (t[2]) _.ops.pop();
                    _.trys.pop();
                    continue;
            }
            op = body.call(thisArg, _);
        } catch (e) {
            op = [
                6,
                e
            ];
            y = 0;
        } finally{
            f = t = 0;
        }
        if (op[0] & 5) throw op[1];
        return {
            value: op[0] ? op[1] : void 0,
            done: true
        };
    }
}
var useEnsSignOut = function(params) {
    var _useJustaName = useJustaName(), backendUrl = _useJustaName.backendUrl, routes = _useJustaName.routes;
    var _backendUrl = useMemo(function() {
        return (params === null || params === void 0 ? void 0 : params.backendUrl) || backendUrl || '';
    }, [
        backendUrl,
        params === null || params === void 0 ? void 0 : params.backendUrl
    ]);
    var _signoutRoute = useMemo(function() {
        return (params === null || params === void 0 ? void 0 : params.signoutRoute) || routes.signoutRoute;
    }, [
        routes.signoutRoute,
        params === null || params === void 0 ? void 0 : params.signoutRoute
    ]);
    var signoutEndpoint = useMemo(function() {
        return _backendUrl + _signoutRoute;
    }, [
        _backendUrl,
        _signoutRoute
    ]);
    var _currentEnsRoute = useMemo(function() {
        return (params === null || params === void 0 ? void 0 : params.currentEnsRoute) || routes.currentEnsRoute;
    }, [
        routes.currentEnsRoute,
        params === null || params === void 0 ? void 0 : params.currentEnsRoute
    ]);
    var _signinNonceRoute = useMemo(function() {
        return (params === null || params === void 0 ? void 0 : params.signinNonceRoute) || routes.signinNonceRoute;
    }, [
        routes.signinNonceRoute,
        params === null || params === void 0 ? void 0 : params.signinNonceRoute
    ]);
    var refreshEnsAuth = useEnsAuth({
        backendUrl: _backendUrl,
        currentEnsRoute: _currentEnsRoute,
        local: params === null || params === void 0 ? void 0 : params.local
    }).refreshEnsAuth;
    var refetchNonce = useEnsNonce({
        backendUrl: _backendUrl,
        signinNonceRoute: _signinNonceRoute,
        enabled: !(params === null || params === void 0 ? void 0 : params.local)
    }).refetchNonce;
    var mutation = useMutation({
        mutationFn: /*#__PURE__*/ _async_to_generator$a(function() {
            return _ts_generator$a(this, function(_state) {
                switch(_state.label){
                    case 0:
                        if (params === null || params === void 0 ? void 0 : params.local) {
                            localStorage.removeItem('ENS_AUTH');
                            refreshEnsAuth();
                            return [
                                2
                            ];
                        }
                        return [
                            4,
                            fetch(signoutEndpoint, {
                                method: 'POST',
                                credentials: 'include'
                            })
                        ];
                    case 1:
                        _state.sent();
                        return [
                            4,
                            refetchNonce()
                        ];
                    case 2:
                        _state.sent();
                        refreshEnsAuth();
                        return [
                            2
                        ];
                }
            });
        })
    });
    return {
        signOut: mutation.mutateAsync,
        isSignOutPending: mutation.isPending
    };
};

var splitDomain = function(domain) {
    var parts = domain.split('.');
    if (parts.length === 2) {
        return [
            '',
            domain
        ];
    }
    if (parts.length > 2) {
        return [
            parts.slice(0, -2).join('.'),
            parts.slice(-2).join('.')
        ];
    }
    return [
        '',
        ''
    ];
};

function _array_like_to_array$5(arr, len) {
    if (len == null || len > arr.length) len = arr.length;
    for(var i = 0, arr2 = new Array(len); i < len; i++)arr2[i] = arr[i];
    return arr2;
}
function _array_with_holes$2(arr) {
    if (Array.isArray(arr)) return arr;
}
function asyncGeneratorStep$9(gen, resolve, reject, _next, _throw, key, arg) {
    try {
        var info = gen[key](arg);
        var value = info.value;
    } catch (error) {
        reject(error);
        return;
    }
    if (info.done) {
        resolve(value);
    } else {
        Promise.resolve(value).then(_next, _throw);
    }
}
function _async_to_generator$9(fn) {
    return function() {
        var self = this, args = arguments;
        return new Promise(function(resolve, reject) {
            var gen = fn.apply(self, args);
            function _next(value) {
                asyncGeneratorStep$9(gen, resolve, reject, _next, _throw, "next", value);
            }
            function _throw(err) {
                asyncGeneratorStep$9(gen, resolve, reject, _next, _throw, "throw", err);
            }
            _next(undefined);
        });
    };
}
function _define_property$9(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _iterable_to_array_limit$2(arr, i) {
    var _i = arr == null ? null : typeof Symbol !== "undefined" && arr[Symbol.iterator] || arr["@@iterator"];
    if (_i == null) return;
    var _arr = [];
    var _n = true;
    var _d = false;
    var _s, _e;
    try {
        for(_i = _i.call(arr); !(_n = (_s = _i.next()).done); _n = true){
            _arr.push(_s.value);
            if (i && _arr.length === i) break;
        }
    } catch (err) {
        _d = true;
        _e = err;
    } finally{
        try {
            if (!_n && _i["return"] != null) _i["return"]();
        } finally{
            if (_d) throw _e;
        }
    }
    return _arr;
}
function _non_iterable_rest$2() {
    throw new TypeError("Invalid attempt to destructure non-iterable instance.\\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}
function _object_spread$9(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property$9(target, key, source[key]);
        });
    }
    return target;
}
function ownKeys$9(object, enumerableOnly) {
    var keys = Object.keys(object);
    if (Object.getOwnPropertySymbols) {
        var symbols = Object.getOwnPropertySymbols(object);
        keys.push.apply(keys, symbols);
    }
    return keys;
}
function _object_spread_props$9(target, source) {
    source = source != null ? source : {};
    if (Object.getOwnPropertyDescriptors) {
        Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
        ownKeys$9(Object(source)).forEach(function(key) {
            Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
        });
    }
    return target;
}
function _sliced_to_array$2(arr, i) {
    return _array_with_holes$2(arr) || _iterable_to_array_limit$2(arr, i) || _unsupported_iterable_to_array$5(arr, i) || _non_iterable_rest$2();
}
function _unsupported_iterable_to_array$5(o, minLen) {
    if (!o) return;
    if (typeof o === "string") return _array_like_to_array$5(o, minLen);
    var n = Object.prototype.toString.call(o).slice(8, -1);
    if (n === "Object" && o.constructor) n = o.constructor.name;
    if (n === "Map" || n === "Set") return Array.from(n);
    if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _array_like_to_array$5(o, minLen);
}
function _ts_generator$9(thisArg, body) {
    var f, y, t, g, _ = {
        label: 0,
        sent: function() {
            if (t[0] & 1) throw t[1];
            return t[1];
        },
        trys: [],
        ops: []
    };
    return g = {
        next: verb(0),
        "throw": verb(1),
        "return": verb(2)
    }, typeof Symbol === "function" && (g[Symbol.iterator] = function() {
        return this;
    }), g;
    function verb(n) {
        return function(v) {
            return step([
                n,
                v
            ]);
        };
    }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while(_)try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [
                op[0] & 2,
                t.value
            ];
            switch(op[0]){
                case 0:
                case 1:
                    t = op;
                    break;
                case 4:
                    _.label++;
                    return {
                        value: op[1],
                        done: false
                    };
                case 5:
                    _.label++;
                    y = op[1];
                    op = [
                        0
                    ];
                    continue;
                case 7:
                    op = _.ops.pop();
                    _.trys.pop();
                    continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) {
                        _ = 0;
                        continue;
                    }
                    if (op[0] === 3 && (!t || op[1] > t[0] && op[1] < t[3])) {
                        _.label = op[1];
                        break;
                    }
                    if (op[0] === 6 && _.label < t[1]) {
                        _.label = t[1];
                        t = op;
                        break;
                    }
                    if (t && _.label < t[2]) {
                        _.label = t[2];
                        _.ops.push(op);
                        break;
                    }
                    if (t[2]) _.ops.pop();
                    _.trys.pop();
                    continue;
            }
            op = body.call(thisArg, _);
        } catch (e) {
            op = [
                6,
                e
            ];
            y = 0;
        } finally{
            f = t = 0;
        }
        if (op[0] & 5) throw op[1];
        return {
            value: op[0] ? op[1] : void 0,
            done: true
        };
    }
}
var useAcceptSubname = function(params) {
    var _useJustaName = useJustaName(), justaname = _useJustaName.justaname, chainId = _useJustaName.chainId;
    var address = useMountedAccount().address;
    var refetchInvitations = useAccountInvitations().refetchInvitations;
    var getSignature = useSubnameSignature().getSignature;
    var refetchAccountSubnames = useAccountSubnames().refetchAccountSubnames;
    var _chainId = useMemo(function() {
        return (params === null || params === void 0 ? void 0 : params.chainId) || chainId;
    }, [
        params === null || params === void 0 ? void 0 : params.chainId,
        chainId
    ]);
    var mutate = useMutation({
        mutationFn: function() {
            var _ref = _async_to_generator$9(function(_params) {
                var _ens, _splitDomain, _username, _ensDomain, signature, accepted;
                return _ts_generator$9(this, function(_state) {
                    switch(_state.label){
                        case 0:
                            if (!address) {
                                throw new Error('No address found');
                            }
                            _ens = _params.ens;
                            _splitDomain = _sliced_to_array$2(splitDomain(_ens), 2), _username = _splitDomain[0], _ensDomain = _splitDomain[1];
                            return [
                                4,
                                getSignature()
                            ];
                        case 1:
                            signature = _state.sent();
                            return [
                                4,
                                justaname.subnames.acceptSubname({
                                    addresses: _params.addresses,
                                    chainId: _params.chainId || _chainId,
                                    contentHash: _params.contentHash,
                                    ensDomain: _ensDomain,
                                    text: _params.text,
                                    username: _username
                                }, {
                                    xAddress: address,
                                    xSignature: signature.signature,
                                    xMessage: signature.message
                                })
                            ];
                        case 2:
                            accepted = _state.sent();
                            refetchAccountSubnames();
                            refetchInvitations();
                            return [
                                2,
                                _object_spread_props$9(_object_spread$9({}, accepted), {
                                    sanitizedRecords: sanitizeRecords(accepted)
                                })
                            ];
                    }
                });
            });
            return function(_params) {
                return _ref.apply(this, arguments);
            };
        }()
    });
    return {
        acceptSubname: mutate.mutateAsync,
        isAcceptSubnamePending: mutate.isPending
    };
};

function asyncGeneratorStep$8(gen, resolve, reject, _next, _throw, key, arg) {
    try {
        var info = gen[key](arg);
        var value = info.value;
    } catch (error) {
        reject(error);
        return;
    }
    if (info.done) {
        resolve(value);
    } else {
        Promise.resolve(value).then(_next, _throw);
    }
}
function _async_to_generator$8(fn) {
    return function() {
        var self = this, args = arguments;
        return new Promise(function(resolve, reject) {
            var gen = fn.apply(self, args);
            function _next(value) {
                asyncGeneratorStep$8(gen, resolve, reject, _next, _throw, "next", value);
            }
            function _throw(err) {
                asyncGeneratorStep$8(gen, resolve, reject, _next, _throw, "throw", err);
            }
            _next(undefined);
        });
    };
}
function _define_property$8(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _object_spread$8(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property$8(target, key, source[key]);
        });
    }
    return target;
}
function ownKeys$8(object, enumerableOnly) {
    var keys = Object.keys(object);
    if (Object.getOwnPropertySymbols) {
        var symbols = Object.getOwnPropertySymbols(object);
        keys.push.apply(keys, symbols);
    }
    return keys;
}
function _object_spread_props$8(target, source) {
    source = source != null ? source : {};
    if (Object.getOwnPropertyDescriptors) {
        Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
        ownKeys$8(Object(source)).forEach(function(key) {
            Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
        });
    }
    return target;
}
function _ts_generator$8(thisArg, body) {
    var f, y, t, g, _ = {
        label: 0,
        sent: function() {
            if (t[0] & 1) throw t[1];
            return t[1];
        },
        trys: [],
        ops: []
    };
    return g = {
        next: verb(0),
        "throw": verb(1),
        "return": verb(2)
    }, typeof Symbol === "function" && (g[Symbol.iterator] = function() {
        return this;
    }), g;
    function verb(n) {
        return function(v) {
            return step([
                n,
                v
            ]);
        };
    }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while(_)try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [
                op[0] & 2,
                t.value
            ];
            switch(op[0]){
                case 0:
                case 1:
                    t = op;
                    break;
                case 4:
                    _.label++;
                    return {
                        value: op[1],
                        done: false
                    };
                case 5:
                    _.label++;
                    y = op[1];
                    op = [
                        0
                    ];
                    continue;
                case 7:
                    op = _.ops.pop();
                    _.trys.pop();
                    continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) {
                        _ = 0;
                        continue;
                    }
                    if (op[0] === 3 && (!t || op[1] > t[0] && op[1] < t[3])) {
                        _.label = op[1];
                        break;
                    }
                    if (op[0] === 6 && _.label < t[1]) {
                        _.label = t[1];
                        t = op;
                        break;
                    }
                    if (t && _.label < t[2]) {
                        _.label = t[2];
                        _.ops.push(op);
                        break;
                    }
                    if (t[2]) _.ops.pop();
                    _.trys.pop();
                    continue;
            }
            op = body.call(thisArg, _);
        } catch (e) {
            op = [
                6,
                e
            ];
            y = 0;
        } finally{
            f = t = 0;
        }
        if (op[0] & 5) throw op[1];
        return {
            value: op[0] ? op[1] : void 0,
            done: true
        };
    }
}
var useAddSubname = function(params) {
    var _ensDomains_find;
    var _useJustaName = useJustaName(), justaname = _useJustaName.justaname, backendUrl = _useJustaName.backendUrl, routes = _useJustaName.routes, chainId = _useJustaName.chainId, ensDomains = _useJustaName.ensDomains;
    var address = useMountedAccount().address;
    var getSignature = useSubnameSignature().getSignature;
    var refetchAccountSubnames = useAccountSubnames().refetchAccountSubnames;
    var _chainId = useMemo(function() {
        return (params === null || params === void 0 ? void 0 : params.chainId) || chainId;
    }, [
        params === null || params === void 0 ? void 0 : params.chainId,
        chainId
    ]);
    var _ensDomain = useMemo(function() {
        var _ensDomains_find;
        return (params === null || params === void 0 ? void 0 : params.ensDomain) || ((_ensDomains_find = ensDomains.find(function(ensDomain) {
            return ensDomain.chainId === _chainId;
        })) === null || _ensDomains_find === void 0 ? void 0 : _ensDomains_find.ensDomain);
    }, [
        params === null || params === void 0 ? void 0 : params.ensDomain,
        ensDomains,
        _chainId
    ]);
    var _backendUrl = useMemo(function() {
        return (params === null || params === void 0 ? void 0 : params.backendUrl) || backendUrl;
    }, [
        params === null || params === void 0 ? void 0 : params.backendUrl,
        backendUrl
    ]);
    var _addSubnameRoute = useMemo(function() {
        return (params === null || params === void 0 ? void 0 : params.addSubnameRoute) || routes.addSubnameRoute;
    }, [
        params === null || params === void 0 ? void 0 : params.addSubnameRoute,
        routes.addSubnameRoute
    ]);
    var addSubnameEndpoint = useMemo(function() {
        return _backendUrl + _addSubnameRoute;
    }, [
        _backendUrl,
        _addSubnameRoute
    ]);
    var _apiKey = (params === null || params === void 0 ? void 0 : params.apiKey) || ((_ensDomains_find = ensDomains.find(function(ensDomain) {
        return ensDomain.chainId === _chainId;
    })) === null || _ensDomains_find === void 0 ? void 0 : _ensDomains_find.apiKey);
    var mutate = useMutation({
        mutationFn: function() {
            var _ref = _async_to_generator$8(function(_params) {
                var signature, __apiKey, __ensDomain, __chainId, __addresses, __text, __contentHash, response, backendResponse;
                return _ts_generator$8(this, function(_state) {
                    switch(_state.label){
                        case 0:
                            if (!address) {
                                throw new Error('No address found');
                            }
                            return [
                                4,
                                getSignature()
                            ];
                        case 1:
                            signature = _state.sent();
                            __apiKey = _params.apiKey || _apiKey;
                            __ensDomain = _params.ensDomain || _ensDomain;
                            __chainId = _params.chainId || _chainId;
                            __addresses = _params.addresses || (params === null || params === void 0 ? void 0 : params.addresses);
                            __text = _params.text || (params === null || params === void 0 ? void 0 : params.text);
                            __contentHash = _params.contentHash || (params === null || params === void 0 ? void 0 : params.contentHash);
                            if (!__apiKey) return [
                                3,
                                3
                            ];
                            return [
                                4,
                                justaname.subnames.addSubname({
                                    username: _params.username,
                                    ensDomain: __ensDomain,
                                    chainId: __chainId,
                                    addresses: __addresses,
                                    text: __text,
                                    contentHash: __contentHash
                                }, {
                                    xSignature: signature.signature,
                                    xAddress: address,
                                    xMessage: signature.message,
                                    xApiKey: __apiKey
                                })
                            ];
                        case 2:
                            response = _state.sent();
                            return [
                                3,
                                6
                            ];
                        case 3:
                            return [
                                4,
                                fetch(addSubnameEndpoint, {
                                    method: 'POST',
                                    headers: {
                                        'Content-Type': 'application/json'
                                    },
                                    body: JSON.stringify({
                                        username: _params.username,
                                        ensDomain: __ensDomain,
                                        chainId: __chainId,
                                        signature: signature.signature,
                                        address: address,
                                        message: signature.message,
                                        text: __text,
                                        contentHash: __contentHash,
                                        addresses: __addresses
                                    })
                                })
                            ];
                        case 4:
                            backendResponse = _state.sent();
                            if (!backendResponse.ok) {
                                throw new Error('Network response was not ok');
                            }
                            return [
                                4,
                                backendResponse.json()
                            ];
                        case 5:
                            response = _state.sent();
                            _state.label = 6;
                        case 6:
                            refetchAccountSubnames();
                            return [
                                2,
                                _object_spread_props$8(_object_spread$8({}, response), {
                                    sanitizedRecords: sanitizeRecords(response)
                                })
                            ];
                    }
                });
            });
            return function(_params) {
                return _ref.apply(this, arguments);
            };
        }()
    });
    return {
        addSubname: mutate.mutateAsync,
        isAddSubnamePending: mutate.isPending
    };
};

function _define_property$7(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _object_spread$7(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property$7(target, key, source[key]);
        });
    }
    return target;
}
function ownKeys$7(object, enumerableOnly) {
    var keys = Object.keys(object);
    if (Object.getOwnPropertySymbols) {
        var symbols = Object.getOwnPropertySymbols(object);
        keys.push.apply(keys, symbols);
    }
    return keys;
}
function _object_spread_props$7(target, source) {
    source = source != null ? source : {};
    if (Object.getOwnPropertyDescriptors) {
        Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
        ownKeys$7(Object(source)).forEach(function(key) {
            Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
        });
    }
    return target;
}
var buildIsSubnameAvailableKey = function(username, ensDomain, chainId) {
    return [
        'IS_SUBNAME_AVAILABLE',
        username,
        ensDomain,
        chainId
    ];
};
var useIsSubnameAvailable = function(params) {
    var _ensDomains_find;
    var _useJustaName = useJustaName(), justaname = _useJustaName.justaname, chainId = _useJustaName.chainId, ensDomains = _useJustaName.ensDomains;
    var _enabled = (params === null || params === void 0 ? void 0 : params.enabled) !== undefined ? params.enabled : true;
    var _username = params.username;
    var _ensDomain = params.ensDomain || ((_ensDomains_find = ensDomains.find(function(ensDomain) {
        return ensDomain.chainId === chainId;
    })) === null || _ensDomains_find === void 0 ? void 0 : _ensDomains_find.ensDomain);
    var _chainId = params.chainId ? params.chainId : chainId;
    var query = useQuery(_object_spread_props$7(_object_spread$7({}, defaultOptions), {
        queryKey: buildIsSubnameAvailableKey(_username, _ensDomain, _chainId),
        queryFn: function() {
            return justaname === null || justaname === void 0 ? void 0 : justaname.subnames.isSubnameAvailable({
                subname: _username + '.' + _ensDomain,
                chainId: _chainId
            });
        },
        enabled: Boolean(_username) && Boolean(_chainId) && Boolean(_ensDomain) && Boolean(_enabled)
    }));
    return {
        isSubnameAvailable: query.data,
        isSubnameAvailablePending: query.isPending,
        isSubnameAvailableFetching: query.isFetching,
        isSubnameAvailableLoading: query.isPending || query.isFetching
    };
};

function _array_like_to_array$4(arr, len) {
    if (len == null || len > arr.length) len = arr.length;
    for(var i = 0, arr2 = new Array(len); i < len; i++)arr2[i] = arr[i];
    return arr2;
}
function _array_with_holes$1(arr) {
    if (Array.isArray(arr)) return arr;
}
function asyncGeneratorStep$7(gen, resolve, reject, _next, _throw, key, arg) {
    try {
        var info = gen[key](arg);
        var value = info.value;
    } catch (error) {
        reject(error);
        return;
    }
    if (info.done) {
        resolve(value);
    } else {
        Promise.resolve(value).then(_next, _throw);
    }
}
function _async_to_generator$7(fn) {
    return function() {
        var self = this, args = arguments;
        return new Promise(function(resolve, reject) {
            var gen = fn.apply(self, args);
            function _next(value) {
                asyncGeneratorStep$7(gen, resolve, reject, _next, _throw, "next", value);
            }
            function _throw(err) {
                asyncGeneratorStep$7(gen, resolve, reject, _next, _throw, "throw", err);
            }
            _next(undefined);
        });
    };
}
function _define_property$6(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _iterable_to_array_limit$1(arr, i) {
    var _i = arr == null ? null : typeof Symbol !== "undefined" && arr[Symbol.iterator] || arr["@@iterator"];
    if (_i == null) return;
    var _arr = [];
    var _n = true;
    var _d = false;
    var _s, _e;
    try {
        for(_i = _i.call(arr); !(_n = (_s = _i.next()).done); _n = true){
            _arr.push(_s.value);
            if (i && _arr.length === i) break;
        }
    } catch (err) {
        _d = true;
        _e = err;
    } finally{
        try {
            if (!_n && _i["return"] != null) _i["return"]();
        } finally{
            if (_d) throw _e;
        }
    }
    return _arr;
}
function _non_iterable_rest$1() {
    throw new TypeError("Invalid attempt to destructure non-iterable instance.\\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}
function _object_spread$6(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property$6(target, key, source[key]);
        });
    }
    return target;
}
function ownKeys$6(object, enumerableOnly) {
    var keys = Object.keys(object);
    if (Object.getOwnPropertySymbols) {
        var symbols = Object.getOwnPropertySymbols(object);
        keys.push.apply(keys, symbols);
    }
    return keys;
}
function _object_spread_props$6(target, source) {
    source = source != null ? source : {};
    if (Object.getOwnPropertyDescriptors) {
        Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
        ownKeys$6(Object(source)).forEach(function(key) {
            Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
        });
    }
    return target;
}
function _sliced_to_array$1(arr, i) {
    return _array_with_holes$1(arr) || _iterable_to_array_limit$1(arr, i) || _unsupported_iterable_to_array$4(arr, i) || _non_iterable_rest$1();
}
function _unsupported_iterable_to_array$4(o, minLen) {
    if (!o) return;
    if (typeof o === "string") return _array_like_to_array$4(o, minLen);
    var n = Object.prototype.toString.call(o).slice(8, -1);
    if (n === "Object" && o.constructor) n = o.constructor.name;
    if (n === "Map" || n === "Set") return Array.from(n);
    if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _array_like_to_array$4(o, minLen);
}
function _ts_generator$7(thisArg, body) {
    var f, y, t, g, _ = {
        label: 0,
        sent: function() {
            if (t[0] & 1) throw t[1];
            return t[1];
        },
        trys: [],
        ops: []
    };
    return g = {
        next: verb(0),
        "throw": verb(1),
        "return": verb(2)
    }, typeof Symbol === "function" && (g[Symbol.iterator] = function() {
        return this;
    }), g;
    function verb(n) {
        return function(v) {
            return step([
                n,
                v
            ]);
        };
    }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while(_)try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [
                op[0] & 2,
                t.value
            ];
            switch(op[0]){
                case 0:
                case 1:
                    t = op;
                    break;
                case 4:
                    _.label++;
                    return {
                        value: op[1],
                        done: false
                    };
                case 5:
                    _.label++;
                    y = op[1];
                    op = [
                        0
                    ];
                    continue;
                case 7:
                    op = _.ops.pop();
                    _.trys.pop();
                    continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) {
                        _ = 0;
                        continue;
                    }
                    if (op[0] === 3 && (!t || op[1] > t[0] && op[1] < t[3])) {
                        _.label = op[1];
                        break;
                    }
                    if (op[0] === 6 && _.label < t[1]) {
                        _.label = t[1];
                        t = op;
                        break;
                    }
                    if (t && _.label < t[2]) {
                        _.label = t[2];
                        _.ops.push(op);
                        break;
                    }
                    if (t[2]) _.ops.pop();
                    _.trys.pop();
                    continue;
            }
            op = body.call(thisArg, _);
        } catch (e) {
            op = [
                6,
                e
            ];
            y = 0;
        } finally{
            f = t = 0;
        }
        if (op[0] & 5) throw op[1];
        return {
            value: op[0] ? op[1] : void 0,
            done: true
        };
    }
}
var useRejectSubname = function(params) {
    var _useJustaName = useJustaName(), justaname = _useJustaName.justaname, chainId = _useJustaName.chainId;
    var address = useMountedAccount().address;
    var refetchInvitations = useAccountInvitations().refetchInvitations;
    var getSignature = useSubnameSignature().getSignature;
    var refetchAccountSubnames = useAccountSubnames().refetchAccountSubnames;
    var _chainId = useMemo(function() {
        return (params === null || params === void 0 ? void 0 : params.chainId) || chainId;
    }, [
        params === null || params === void 0 ? void 0 : params.chainId,
        chainId
    ]);
    var mutate = useMutation({
        mutationFn: function() {
            var _ref = _async_to_generator$7(function(_params) {
                var __chainId, _ens, _splitDomain, _username, _ensDomain, signature, rejected;
                return _ts_generator$7(this, function(_state) {
                    switch(_state.label){
                        case 0:
                            if (!address) {
                                throw new Error('No address found');
                            }
                            __chainId = _params.chainId || _chainId;
                            _ens = _params.ens;
                            _splitDomain = _sliced_to_array$1(splitDomain(_ens), 2), _username = _splitDomain[0], _ensDomain = _splitDomain[1];
                            return [
                                4,
                                getSignature()
                            ];
                        case 1:
                            signature = _state.sent();
                            return [
                                4,
                                justaname.subnames.rejectSubname({
                                    ensDomain: _ensDomain,
                                    chainId: __chainId,
                                    username: _username
                                }, {
                                    xAddress: address,
                                    xSignature: signature.signature,
                                    xMessage: signature.message
                                })
                            ];
                        case 2:
                            rejected = _state.sent();
                            refetchAccountSubnames();
                            refetchInvitations();
                            return [
                                2,
                                _object_spread_props$6(_object_spread$6({}, rejected), {
                                    sanitizedRecords: sanitizeRecords(rejected)
                                })
                            ];
                    }
                });
            });
            return function(_params) {
                return _ref.apply(this, arguments);
            };
        }()
    });
    return {
        rejectSubname: mutate.mutateAsync,
        isRejectSubnamePending: mutate.isPending
    };
};

function asyncGeneratorStep$6(gen, resolve, reject, _next, _throw, key, arg) {
    try {
        var info = gen[key](arg);
        var value = info.value;
    } catch (error) {
        reject(error);
        return;
    }
    if (info.done) {
        resolve(value);
    } else {
        Promise.resolve(value).then(_next, _throw);
    }
}
function _async_to_generator$6(fn) {
    return function() {
        var self = this, args = arguments;
        return new Promise(function(resolve, reject) {
            var gen = fn.apply(self, args);
            function _next(value) {
                asyncGeneratorStep$6(gen, resolve, reject, _next, _throw, "next", value);
            }
            function _throw(err) {
                asyncGeneratorStep$6(gen, resolve, reject, _next, _throw, "throw", err);
            }
            _next(undefined);
        });
    };
}
function _define_property$5(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _object_spread$5(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property$5(target, key, source[key]);
        });
    }
    return target;
}
function ownKeys$5(object, enumerableOnly) {
    var keys = Object.keys(object);
    if (Object.getOwnPropertySymbols) {
        var symbols = Object.getOwnPropertySymbols(object);
        keys.push.apply(keys, symbols);
    }
    return keys;
}
function _object_spread_props$5(target, source) {
    source = source != null ? source : {};
    if (Object.getOwnPropertyDescriptors) {
        Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
        ownKeys$5(Object(source)).forEach(function(key) {
            Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
        });
    }
    return target;
}
function _ts_generator$6(thisArg, body) {
    var f, y, t, g, _ = {
        label: 0,
        sent: function() {
            if (t[0] & 1) throw t[1];
            return t[1];
        },
        trys: [],
        ops: []
    };
    return g = {
        next: verb(0),
        "throw": verb(1),
        "return": verb(2)
    }, typeof Symbol === "function" && (g[Symbol.iterator] = function() {
        return this;
    }), g;
    function verb(n) {
        return function(v) {
            return step([
                n,
                v
            ]);
        };
    }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while(_)try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [
                op[0] & 2,
                t.value
            ];
            switch(op[0]){
                case 0:
                case 1:
                    t = op;
                    break;
                case 4:
                    _.label++;
                    return {
                        value: op[1],
                        done: false
                    };
                case 5:
                    _.label++;
                    y = op[1];
                    op = [
                        0
                    ];
                    continue;
                case 7:
                    op = _.ops.pop();
                    _.trys.pop();
                    continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) {
                        _ = 0;
                        continue;
                    }
                    if (op[0] === 3 && (!t || op[1] > t[0] && op[1] < t[3])) {
                        _.label = op[1];
                        break;
                    }
                    if (op[0] === 6 && _.label < t[1]) {
                        _.label = t[1];
                        t = op;
                        break;
                    }
                    if (t && _.label < t[2]) {
                        _.label = t[2];
                        _.ops.push(op);
                        break;
                    }
                    if (t[2]) _.ops.pop();
                    _.trys.pop();
                    continue;
            }
            op = body.call(thisArg, _);
        } catch (e) {
            op = [
                6,
                e
            ];
            y = 0;
        } finally{
            f = t = 0;
        }
        if (op[0] & 5) throw op[1];
        return {
            value: op[0] ? op[1] : void 0,
            done: true
        };
    }
}
var useRevokeSubname = function(params) {
    var _ensDomains_find;
    var _useJustaName = useJustaName(), backendUrl = _useJustaName.backendUrl, routes = _useJustaName.routes, justaname = _useJustaName.justaname, chainId = _useJustaName.chainId, ensDomains = _useJustaName.ensDomains;
    var address = useMountedAccount().address;
    var getSignature = useSubnameSignature().getSignature;
    var refetchAccountSubnames = useAccountSubnames().refetchAccountSubnames;
    var _chainId = useMemo(function() {
        return (params === null || params === void 0 ? void 0 : params.chainId) || chainId;
    }, [
        params === null || params === void 0 ? void 0 : params.chainId,
        chainId
    ]);
    var _ensDomain = useMemo(function() {
        var _ensDomains_find;
        return (params === null || params === void 0 ? void 0 : params.ensDomain) || ((_ensDomains_find = ensDomains.find(function(ensDomain) {
            return ensDomain.chainId === _chainId;
        })) === null || _ensDomains_find === void 0 ? void 0 : _ensDomains_find.ensDomain);
    }, [
        params === null || params === void 0 ? void 0 : params.ensDomain,
        ensDomains,
        _chainId
    ]);
    var _backendUrl = useMemo(function() {
        return (params === null || params === void 0 ? void 0 : params.backendUrl) || backendUrl;
    }, [
        params === null || params === void 0 ? void 0 : params.backendUrl,
        backendUrl
    ]);
    var _revokeSubnameRoute = useMemo(function() {
        return (params === null || params === void 0 ? void 0 : params.revokeSubnameRoute) || routes.revokeSubnameRoute;
    }, [
        params === null || params === void 0 ? void 0 : params.revokeSubnameRoute,
        routes.revokeSubnameRoute
    ]);
    var revokeEndpoint = useMemo(function() {
        return _backendUrl + _revokeSubnameRoute;
    }, [
        _backendUrl,
        _revokeSubnameRoute
    ]);
    var _apiKey = (params === null || params === void 0 ? void 0 : params.apiKey) || ((_ensDomains_find = ensDomains.find(function(ensDomain) {
        return ensDomain.chainId === _chainId;
    })) === null || _ensDomains_find === void 0 ? void 0 : _ensDomains_find.apiKey);
    var mutate = useMutation({
        mutationFn: function() {
            var _ref = _async_to_generator$6(function(_params) {
                var signature, response, __apiKey, __ensDomain, __chainId, backendResponse;
                return _ts_generator$6(this, function(_state) {
                    switch(_state.label){
                        case 0:
                            if (!address) {
                                throw new Error('No address found');
                            }
                            return [
                                4,
                                getSignature()
                            ];
                        case 1:
                            signature = _state.sent();
                            if (!_ensDomain) {
                                throw new Error('Missing ensDomain name: add the ensDomain to the hook params or the function params or to the provider');
                            }
                            __apiKey = (params === null || params === void 0 ? void 0 : params.apiKey) || _apiKey;
                            __ensDomain = (params === null || params === void 0 ? void 0 : params.ensDomain) || _ensDomain;
                            __chainId = (params === null || params === void 0 ? void 0 : params.chainId) || _chainId;
                            if (!__apiKey) return [
                                3,
                                3
                            ];
                            return [
                                4,
                                justaname.subnames.revokeSubname({
                                    username: _params.username,
                                    ensDomain: __ensDomain,
                                    chainId: __chainId
                                }, {
                                    xSignature: signature.signature,
                                    xAddress: address,
                                    xMessage: signature.message,
                                    xApiKey: __apiKey
                                })
                            ];
                        case 2:
                            response = _state.sent();
                            return [
                                3,
                                6
                            ];
                        case 3:
                            return [
                                4,
                                fetch(revokeEndpoint, {
                                    method: 'POST',
                                    headers: {
                                        'Content-Type': 'application/json'
                                    },
                                    body: JSON.stringify(_object_spread_props$5(_object_spread$5({}, params), {
                                        signature: signature.signature,
                                        ensDomain: __ensDomain,
                                        chainId: __chainId,
                                        address: address,
                                        message: signature.message,
                                        username: _params.username
                                    }))
                                })
                            ];
                        case 4:
                            backendResponse = _state.sent();
                            if (!backendResponse.ok) {
                                throw new Error('Network response was not ok');
                            }
                            return [
                                4,
                                backendResponse.json()
                            ];
                        case 5:
                            response = _state.sent();
                            _state.label = 6;
                        case 6:
                            refetchAccountSubnames();
                            return [
                                2,
                                _object_spread_props$5(_object_spread$5({}, response), {
                                    sanitizedRecords: sanitizeRecords(response)
                                })
                            ];
                    }
                });
            });
            return function(_params) {
                return _ref.apply(this, arguments);
            };
        }()
    });
    return {
        revokeSubname: mutate.mutateAsync,
        isRevokeSubnamePending: mutate.isPending
    };
};

function _array_like_to_array$3(arr, len) {
    if (len == null || len > arr.length) len = arr.length;
    for(var i = 0, arr2 = new Array(len); i < len; i++)arr2[i] = arr[i];
    return arr2;
}
function _array_without_holes$2(arr) {
    if (Array.isArray(arr)) return _array_like_to_array$3(arr);
}
function asyncGeneratorStep$5(gen, resolve, reject, _next, _throw, key, arg) {
    try {
        var info = gen[key](arg);
        var value = info.value;
    } catch (error) {
        reject(error);
        return;
    }
    if (info.done) {
        resolve(value);
    } else {
        Promise.resolve(value).then(_next, _throw);
    }
}
function _async_to_generator$5(fn) {
    return function() {
        var self = this, args = arguments;
        return new Promise(function(resolve, reject) {
            var gen = fn.apply(self, args);
            function _next(value) {
                asyncGeneratorStep$5(gen, resolve, reject, _next, _throw, "next", value);
            }
            function _throw(err) {
                asyncGeneratorStep$5(gen, resolve, reject, _next, _throw, "throw", err);
            }
            _next(undefined);
        });
    };
}
function _define_property$4(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _iterable_to_array$2(iter) {
    if (typeof Symbol !== "undefined" && iter[Symbol.iterator] != null || iter["@@iterator"] != null) return Array.from(iter);
}
function _non_iterable_spread$2() {
    throw new TypeError("Invalid attempt to spread non-iterable instance.\\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}
function _object_spread$4(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property$4(target, key, source[key]);
        });
    }
    return target;
}
function ownKeys$4(object, enumerableOnly) {
    var keys = Object.keys(object);
    if (Object.getOwnPropertySymbols) {
        var symbols = Object.getOwnPropertySymbols(object);
        keys.push.apply(keys, symbols);
    }
    return keys;
}
function _object_spread_props$4(target, source) {
    source = source != null ? source : {};
    if (Object.getOwnPropertyDescriptors) {
        Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
        ownKeys$4(Object(source)).forEach(function(key) {
            Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
        });
    }
    return target;
}
function _object_without_properties(source, excluded) {
    if (source == null) return {};
    var target = _object_without_properties_loose(source, excluded);
    var key, i;
    if (Object.getOwnPropertySymbols) {
        var sourceSymbolKeys = Object.getOwnPropertySymbols(source);
        for(i = 0; i < sourceSymbolKeys.length; i++){
            key = sourceSymbolKeys[i];
            if (excluded.indexOf(key) >= 0) continue;
            if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
            target[key] = source[key];
        }
    }
    return target;
}
function _object_without_properties_loose(source, excluded) {
    if (source == null) return {};
    var target = {};
    var sourceKeys = Object.keys(source);
    var key, i;
    for(i = 0; i < sourceKeys.length; i++){
        key = sourceKeys[i];
        if (excluded.indexOf(key) >= 0) continue;
        target[key] = source[key];
    }
    return target;
}
function _to_consumable_array$2(arr) {
    return _array_without_holes$2(arr) || _iterable_to_array$2(arr) || _unsupported_iterable_to_array$3(arr) || _non_iterable_spread$2();
}
function _unsupported_iterable_to_array$3(o, minLen) {
    if (!o) return;
    if (typeof o === "string") return _array_like_to_array$3(o, minLen);
    var n = Object.prototype.toString.call(o).slice(8, -1);
    if (n === "Object" && o.constructor) n = o.constructor.name;
    if (n === "Map" || n === "Set") return Array.from(n);
    if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _array_like_to_array$3(o, minLen);
}
function _ts_generator$5(thisArg, body) {
    var f, y, t, g, _ = {
        label: 0,
        sent: function() {
            if (t[0] & 1) throw t[1];
            return t[1];
        },
        trys: [],
        ops: []
    };
    return g = {
        next: verb(0),
        "throw": verb(1),
        "return": verb(2)
    }, typeof Symbol === "function" && (g[Symbol.iterator] = function() {
        return this;
    }), g;
    function verb(n) {
        return function(v) {
            return step([
                n,
                v
            ]);
        };
    }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while(_)try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [
                op[0] & 2,
                t.value
            ];
            switch(op[0]){
                case 0:
                case 1:
                    t = op;
                    break;
                case 4:
                    _.label++;
                    return {
                        value: op[1],
                        done: false
                    };
                case 5:
                    _.label++;
                    y = op[1];
                    op = [
                        0
                    ];
                    continue;
                case 7:
                    op = _.ops.pop();
                    _.trys.pop();
                    continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) {
                        _ = 0;
                        continue;
                    }
                    if (op[0] === 3 && (!t || op[1] > t[0] && op[1] < t[3])) {
                        _.label = op[1];
                        break;
                    }
                    if (op[0] === 6 && _.label < t[1]) {
                        _.label = t[1];
                        t = op;
                        break;
                    }
                    if (t && _.label < t[2]) {
                        _.label = t[2];
                        _.ops.push(op);
                        break;
                    }
                    if (t[2]) _.ops.pop();
                    _.trys.pop();
                    continue;
            }
            op = body.call(thisArg, _);
        } catch (e) {
            op = [
                6,
                e
            ];
            y = 0;
        } finally{
            f = t = 0;
        }
        if (op[0] & 5) throw op[1];
        return {
            value: op[0] ? op[1] : void 0,
            done: true
        };
    }
}
var buildSearchSubnamesKey = function(params) {
    return [
        'SEARCH_SUBNAME'
    ].concat(_to_consumable_array$2(Object.values(params)));
};
var useSearchSubnames = function(params) {
    var _useJustaName = useJustaName(), justaname = _useJustaName.justaname, defaultChainId = _useJustaName.chainId;
    var name = params.name, chainId = params.chainId, rest = _object_without_properties(params, [
        "name",
        "chainId"
    ]);
    var _chainId = chainId || defaultChainId;
    var _enabled = (params === null || params === void 0 ? void 0 : params.enabled) !== undefined ? params.enabled : true;
    var currentParams = _object_spread$4({
        name: name,
        skip: 0,
        take: 10,
        data: true,
        ensRegistered: false,
        isClaimed: true,
        chainId: _chainId
    }, rest);
    var query = useQuery(_object_spread_props$4(_object_spread$4({}, defaultOptions), {
        queryKey: buildSearchSubnamesKey(currentParams),
        queryFn: /*#__PURE__*/ _async_to_generator$5(function() {
            return _ts_generator$5(this, function(_state) {
                switch(_state.label){
                    case 0:
                        return [
                            4,
                            justaname === null || justaname === void 0 ? void 0 : justaname.subnames.searchSubnames(currentParams)
                        ];
                    case 1:
                        return [
                            2,
                            _state.sent()
                        ];
                }
            });
        }),
        enabled: Boolean(name) && Boolean(justaname) && Boolean(_enabled)
    }));
    return {
        subnames: query.data || {
            domains: []
        },
        refetchSearchSubnames: query.refetch,
        isSubnamesPending: query.isPending,
        isSubnamesFetching: query.isFetching,
        isSubnamesLoading: query.isPending || query.isFetching
    };
};

function _define_property$3(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _object_spread$3(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property$3(target, key, source[key]);
        });
    }
    return target;
}
function ownKeys$3(object, enumerableOnly) {
    var keys = Object.keys(object);
    if (Object.getOwnPropertySymbols) {
        var symbols = Object.getOwnPropertySymbols(object);
        keys.push.apply(keys, symbols);
    }
    return keys;
}
function _object_spread_props$3(target, source) {
    source = source != null ? source : {};
    if (Object.getOwnPropertyDescriptors) {
        Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
        ownKeys$3(Object(source)).forEach(function(key) {
            Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
        });
    }
    return target;
}
var useEnsWalletClient = function(params) {
    var query = useWalletClient();
    var transformWalletToEnsWallet = function(wallet) {
        if (!wallet) {
            return;
        }
        if ((wallet === null || wallet === void 0 ? void 0 : wallet.chain.id) !== 1 && (wallet === null || wallet === void 0 ? void 0 : wallet.chain.id) !== 11155111) {
            return;
        }
        return _object_spread_props$3(_object_spread$3({}, wallet), {
            chain: addEnsContracts(wallet.chain)
        });
    };
    return {
        ensWalletClient: transformWalletToEnsWallet(query.data),
        isEnsWalletClientPending: query.isPending
    };
};

function _array_like_to_array$2(arr, len) {
    if (len == null || len > arr.length) len = arr.length;
    for(var i = 0, arr2 = new Array(len); i < len; i++)arr2[i] = arr[i];
    return arr2;
}
function _array_without_holes$1(arr) {
    if (Array.isArray(arr)) return _array_like_to_array$2(arr);
}
function asyncGeneratorStep$4(gen, resolve, reject, _next, _throw, key, arg) {
    try {
        var info = gen[key](arg);
        var value = info.value;
    } catch (error) {
        reject(error);
        return;
    }
    if (info.done) {
        resolve(value);
    } else {
        Promise.resolve(value).then(_next, _throw);
    }
}
function _async_to_generator$4(fn) {
    return function() {
        var self = this, args = arguments;
        return new Promise(function(resolve, reject) {
            var gen = fn.apply(self, args);
            function _next(value) {
                asyncGeneratorStep$4(gen, resolve, reject, _next, _throw, "next", value);
            }
            function _throw(err) {
                asyncGeneratorStep$4(gen, resolve, reject, _next, _throw, "throw", err);
            }
            _next(undefined);
        });
    };
}
function _define_property$2(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _iterable_to_array$1(iter) {
    if (typeof Symbol !== "undefined" && iter[Symbol.iterator] != null || iter["@@iterator"] != null) return Array.from(iter);
}
function _non_iterable_spread$1() {
    throw new TypeError("Invalid attempt to spread non-iterable instance.\\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}
function _object_spread$2(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property$2(target, key, source[key]);
        });
    }
    return target;
}
function ownKeys$2(object, enumerableOnly) {
    var keys = Object.keys(object);
    if (Object.getOwnPropertySymbols) {
        var symbols = Object.getOwnPropertySymbols(object);
        keys.push.apply(keys, symbols);
    }
    return keys;
}
function _object_spread_props$2(target, source) {
    source = source != null ? source : {};
    if (Object.getOwnPropertyDescriptors) {
        Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
        ownKeys$2(Object(source)).forEach(function(key) {
            Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
        });
    }
    return target;
}
function _to_consumable_array$1(arr) {
    return _array_without_holes$1(arr) || _iterable_to_array$1(arr) || _unsupported_iterable_to_array$2(arr) || _non_iterable_spread$1();
}
function _unsupported_iterable_to_array$2(o, minLen) {
    if (!o) return;
    if (typeof o === "string") return _array_like_to_array$2(o, minLen);
    var n = Object.prototype.toString.call(o).slice(8, -1);
    if (n === "Object" && o.constructor) n = o.constructor.name;
    if (n === "Map" || n === "Set") return Array.from(n);
    if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _array_like_to_array$2(o, minLen);
}
function _ts_generator$4(thisArg, body) {
    var f, y, t, g, _ = {
        label: 0,
        sent: function() {
            if (t[0] & 1) throw t[1];
            return t[1];
        },
        trys: [],
        ops: []
    };
    return g = {
        next: verb(0),
        "throw": verb(1),
        "return": verb(2)
    }, typeof Symbol === "function" && (g[Symbol.iterator] = function() {
        return this;
    }), g;
    function verb(n) {
        return function(v) {
            return step([
                n,
                v
            ]);
        };
    }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while(_)try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [
                op[0] & 2,
                t.value
            ];
            switch(op[0]){
                case 0:
                case 1:
                    t = op;
                    break;
                case 4:
                    _.label++;
                    return {
                        value: op[1],
                        done: false
                    };
                case 5:
                    _.label++;
                    y = op[1];
                    op = [
                        0
                    ];
                    continue;
                case 7:
                    op = _.ops.pop();
                    _.trys.pop();
                    continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) {
                        _ = 0;
                        continue;
                    }
                    if (op[0] === 3 && (!t || op[1] > t[0] && op[1] < t[3])) {
                        _.label = op[1];
                        break;
                    }
                    if (op[0] === 6 && _.label < t[1]) {
                        _.label = t[1];
                        t = op;
                        break;
                    }
                    if (t && _.label < t[2]) {
                        _.label = t[2];
                        _.ops.push(op);
                        break;
                    }
                    if (t[2]) _.ops.pop();
                    _.trys.pop();
                    continue;
            }
            op = body.call(thisArg, _);
        } catch (e) {
            op = [
                6,
                e
            ];
            y = 0;
        } finally{
            f = t = 0;
        }
        if (op[0] & 5) throw op[1];
        return {
            value: op[0] ? op[1] : void 0,
            done: true
        };
    }
}
var buildUpdateChangesKey = function(params) {
    return [
        'ENS_UPDATE_CHANGES'
    ].concat(_to_consumable_array$1(Object.values(params)));
};
var useUpdateChanges = function(params) {
    var _query_data, _query_data1;
    var chainId = useJustaName().chainId;
    var getRecords = useRecords().getRecords;
    var _chainId = (params === null || params === void 0 ? void 0 : params.chainId) || chainId;
    var checkIfUpdateIsValid = function() {
        var _ref = _async_to_generator$4(function(_params) {
            var _ref, changedAddresses, changedTexts, changedContentHash;
            return _ts_generator$4(this, function(_state) {
                switch(_state.label){
                    case 0:
                        return [
                            4,
                            getUpdateChanges(_params)
                        ];
                    case 1:
                        _ref = _state.sent(), changedAddresses = _ref.changedAddresses, changedTexts = _ref.changedTexts, changedContentHash = _ref.changedContentHash;
                        return [
                            2,
                            changedAddresses.length > 0 || changedTexts.length > 0 || changedContentHash !== undefined
                        ];
                }
            });
        });
        return function checkIfUpdateIsValid(_params) {
            return _ref.apply(this, arguments);
        };
    }();
    var getUpdateChanges = function() {
        var _ref = _async_to_generator$4(function(_params) {
            var _sanitizeAddresses, _sanitizeTexts, ens, __chainId, records, sanitizedRequestAddress, sanitizedRequestText, changedAddresses, changedTexts, changedContentHash;
            return _ts_generator$4(this, function(_state) {
                switch(_state.label){
                    case 0:
                        ens = _params.ens;
                        __chainId = _params.chainId || _chainId;
                        return [
                            4,
                            getRecords({
                                ens: ens,
                                chainId: __chainId
                            })
                        ];
                    case 1:
                        records = _state.sent();
                        sanitizedRequestAddress = (_sanitizeAddresses = sanitizeAddresses(_params.addresses)) === null || _sanitizeAddresses === void 0 ? void 0 : _sanitizeAddresses.filter(function(address) {
                            return address.coinType >= 0;
                        });
                        sanitizedRequestText = (_sanitizeTexts = sanitizeTexts(_params.text)) === null || _sanitizeTexts === void 0 ? void 0 : _sanitizeTexts.filter(function(text) {
                            return !!text.key;
                        });
                        if (!records) {
                            throw new Error('No records found');
                        }
                        changedAddresses = getChangedAddresses(sanitizedRequestAddress, records);
                        changedTexts = getChangedTextRecords(sanitizedRequestText, records);
                        changedContentHash = getChangedContentHash(_params.contentHash, records);
                        return [
                            2,
                            {
                                changedAddresses: changedAddresses,
                                changedTexts: changedTexts,
                                changedContentHash: changedContentHash
                            }
                        ];
                }
            });
        });
        return function getUpdateChanges(_params) {
            return _ref.apply(this, arguments);
        };
    }();
    var query = useQuery(_object_spread_props$2(_object_spread$2({}, defaultOptions), {
        queryKey: buildUpdateChangesKey(_object_spread_props$2(_object_spread$2({}, params), {
            ens: (params === null || params === void 0 ? void 0 : params.ens) || '',
            chainId: _chainId
        })),
        queryFn: /*#__PURE__*/ _async_to_generator$4(function() {
            var _tmp;
            return _ts_generator$4(this, function(_state) {
                switch(_state.label){
                    case 0:
                        _tmp = {};
                        return [
                            4,
                            getUpdateChanges(_object_spread_props$2(_object_spread$2({}, params), {
                                ens: (params === null || params === void 0 ? void 0 : params.ens) || '',
                                chainId: _chainId
                            }))
                        ];
                    case 1:
                        _tmp.changes = _state.sent();
                        return [
                            4,
                            checkIfUpdateIsValid(_object_spread_props$2(_object_spread$2({}, params), {
                                ens: (params === null || params === void 0 ? void 0 : params.ens) || '',
                                chainId: _chainId
                            }))
                        ];
                    case 2:
                        return [
                            2,
                            (_tmp.canUpdate = _state.sent(), _tmp)
                        ];
                }
            });
        }),
        enabled: Boolean(params === null || params === void 0 ? void 0 : params.ens) && Boolean((params === null || params === void 0 ? void 0 : params.addresses) || (params === null || params === void 0 ? void 0 : params.text) || (params === null || params === void 0 ? void 0 : params.contentHash))
    }));
    return {
        changes: (_query_data = query.data) === null || _query_data === void 0 ? void 0 : _query_data.changes,
        canUpdateEns: (_query_data1 = query.data) === null || _query_data1 === void 0 ? void 0 : _query_data1.canUpdate,
        getUpdateChanges: getUpdateChanges,
        checkIfUpdateIsValid: checkIfUpdateIsValid,
        refetchUpdateChanges: query.refetch,
        isUpdateChangesPending: query.isPending,
        isUpdateChangesFetching: query.isFetching,
        isUpdateChangesLoading: query.isPending || query.isFetching
    };
};
var getChangedAddresses = function(sanitizedRequestAddress, records) {
    var changedAddresses = [];
    sanitizedRequestAddress === null || sanitizedRequestAddress === void 0 ? void 0 : sanitizedRequestAddress.forEach(function(address) {
        var record = records.records.coins.find(function(record) {
            return record.id === address.coinType;
        });
        if ((!record || record.value !== address.address) && (!!(record === null || record === void 0 ? void 0 : record.value) || !!address.address)) {
            changedAddresses.push(address);
        }
    });
    return changedAddresses === null || changedAddresses === void 0 ? void 0 : changedAddresses.filter(function(address) {
        return address.coinType >= 0;
    });
};
var getChangedTextRecords = function(sanitizedRequestText, records) {
    var changedTexts = [];
    sanitizedRequestText === null || sanitizedRequestText === void 0 ? void 0 : sanitizedRequestText.forEach(function(text) {
        var record = records.records.texts.find(function(record) {
            return record.key === text.key;
        });
        if ((!record || record.value !== text.value) && (!!(record === null || record === void 0 ? void 0 : record.value) || !!text.value)) {
            changedTexts.push(text);
        }
    });
    return changedTexts.filter(function(text) {
        return !!text.key;
    });
};
var getChangedContentHash = function(contentHash, records) {
    if (contentHash === '') {
        return '';
    }
    if (!contentHash) {
        return undefined;
    }
    if (!records.records.contentHash) {
        // return `${contentHash.protocolType}://${contentHash.decoded}`;
        return contentHash;
    }
    if (// records.records.contentHash.protocolType !== contentHash.protocolType ||
    // records.records.contentHash.decoded !== contentHash.decoded
    contentHash !== records.records.contentHash.protocolType + '://' + records.records.contentHash.decoded) {
        return contentHash;
    }
    return undefined;
};

function _array_like_to_array$1(arr, len) {
    if (len == null || len > arr.length) len = arr.length;
    for(var i = 0, arr2 = new Array(len); i < len; i++)arr2[i] = arr[i];
    return arr2;
}
function _array_with_holes(arr) {
    if (Array.isArray(arr)) return arr;
}
function asyncGeneratorStep$3(gen, resolve, reject, _next, _throw, key, arg) {
    try {
        var info = gen[key](arg);
        var value = info.value;
    } catch (error) {
        reject(error);
        return;
    }
    if (info.done) {
        resolve(value);
    } else {
        Promise.resolve(value).then(_next, _throw);
    }
}
function _async_to_generator$3(fn) {
    return function() {
        var self = this, args = arguments;
        return new Promise(function(resolve, reject) {
            var gen = fn.apply(self, args);
            function _next(value) {
                asyncGeneratorStep$3(gen, resolve, reject, _next, _throw, "next", value);
            }
            function _throw(err) {
                asyncGeneratorStep$3(gen, resolve, reject, _next, _throw, "throw", err);
            }
            _next(undefined);
        });
    };
}
function _iterable_to_array_limit(arr, i) {
    var _i = arr == null ? null : typeof Symbol !== "undefined" && arr[Symbol.iterator] || arr["@@iterator"];
    if (_i == null) return;
    var _arr = [];
    var _n = true;
    var _d = false;
    var _s, _e;
    try {
        for(_i = _i.call(arr); !(_n = (_s = _i.next()).done); _n = true){
            _arr.push(_s.value);
            if (i && _arr.length === i) break;
        }
    } catch (err) {
        _d = true;
        _e = err;
    } finally{
        try {
            if (!_n && _i["return"] != null) _i["return"]();
        } finally{
            if (_d) throw _e;
        }
    }
    return _arr;
}
function _non_iterable_rest() {
    throw new TypeError("Invalid attempt to destructure non-iterable instance.\\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}
function _sliced_to_array(arr, i) {
    return _array_with_holes(arr) || _iterable_to_array_limit(arr, i) || _unsupported_iterable_to_array$1(arr, i) || _non_iterable_rest();
}
function _unsupported_iterable_to_array$1(o, minLen) {
    if (!o) return;
    if (typeof o === "string") return _array_like_to_array$1(o, minLen);
    var n = Object.prototype.toString.call(o).slice(8, -1);
    if (n === "Object" && o.constructor) n = o.constructor.name;
    if (n === "Map" || n === "Set") return Array.from(n);
    if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _array_like_to_array$1(o, minLen);
}
function _ts_generator$3(thisArg, body) {
    var f, y, t, g, _ = {
        label: 0,
        sent: function() {
            if (t[0] & 1) throw t[1];
            return t[1];
        },
        trys: [],
        ops: []
    };
    return g = {
        next: verb(0),
        "throw": verb(1),
        "return": verb(2)
    }, typeof Symbol === "function" && (g[Symbol.iterator] = function() {
        return this;
    }), g;
    function verb(n) {
        return function(v) {
            return step([
                n,
                v
            ]);
        };
    }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while(_)try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [
                op[0] & 2,
                t.value
            ];
            switch(op[0]){
                case 0:
                case 1:
                    t = op;
                    break;
                case 4:
                    _.label++;
                    return {
                        value: op[1],
                        done: false
                    };
                case 5:
                    _.label++;
                    y = op[1];
                    op = [
                        0
                    ];
                    continue;
                case 7:
                    op = _.ops.pop();
                    _.trys.pop();
                    continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) {
                        _ = 0;
                        continue;
                    }
                    if (op[0] === 3 && (!t || op[1] > t[0] && op[1] < t[3])) {
                        _.label = op[1];
                        break;
                    }
                    if (op[0] === 6 && _.label < t[1]) {
                        _.label = t[1];
                        t = op;
                        break;
                    }
                    if (t && _.label < t[2]) {
                        _.label = t[2];
                        _.ops.push(op);
                        break;
                    }
                    if (t[2]) _.ops.pop();
                    _.trys.pop();
                    continue;
            }
            op = body.call(thisArg, _);
        } catch (e) {
            op = [
                6,
                e
            ];
            y = 0;
        } finally{
            f = t = 0;
        }
        if (op[0] & 5) throw op[1];
        return {
            value: op[0] ? op[1] : void 0,
            done: true
        };
    }
}
var useUpdateSubname = function(params) {
    var queryClient = useQueryClient();
    var _useJustaName = useJustaName(), justaname = _useJustaName.justaname, chainId = _useJustaName.chainId;
    var _chainId = useMemo(function() {
        return (params === null || params === void 0 ? void 0 : params.chainId) || chainId;
    }, [
        params,
        chainId
    ]);
    var address = useMountedAccount().address;
    var getSignature = useSubnameSignature().getSignature;
    var refetchAccountSubnames = useAccountSubnames({
        chainId: _chainId
    }).refetchAccountSubnames;
    var getRecords = useRecords({
        chainId: _chainId
    }).getRecords;
    var ensWalletClient = useEnsWalletClient().ensWalletClient;
    var ensClient = useEnsPublicClient({
        chainId: _chainId
    }).ensClient;
    var _useUpdateChanges = useUpdateChanges({
        chainId: _chainId
    }), checkIfUpdateIsValid = _useUpdateChanges.checkIfUpdateIsValid, getUpdateChanges = _useUpdateChanges.getUpdateChanges;
    var mutate = useMutation({
        mutationFn: function() {
            var _ref = _async_to_generator$3(function(_params) {
                var records, _ref, changedAddresses, changedTexts, changedContentHash, signature, _splitDomain, username, ensDomain, changeIsValid, changes, hash;
                return _ts_generator$3(this, function(_state) {
                    switch(_state.label){
                        case 0:
                            if (!address) {
                                throw new Error('No address found');
                            }
                            return [
                                4,
                                getRecords({
                                    ens: _params.ens,
                                    chainId: _params.chainId || _chainId
                                })
                            ];
                        case 1:
                            records = _state.sent();
                            if (!records) {
                                throw new Error('No records found');
                            }
                            return [
                                4,
                                getUpdateChanges(_params)
                            ];
                        case 2:
                            _ref = _state.sent(), changedAddresses = _ref.changedAddresses, changedTexts = _ref.changedTexts, changedContentHash = _ref.changedContentHash;
                            if (!records.isJAN) return [
                                3,
                                5
                            ];
                            return [
                                4,
                                getSignature()
                            ];
                        case 3:
                            signature = _state.sent();
                            _splitDomain = _sliced_to_array(splitDomain(_params.ens), 2), username = _splitDomain[0], ensDomain = _splitDomain[1];
                            return [
                                4,
                                justaname.subnames.updateSubname({
                                    chainId: _params.chainId || _chainId,
                                    // contentHash: _params.contentHash?.protocolType ? _params?.contentHash?.protocolType === '' ? '' : `${_params?.contentHash?.protocolType}://${_params?.contentHash?.decoded}` : undefined,
                                    contentHash: changedContentHash,
                                    addresses: changedAddresses === null || changedAddresses === void 0 ? void 0 : changedAddresses.map(function(address) {
                                        return {
                                            address: address.address,
                                            coinType: address.coinType.toString()
                                        };
                                    }),
                                    text: changedTexts,
                                    ensDomain: ensDomain,
                                    username: username
                                }, {
                                    xAddress: address,
                                    xSignature: signature.signature,
                                    xMessage: signature.message
                                })
                            ];
                        case 4:
                            _state.sent();
                            return [
                                3,
                                16
                            ];
                        case 5:
                            if (!ensWalletClient) {
                                throw new Error('No Wallet Client found, chain not supported, only mainnet and sepolia are supported');
                            }
                            return [
                                4,
                                checkIfUpdateIsValid(_params)
                            ];
                        case 6:
                            changeIsValid = _state.sent();
                            if (!changeIsValid) {
                                return [
                                    2
                                ];
                            }
                            changes = 0;
                            if (changedAddresses && changedAddresses.length > 0) {
                                changes++;
                            }
                            if (changedTexts && changedTexts.length > 0) {
                                changes++;
                            }
                            if (changedContentHash) {
                                changes++;
                            }
                            if (changes === 0) {
                                return [
                                    2
                                ];
                            }
                            if (!(changes === 1)) return [
                                3,
                                12
                            ];
                            if (!(changedAddresses && changedAddresses.length === 1)) return [
                                3,
                                8
                            ];
                            return [
                                4,
                                setAddressRecord(ensWalletClient, {
                                    name: _params.ens,
                                    account: address,
                                    coin: changedAddresses[0].coinType,
                                    value: changedAddresses[0].address,
                                    resolverAddress: records.records.resolverAddress
                                })
                            ];
                        case 7:
                            hash = _state.sent();
                            _state.label = 8;
                        case 8:
                            if (!(changedTexts && changedTexts.length === 1)) return [
                                3,
                                10
                            ];
                            return [
                                4,
                                setTextRecord(ensWalletClient, {
                                    name: _params.ens,
                                    account: address,
                                    key: changedTexts[0].key,
                                    value: changedTexts[0].value,
                                    resolverAddress: records.records.resolverAddress
                                })
                            ];
                        case 9:
                            hash = _state.sent();
                            _state.label = 10;
                        case 10:
                            if (!changedContentHash) return [
                                3,
                                12
                            ];
                            return [
                                4,
                                setContentHashRecord(ensWalletClient, {
                                    name: _params.ens,
                                    account: address,
                                    contentHash: changedContentHash,
                                    resolverAddress: records.records.resolverAddress
                                })
                            ];
                        case 11:
                            hash = _state.sent();
                            _state.label = 12;
                        case 12:
                            if (!!hash) return [
                                3,
                                14
                            ];
                            return [
                                4,
                                setRecords(ensWalletClient, {
                                    name: _params.ens,
                                    account: address,
                                    coins: changedAddresses && changedAddresses.length > 0 ? changedAddresses.map(function(address) {
                                        return {
                                            value: address.address,
                                            coin: address.coinType
                                        };
                                    }) : undefined,
                                    texts: changedTexts && changedTexts.length > 0 ? changedTexts : undefined,
                                    contentHash: changedContentHash,
                                    resolverAddress: records.records.resolverAddress
                                })
                            ];
                        case 13:
                            hash = _state.sent();
                            _state.label = 14;
                        case 14:
                            return [
                                4,
                                ensClient === null || ensClient === void 0 ? void 0 : ensClient.waitForTransactionReceipt({
                                    hash: hash
                                })
                            ];
                        case 15:
                            _state.sent();
                            _state.label = 16;
                        case 16:
                            getRecords({
                                ens: _params.ens,
                                chainId: _params.chainId || _chainId
                            }, true).then(function() {
                                queryClient.invalidateQueries({
                                    predicate: function(query) {
                                        return query.queryKey.includes('ENS_UPDATE_CHANGES');
                                    }
                                });
                            });
                            refetchAccountSubnames();
                            return [
                                2
                            ];
                    }
                });
            });
            return function(_params) {
                return _ref.apply(this, arguments);
            };
        }()
    });
    return {
        updateSubname: mutate.mutateAsync,
        isUpdateSubnamePending: mutate.isPending
    };
};

var PrimaryNameTaskQueue = new TaskQueue(5);

function _array_like_to_array(arr, len) {
    if (len == null || len > arr.length) len = arr.length;
    for(var i = 0, arr2 = new Array(len); i < len; i++)arr2[i] = arr[i];
    return arr2;
}
function _array_without_holes(arr) {
    if (Array.isArray(arr)) return _array_like_to_array(arr);
}
function asyncGeneratorStep$2(gen, resolve, reject, _next, _throw, key, arg) {
    try {
        var info = gen[key](arg);
        var value = info.value;
    } catch (error) {
        reject(error);
        return;
    }
    if (info.done) {
        resolve(value);
    } else {
        Promise.resolve(value).then(_next, _throw);
    }
}
function _async_to_generator$2(fn) {
    return function() {
        var self = this, args = arguments;
        return new Promise(function(resolve, reject) {
            var gen = fn.apply(self, args);
            function _next(value) {
                asyncGeneratorStep$2(gen, resolve, reject, _next, _throw, "next", value);
            }
            function _throw(err) {
                asyncGeneratorStep$2(gen, resolve, reject, _next, _throw, "throw", err);
            }
            _next(undefined);
        });
    };
}
function _define_property$1(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _iterable_to_array(iter) {
    if (typeof Symbol !== "undefined" && iter[Symbol.iterator] != null || iter["@@iterator"] != null) return Array.from(iter);
}
function _non_iterable_spread() {
    throw new TypeError("Invalid attempt to spread non-iterable instance.\\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}
function _object_spread$1(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property$1(target, key, source[key]);
        });
    }
    return target;
}
function ownKeys$1(object, enumerableOnly) {
    var keys = Object.keys(object);
    if (Object.getOwnPropertySymbols) {
        var symbols = Object.getOwnPropertySymbols(object);
        keys.push.apply(keys, symbols);
    }
    return keys;
}
function _object_spread_props$1(target, source) {
    source = source != null ? source : {};
    if (Object.getOwnPropertyDescriptors) {
        Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
        ownKeys$1(Object(source)).forEach(function(key) {
            Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
        });
    }
    return target;
}
function _to_consumable_array(arr) {
    return _array_without_holes(arr) || _iterable_to_array(arr) || _unsupported_iterable_to_array(arr) || _non_iterable_spread();
}
function _unsupported_iterable_to_array(o, minLen) {
    if (!o) return;
    if (typeof o === "string") return _array_like_to_array(o, minLen);
    var n = Object.prototype.toString.call(o).slice(8, -1);
    if (n === "Object" && o.constructor) n = o.constructor.name;
    if (n === "Map" || n === "Set") return Array.from(n);
    if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _array_like_to_array(o, minLen);
}
function _ts_generator$2(thisArg, body) {
    var f, y, t, g, _ = {
        label: 0,
        sent: function() {
            if (t[0] & 1) throw t[1];
            return t[1];
        },
        trys: [],
        ops: []
    };
    return g = {
        next: verb(0),
        "throw": verb(1),
        "return": verb(2)
    }, typeof Symbol === "function" && (g[Symbol.iterator] = function() {
        return this;
    }), g;
    function verb(n) {
        return function(v) {
            return step([
                n,
                v
            ]);
        };
    }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while(_)try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [
                op[0] & 2,
                t.value
            ];
            switch(op[0]){
                case 0:
                case 1:
                    t = op;
                    break;
                case 4:
                    _.label++;
                    return {
                        value: op[1],
                        done: false
                    };
                case 5:
                    _.label++;
                    y = op[1];
                    op = [
                        0
                    ];
                    continue;
                case 7:
                    op = _.ops.pop();
                    _.trys.pop();
                    continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) {
                        _ = 0;
                        continue;
                    }
                    if (op[0] === 3 && (!t || op[1] > t[0] && op[1] < t[3])) {
                        _.label = op[1];
                        break;
                    }
                    if (op[0] === 6 && _.label < t[1]) {
                        _.label = t[1];
                        t = op;
                        break;
                    }
                    if (t && _.label < t[2]) {
                        _.label = t[2];
                        _.ops.push(op);
                        break;
                    }
                    if (t[2]) _.ops.pop();
                    _.trys.pop();
                    continue;
            }
            op = body.call(thisArg, _);
        } catch (e) {
            op = [
                6,
                e
            ];
            y = 0;
        } finally{
            f = t = 0;
        }
        if (op[0] & 5) throw op[1];
        return {
            value: op[0] ? op[1] : void 0,
            done: true
        };
    }
}
var buildPrimaryNameBatchByAddressesKey = function(addresses, chainId) {
    return [
        'PRIMARY_NAME_BATCH',
        addresses,
        chainId
    ];
};
var buildPrimaryNameBatchKey = function(chainId) {
    return [
        'ALL_PRIMARY_NAME_BATCH',
        chainId
    ];
};
var usePrimaryNameBatch = function(params) {
    var _useJustaName = useJustaName(), chainId = _useJustaName.chainId, justaname = _useJustaName.justaname;
    var _enabled = (params === null || params === void 0 ? void 0 : params.enabled) !== undefined ? params.enabled : true;
    var _chainId = (params === null || params === void 0 ? void 0 : params.chainId) || chainId;
    var ensClient = useEnsPublicClient({
        chainId: _chainId
    }).ensClient;
    var queryClient = useQueryClient();
    var getPrimaryNameBatch = function() {
        var _ref = _async_to_generator$2(function(_params) {
            var primaryNameBatch, checkMissing, batchCalls, names, justanamePrimaryNames;
            return _ts_generator$2(this, function(_state) {
                switch(_state.label){
                    case 0:
                        if (!ensClient) {
                            throw new Error('Public client not found');
                        }
                        if (!(params === null || params === void 0 ? void 0 : params.addresses) || (params === null || params === void 0 ? void 0 : params.addresses.length) === 0) {
                            throw new Error('Address is required');
                        }
                        primaryNameBatch = params === null || params === void 0 ? void 0 : params.addresses.reduce(function(acc, address) {
                            if (address) {
                                acc[address] = null;
                            }
                            return acc;
                        }, {});
                        params === null || params === void 0 ? void 0 : params.addresses.forEach(function(address) {
                            var primaryName = queryClient.getQueryData(buildPrimaryName(address, _chainId));
                            if (typeof primaryName === 'string') {
                                primaryNameBatch[address] = primaryName;
                            }
                        });
                        checkMissing = Object.keys(primaryNameBatch).filter(function(address) {
                            return primaryNameBatch[address] === null;
                        });
                        batchCalls = checkMissing === null || checkMissing === void 0 ? void 0 : checkMissing.map(function(address) {
                            return getName.batch({
                                address: address
                            });
                        });
                        return [
                            4,
                            batch.apply(void 0, [
                                ensClient
                            ].concat(_to_consumable_array(batchCalls)))
                        ];
                    case 1:
                        names = _state.sent();
                        names.forEach(function(name, index) {
                            if (name && name.name) {
                                primaryNameBatch[checkMissing[index]] = name === null || name === void 0 ? void 0 : name.name;
                            }
                        });
                        checkMissing = Object.keys(primaryNameBatch).filter(function(address) {
                            return primaryNameBatch[address] === null;
                        });
                        return [
                            4,
                            Promise.all(checkMissing.map(function(address) {
                                return justaname.subnames.getPrimaryNameByAddress({
                                    address: address,
                                    chainId: _chainId
                                });
                            }))
                        ];
                    case 2:
                        justanamePrimaryNames = _state.sent();
                        justanamePrimaryNames.forEach(function(name, index) {
                            if (name.name) {
                                primaryNameBatch[checkMissing[index]] = name.name;
                            } else {
                                primaryNameBatch[checkMissing[index]] = '';
                            }
                        });
                        Object.keys(primaryNameBatch).forEach(function(address) {
                            queryClient.setQueryData(buildPrimaryName(address, _chainId), primaryNameBatch[address]);
                        });
                        queryClient.setQueryData(buildPrimaryNameBatchKey(_chainId), function(prev) {
                            return _object_spread$1({}, prev, primaryNameBatch);
                        });
                        return [
                            2,
                            primaryNameBatch
                        ];
                }
            });
        });
        return function getPrimaryNameBatch(_params) {
            return _ref.apply(this, arguments);
        };
    }();
    var query = useQuery(_object_spread_props$1(_object_spread$1({}, defaultOptions), {
        queryKey: buildPrimaryNameBatchByAddressesKey((params === null || params === void 0 ? void 0 : params.addresses) || [], _chainId),
        queryFn: function() {
            return getPrimaryNameBatch({
                addresses: params === null || params === void 0 ? void 0 : params.addresses
            });
        },
        enabled: Boolean(params === null || params === void 0 ? void 0 : params.addresses) && Boolean(ensClient) && Boolean(_enabled)
    }));
    var allQuery = useQuery(_object_spread_props$1(_object_spread$1({}, defaultOptions), {
        queryKey: buildPrimaryNameBatchKey(_chainId),
        queryFn: function() {
            return {};
        },
        enabled: false
    }));
    return {
        primaryNames: query.data,
        isPrimaryNameBatchPending: query.isPending,
        allPrimaryNames: allQuery.data,
        isPrimaryNameBatchFetching: query.isFetching,
        isPrimaryNameBatchLoading: query.isLoading,
        refetchPrimaryNameBatch: query.refetch
    };
};

function asyncGeneratorStep$1(gen, resolve, reject, _next, _throw, key, arg) {
    try {
        var info = gen[key](arg);
        var value = info.value;
    } catch (error) {
        reject(error);
        return;
    }
    if (info.done) {
        resolve(value);
    } else {
        Promise.resolve(value).then(_next, _throw);
    }
}
function _async_to_generator$1(fn) {
    return function() {
        var self = this, args = arguments;
        return new Promise(function(resolve, reject) {
            var gen = fn.apply(self, args);
            function _next(value) {
                asyncGeneratorStep$1(gen, resolve, reject, _next, _throw, "next", value);
            }
            function _throw(err) {
                asyncGeneratorStep$1(gen, resolve, reject, _next, _throw, "throw", err);
            }
            _next(undefined);
        });
    };
}
function _define_property(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _object_spread(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property(target, key, source[key]);
        });
    }
    return target;
}
function ownKeys(object, enumerableOnly) {
    var keys = Object.keys(object);
    if (Object.getOwnPropertySymbols) {
        var symbols = Object.getOwnPropertySymbols(object);
        keys.push.apply(keys, symbols);
    }
    return keys;
}
function _object_spread_props(target, source) {
    source = source != null ? source : {};
    if (Object.getOwnPropertyDescriptors) {
        Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
        ownKeys(Object(source)).forEach(function(key) {
            Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
        });
    }
    return target;
}
function _ts_generator$1(thisArg, body) {
    var f, y, t, g, _ = {
        label: 0,
        sent: function() {
            if (t[0] & 1) throw t[1];
            return t[1];
        },
        trys: [],
        ops: []
    };
    return g = {
        next: verb(0),
        "throw": verb(1),
        "return": verb(2)
    }, typeof Symbol === "function" && (g[Symbol.iterator] = function() {
        return this;
    }), g;
    function verb(n) {
        return function(v) {
            return step([
                n,
                v
            ]);
        };
    }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while(_)try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [
                op[0] & 2,
                t.value
            ];
            switch(op[0]){
                case 0:
                case 1:
                    t = op;
                    break;
                case 4:
                    _.label++;
                    return {
                        value: op[1],
                        done: false
                    };
                case 5:
                    _.label++;
                    y = op[1];
                    op = [
                        0
                    ];
                    continue;
                case 7:
                    op = _.ops.pop();
                    _.trys.pop();
                    continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) {
                        _ = 0;
                        continue;
                    }
                    if (op[0] === 3 && (!t || op[1] > t[0] && op[1] < t[3])) {
                        _.label = op[1];
                        break;
                    }
                    if (op[0] === 6 && _.label < t[1]) {
                        _.label = t[1];
                        t = op;
                        break;
                    }
                    if (t && _.label < t[2]) {
                        _.label = t[2];
                        _.ops.push(op);
                        break;
                    }
                    if (t[2]) _.ops.pop();
                    _.trys.pop();
                    continue;
            }
            op = body.call(thisArg, _);
        } catch (e) {
            op = [
                6,
                e
            ];
            y = 0;
        } finally{
            f = t = 0;
        }
        if (op[0] & 5) throw op[1];
        return {
            value: op[0] ? op[1] : void 0,
            done: true
        };
    }
}
var buildPrimaryName = function(address, chainId) {
    return [
        'PRIMARY_NAME',
        address,
        chainId
    ];
};
var usePrimaryName = function(params) {
    var _useJustaName = useJustaName(), chainId = _useJustaName.chainId, justaname = _useJustaName.justaname;
    var _priority = (params === null || params === void 0 ? void 0 : params.priority) || 'offChain';
    var _enabled = (params === null || params === void 0 ? void 0 : params.enabled) !== undefined ? params.enabled : true;
    var _chainId = (params === null || params === void 0 ? void 0 : params.chainId) || chainId;
    var ensClient = useEnsPublicClient({
        chainId: _chainId
    }).ensClient;
    var queryClient = useQueryClient();
    var getOnChainPrimaryName = function() {
        var _ref = _async_to_generator$1(function(_params) {
            var taskFn, reverseResolution;
            return _ts_generator$1(this, function(_state) {
                switch(_state.label){
                    case 0:
                        if (!ensClient) {
                            throw new Error('Public client not found');
                        }
                        if (!(params === null || params === void 0 ? void 0 : params.address)) {
                            throw new Error('Address is required');
                        }
                        taskFn = function() {
                            return getName(ensClient, {
                                address: params === null || params === void 0 ? void 0 : params.address
                            });
                        };
                        return [
                            4,
                            PrimaryNameTaskQueue.enqueue(taskFn)
                        ];
                    case 1:
                        reverseResolution = _state.sent();
                        if (reverseResolution && (reverseResolution === null || reverseResolution === void 0 ? void 0 : reverseResolution.name)) {
                            return [
                                2,
                                reverseResolution.name
                            ];
                        }
                        return [
                            2,
                            ''
                        ];
                }
            });
        });
        return function getOnChainPrimaryName(_params) {
            return _ref.apply(this, arguments);
        };
    }();
    var getOffChainPrimaryName = function() {
        var _ref = _async_to_generator$1(function(_params) {
            var primaryNames, primaryNameGetByAddressResponse;
            return _ts_generator$1(this, function(_state) {
                switch(_state.label){
                    case 0:
                        if (!(params === null || params === void 0 ? void 0 : params.address)) {
                            throw new Error('Address is required');
                        }
                        primaryNames = queryClient.getQueryData(buildPrimaryNameBatchKey(_chainId));
                        if (primaryNames && (_params === null || _params === void 0 ? void 0 : _params.address)) {
                            if (primaryNames[_params === null || _params === void 0 ? void 0 : _params.address]) {
                                return [
                                    2,
                                    primaryNames[_params.address]
                                ];
                            }
                        }
                        return [
                            4,
                            justaname.subnames.getPrimaryNameByAddress({
                                address: params === null || params === void 0 ? void 0 : params.address,
                                chainId: _chainId
                            })
                        ];
                    case 1:
                        primaryNameGetByAddressResponse = _state.sent();
                        if (primaryNameGetByAddressResponse.name) {
                            return [
                                2,
                                primaryNameGetByAddressResponse.name
                            ];
                        } else {
                            return [
                                2,
                                ''
                            ];
                        }
                }
            });
        });
        return function getOffChainPrimaryName(_params) {
            return _ref.apply(this, arguments);
        };
    }();
    var getPrimaryName = function() {
        var _ref = _async_to_generator$1(function(_params) {
            var name;
            return _ts_generator$1(this, function(_state) {
                switch(_state.label){
                    case 0:
                        name = '';
                        if (!(_priority === 'offChain')) return [
                            3,
                            5
                        ];
                        return [
                            4,
                            getOffChainPrimaryName(_params)
                        ];
                    case 1:
                        name = _state.sent();
                        if (!(name.length > 0)) return [
                            3,
                            2
                        ];
                        return [
                            2,
                            name
                        ];
                    case 2:
                        return [
                            4,
                            getOnChainPrimaryName(_params)
                        ];
                    case 3:
                        name = _state.sent();
                        return [
                            2,
                            name
                        ];
                    case 4:
                        return [
                            3,
                            9
                        ];
                    case 5:
                        return [
                            4,
                            getOnChainPrimaryName(_params)
                        ];
                    case 6:
                        name = _state.sent();
                        if (!(name.length > 0)) return [
                            3,
                            7
                        ];
                        return [
                            2,
                            name
                        ];
                    case 7:
                        return [
                            4,
                            getOffChainPrimaryName(_params)
                        ];
                    case 8:
                        name = _state.sent();
                        return [
                            2,
                            name
                        ];
                    case 9:
                        return [
                            2
                        ];
                }
            });
        });
        return function getPrimaryName(_params) {
            return _ref.apply(this, arguments);
        };
    }();
    var query = useQuery(_object_spread_props(_object_spread({}, defaultOptions), {
        retry: function(_count, error) {
            if (error === null || error === void 0 ? void 0 : error.message.includes('PrimaryNameNotFound')) {
                return false;
            }
            return _count < 3;
        },
        queryKey: buildPrimaryName((params === null || params === void 0 ? void 0 : params.address) || '', _chainId),
        queryFn: function() {
            return getPrimaryName({
                address: params === null || params === void 0 ? void 0 : params.address
            });
        },
        enabled: Boolean(params === null || params === void 0 ? void 0 : params.address) && Boolean(ensClient) && Boolean(_enabled)
    }));
    var getPrimaryNameInternal = function() {
        var _ref = _async_to_generator$1(function(params) {
            var force, _address, cachedNames, names;
            var _arguments = arguments;
            return _ts_generator$1(this, function(_state) {
                switch(_state.label){
                    case 0:
                        force = _arguments.length > 1 && _arguments[1] !== void 0 ? _arguments[1] : false;
                        _address = params.address || (params === null || params === void 0 ? void 0 : params.address);
                        if (!_address) {
                            throw new Error('Address is required');
                        }
                        if (!force) {
                            cachedNames = queryClient.getQueryData(buildPrimaryName(_address, _chainId));
                            if (cachedNames) {
                                return [
                                    2,
                                    cachedNames
                                ];
                            }
                        }
                        return [
                            4,
                            getPrimaryName({
                                address: _address,
                                chainId: _chainId
                            })
                        ];
                    case 1:
                        names = _state.sent();
                        queryClient.setQueryData(buildPrimaryName(_address, _chainId), names);
                        return [
                            2,
                            names
                        ];
                }
            });
        });
        return function getPrimaryNameInternal(params) {
            return _ref.apply(this, arguments);
        };
    }();
    return {
        primaryName: query.data,
        getPrimaryName: getPrimaryNameInternal,
        isPrimaryNamePending: query.isPending,
        isPrimaryNameFetching: query.isFetching,
        isPrimaryNameLoading: query.isLoading,
        refetchPrimaryName: query.refetch
    };
};

function asyncGeneratorStep(gen, resolve, reject, _next, _throw, key, arg) {
    try {
        var info = gen[key](arg);
        var value = info.value;
    } catch (error) {
        reject(error);
        return;
    }
    if (info.done) {
        resolve(value);
    } else {
        Promise.resolve(value).then(_next, _throw);
    }
}
function _async_to_generator(fn) {
    return function() {
        var self = this, args = arguments;
        return new Promise(function(resolve, reject) {
            var gen = fn.apply(self, args);
            function _next(value) {
                asyncGeneratorStep(gen, resolve, reject, _next, _throw, "next", value);
            }
            function _throw(err) {
                asyncGeneratorStep(gen, resolve, reject, _next, _throw, "throw", err);
            }
            _next(undefined);
        });
    };
}
function _ts_generator(thisArg, body) {
    var f, y, t, g, _ = {
        label: 0,
        sent: function() {
            if (t[0] & 1) throw t[1];
            return t[1];
        },
        trys: [],
        ops: []
    };
    return g = {
        next: verb(0),
        "throw": verb(1),
        "return": verb(2)
    }, typeof Symbol === "function" && (g[Symbol.iterator] = function() {
        return this;
    }), g;
    function verb(n) {
        return function(v) {
            return step([
                n,
                v
            ]);
        };
    }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while(_)try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [
                op[0] & 2,
                t.value
            ];
            switch(op[0]){
                case 0:
                case 1:
                    t = op;
                    break;
                case 4:
                    _.label++;
                    return {
                        value: op[1],
                        done: false
                    };
                case 5:
                    _.label++;
                    y = op[1];
                    op = [
                        0
                    ];
                    continue;
                case 7:
                    op = _.ops.pop();
                    _.trys.pop();
                    continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) {
                        _ = 0;
                        continue;
                    }
                    if (op[0] === 3 && (!t || op[1] > t[0] && op[1] < t[3])) {
                        _.label = op[1];
                        break;
                    }
                    if (op[0] === 6 && _.label < t[1]) {
                        _.label = t[1];
                        t = op;
                        break;
                    }
                    if (t && _.label < t[2]) {
                        _.label = t[2];
                        _.ops.push(op);
                        break;
                    }
                    if (t[2]) _.ops.pop();
                    _.trys.pop();
                    continue;
            }
            op = body.call(thisArg, _);
        } catch (e) {
            op = [
                6,
                e
            ];
            y = 0;
        } finally{
            f = t = 0;
        }
        if (op[0] & 5) throw op[1];
        return {
            value: op[0] ? op[1] : void 0,
            done: true
        };
    }
}
var useSetPrimaryName = function(params) {
    var _useJustaName = useJustaName(), justaname = _useJustaName.justaname, chainId = _useJustaName.chainId;
    var address = useMountedAccount().address;
    var getSignature = useSubnameSignature().getSignature;
    var refetchPrimaryName = usePrimaryName({
        address: address || '',
        enabled: !!address
    }).refetchPrimaryName;
    var _chainId = useMemo(function() {
        return (params === null || params === void 0 ? void 0 : params.chainId) || chainId;
    }, [
        params === null || params === void 0 ? void 0 : params.chainId,
        chainId
    ]);
    var mutate = useMutation({
        mutationFn: function() {
            var _ref = _async_to_generator(function(_params) {
                var _name, signature, primaryNameSet;
                return _ts_generator(this, function(_state) {
                    switch(_state.label){
                        case 0:
                            if (!address) {
                                throw new Error('No address found');
                            }
                            _name = _params.name;
                            return [
                                4,
                                getSignature()
                            ];
                        case 1:
                            signature = _state.sent();
                            return [
                                4,
                                justaname.subnames.setPrimaryName({
                                    address: address,
                                    name: _name,
                                    chainId: _chainId
                                }, {
                                    xAddress: address,
                                    xSignature: signature.signature,
                                    xMessage: signature.message
                                })
                            ];
                        case 2:
                            primaryNameSet = _state.sent();
                            refetchPrimaryName();
                            return [
                                2,
                                primaryNameSet
                            ];
                    }
                });
            });
            return function(_params) {
                return _ref.apply(this, arguments);
            };
        }()
    });
    return {
        setPrimaryName: mutate.mutateAsync,
        isSetPrimaryNamePending: mutate.isPending
    };
};

export { JustaNameContext, JustaNameProvider, OFFCHAIN_RESOLVERS_KEY, buildAccountInvitationsKey, buildAddressEnsNames, buildAddressSubnamesKey, buildAllEnsKey, buildCanEnableMAppsKey, buildEnabledMAppsKey, buildEnsAuthKey, buildEnsAvatarKey, buildEnsSubnamesKey, buildIsMAppEnabledKey, buildIsSubnameAvailableKey, buildPrimaryName, buildPrimaryNameBatchByAddressesKey, buildPrimaryNameBatchKey, buildRecordsBySubnameKey, buildSearchSubnamesKey, buildSignature, buildStandardRecordsBatchByEnsesKey, buildStandardRecordsBatchKey, buildSubnameBySubnameKey, buildUpdateChangesKey, defaultOptions, getChangedAddresses, getChangedContentHash, getChangedTextRecords, splitDomain, useAcceptSubname, useAccountEnsNames, useAccountInvitations, useAccountSubnames, useAddMAppPermission, useAddSubname, useAddressEnsNames, useAddressSubnames, useAllEns, useCanEnableMApps, useEnabledMApps, useEnsAuth, useEnsAvatar, useEnsSignIn, useEnsSignOut, useEnsSubnames, useIsMAppEnabled, useIsSubnameAvailable, useJustaName, useMounted, useMountedAccount, useOffchainResolvers, usePrimaryName, usePrimaryNameBatch, useRecords, useRejectSubname, useRevokeMAppPermission, useRevokeSubname, useSearchSubnames, useSetNameHashJustaNameResolver, useSetPrimaryName, useStandardRecordsBatch, useSubname, useSubnameSignature, useUpdateChanges, useUpdateSubname, useUploadMedia };
